import bridge from '@vkontakte/vk-bridge';
import { useState, useEffect, useRef, useCallback } from "react";
import { gsap } from "gsap";
import { Draggable } from "gsap/Draggable";
import { InertiaPlugin } from "gsap/InertiaPlugin";
gsap.registerPlugin(Draggable, InertiaPlugin);

const API = process.env.REACT_APP_API_URL || "https://iz-holodilnika.ru";

// 3D-иконки UI (лежат на сервере в /ui/). ЗАМЕНИЛ ИКОНКИ → подними UI_VER на +1:
// это меняет URL (?v=N) и пробивает все кеши по пути (браузер, VK-прокси, nginx expires 30d).
const UI_VER = 3;
const UI = (name) => `/ui/${name}.png?v=${UI_VER}`;

// Авторост textarea под контент: окно растёт с каждой строкой (потолок 45% экрана)
const autoGrow = (el) => {
  if (!el) return;
  el.style.height = "auto";
  el.style.height = Math.min(el.scrollHeight, Math.round(window.innerHeight * 0.45)) + "px";
};
const UiIcon = ({ name, size = 24, style = {} }) => (
  <img src={UI(name)} alt="" width={size} height={size}
       style={{ display: "inline-block", verticalAlign: "middle", objectFit: "contain", ...style }} />
);

// Универсальная GSAP-лента: автодрейф + драг с инерцией + бесшовная петля.
// Тикер живёт только пока exists экран с лентой (cleanup снимает) — нагрузки вне неё ноль.
function useGsapMarquee(vpRef, trackRef, enabled, key, speed = 28, pauseMs = 3000) {
  useEffect(() => {
    if (!enabled) return;
    const track = trackRef.current, vp = vpRef.current;
    if (!track || !vp) return;
    const st = { x: 0, dragging: false, pausedUntil: performance.now() + 1200 };
    const half = track.scrollWidth / 2;
    if (!(track.dataset.loop === "1" && half > vp.clientWidth + 8)) { gsap.set(track, { x: 0 }); return; }
    const wrap = gsap.utils.wrap(-half, 0);
    const setX = gsap.quickSetter(track, "x", "px");
    const tickFn = (t, dt) => {
      if (st.dragging || performance.now() < st.pausedUntil) return;
      st.x = wrap(st.x - speed * dt / 1000);
      setX(st.x);
    };
    gsap.ticker.add(tickFn);
    const proxy = document.createElement("div");
    let startX = 0;
    const drag = Draggable.create(proxy, {
      type: "x", trigger: vp, inertia: true, dragClickables: true, minimumMovement: 6,
      cursor: "grab", activeCursor: "grabbing",
      onPressInit() { gsap.set(proxy, { x: 0 }); startX = st.x; },
      onPress() { st.dragging = true; },
      onDrag() { st.x = wrap(startX + this.x); setX(st.x); },
      onRelease() { st.dragging = false; st.pausedUntil = performance.now() + pauseMs; },
      onThrowUpdate() { st.x = wrap(startX + this.x); setX(st.x); },
      onThrowComplete() { st.pausedUntil = performance.now() + pauseMs; },
    })[0];
    return () => { gsap.ticker.remove(tickFn); drag && drag.kill(); gsap.set(track, { x: 0 }); };
  }, [enabled, key, speed, pauseMs]);
}

// Умный поиск продукта в базе: точное имя -> точный алиас -> начало имени -> начало алиаса
function findProductByName(base, q) {
  const n = String(q || "").toLowerCase().trim();
  if (!n || !Array.isArray(base) || !base.length) return null;
  let hit = base.find(x => (x.name || "").toLowerCase() === n);
  if (hit) return hit;
  hit = base.find(x => (x.aliases || "").toLowerCase().split("|").map(s => s.trim()).includes(n));
  if (hit) return hit;
  hit = base.find(x => (x.name || "").toLowerCase().startsWith(n));
  if (hit) return hit;
  hit = base.find(x => (x.aliases || "").toLowerCase().split("|").some(a => a.trim().startsWith(n)));
  return hit || null;
}

// Иконка продукта: показывает картинку (если есть image_url), иначе эмодзи. Фолбэк на эмодзи при ошибке загрузки.
function ProdIcon({ img, emoji, size = 20 }) {
  const [err, setErr] = useState(false);
  if (img && !err) {
    return (
      <img src={img} alt="" onError={() => setErr(true)}
        style={{ width: size, height: size, objectFit: "cover", borderRadius: Math.round(size * 0.28),
                 display: "inline-block", verticalAlign: "middle", flexShrink: 0 }} />
    );
  }
  if (!emoji) {
    return (
      <img src={UI("salad_plate")} alt="" width={size} height={size}
        style={{ display: "inline-block", verticalAlign: "middle", objectFit: "contain", flexShrink: 0 }} />
    );
  }
  return <span style={{ fontSize: Math.round(size * 0.9), lineHeight: 1 }}>{emoji}</span>;
}
const GENERATE_TIMEOUT_MS = 60000;
const FREE_SAVED_LIMIT = 50;
const FRIDGE_IMG = "/images/fridge.png";

// Параметры запуска VK Mini App — должны быть приложены к каждому запросу
// для проверки подписи на сервере (защита от IDOR).
// Берём один раз при загрузке, дальше — глобальная константа.
const VK_LAUNCH_PARAMS = (() => {
  try {
    return window.location.search.slice(1) || "";
  } catch (_) {
    return "";
  }
})();

/**
 * Обёртка над fetch, автоматически добавляющая заголовок X-VK-Launch-Params.
 * Используется для ВСЕХ запросов к нашему бэку.
 */
const apiFetch = (url, options = {}) => {
  const headers = {
    ...(options.headers || {}),
    "X-VK-Launch-Params": VK_LAUNCH_PARAMS,
  };
  return fetch(url, { ...options, headers });
};

/**
 * Логирует попытку показа рекламы в /ad_log (аналитика дашборда, M2).
 * Безопасно: ошибки полностью глотаются, никогда не ломает UX.
 * point: generate | regenerate | book_open | health_open | checkin | replace_ingredient
 */
const logAd = (vkId, point, { ad_type = 'none', shown = false, reason = null } = {}) => {
  if (!vkId) return;
  try {
    apiFetch(`${API}/ad_log`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ vk_id: vkId, point, ad_type, shown, reason }),
    }).catch(() => {});
  } catch (_) {}
};

/**
 * Сообщает бэку, что юзер разрешил/запретил пуш «совет дня в 10:00» (фича Б3).
 * Безопасно: ошибки глотаются, UX не ломает. vk_id на сервере перепроверяется по подписи.
 */
const setNotifyPermission = (vkId, allowed) => {
  if (!vkId) return;
  try {
    apiFetch(`${API}/notify_permission`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ vk_id: vkId, allowed: !!allowed }),
    }).catch(() => {});
  } catch (_) {}
};


const CONTEXTS = [
  { key: "family",   emoji: "👨‍👩‍👧", icon: "ctx_family",   label: "На семью" },
  { key: "kids",     emoji: "👶", icon: "ctx_kids",     label: "Детям" },
  { key: "guests",   emoji: "🎉", icon: "ctx_guests",   label: "Гостям" },
  { key: "lunchbox", emoji: "💼", icon: "ctx_lunchbox", label: "С собой" },
];

// ------------------------------------------------------------------
// Реклама
// ------------------------------------------------------------------
const getLaunchPlatform = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get('vk_platform') || 'desktop_web';
  } catch (_) { return 'desktop_web'; }
};

const VK_PLATFORM = getLaunchPlatform();
// eslint-disable-next-line no-unused-vars
const CAN_SHOW_SUBSCRIPTION = VK_PLATFORM === 'desktop_web';

let lastInterstitialAt = 0;
// Глобальный кулдаун на ЛЮБУЮ рекламу — соблюдаем правило VK 5.1.5.2.Г
// «не чаще 1 раза в 30 сек». Делаем 33 сек с запасом для модерации.
let lastAnyAdAt = 0;
const MIN_AD_INTERVAL_MS = 33000;

// Конфиг точек рекламы (какие вкл/выкл) — грузится с бэка при старте.
// null = ещё не загружено (тогда показываем, чтоб не терять показы при сбое загрузки).
let adPointsConfig = null;
const loadAdConfig = async () => {
  try {
    const r = await fetch(`${API}/ad_config`);
    const data = await r.json();
    if (data && data.points) adPointsConfig = data.points;
  } catch (_) { /* при ошибке adPointsConfig остаётся null → реклама показывается как раньше */ }
};
// Проверка: включена ли точка. Если конфиг не загружен (null) или точка не указана — разрешаем.
const isAdPointEnabled = (point) => {
  if (!point) return true;            // точки без идентификатора — не трогаем (показываем)
  if (adPointsConfig === null) return true;  // конфиг не загрузился — показываем (fail-open)
  return adPointsConfig[point] !== false;    // явно выключена только если false
};

const DEV_VK_ID = '28185868';  // владелец — только он видит/переключает dev-оверлей рекламы
// Короткие подписи точек для dev-плашек (по смыслу совпадают с админкой).
const AD_POINT_LABELS = {
  generate: 'Генерация',
  recipes_base_open: 'База рецептов',
  book_open: 'Мои рецепты',
  recipe_save: 'Сохранение',
  health_open: 'Здоровье',
  checkin: 'Чек-ин веса',
  replace_ingredient: 'Замена ингр.',
  product_ref_open: 'Справочник',
  challenge_open: 'Челлендж',
  product_cook: 'Приготовить с прод.',
  health_to_recipe: 'Здоровье→рецепт',
};

/**
 * Dev-плашка рекламы — видна ТОЛЬКО владельцу при включённом режиме.
 * Маленький ярлычок поверх элемента: название точки + ВКЛ/ВЫКЛ. Тап = переключить точку.
 * Когда режим выключен (devOn=false) — ничего не рендерит, вёрстку не трогает.
 * Родитель должен иметь position:relative.
 */
function AdDevBadge({ point, devOn, onToggle }) {
  if (!devOn) return null;
  const on = adPointsConfig ? adPointsConfig[point] !== false : true;
  return (
    <div
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggle(point); }}
      style={{
        position: 'absolute', top: 3, left: 3, right: 3, zIndex: 30,
        display: 'flex', alignItems: 'center', gap: 4,
        background: on ? 'rgba(31,111,63,0.95)' : 'rgba(122,31,31,0.95)',
        color: '#fff', fontSize: 12, fontWeight: 700,
        padding: '2px 6px', borderRadius: 6, lineHeight: 1.3,
        cursor: 'pointer', userSelect: 'none',
        border: '1px dashed rgba(255,255,255,0.5)',
      }}>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📺 {AD_POINT_LABELS[point] || point}</span>
      <span style={{ marginLeft: 'auto', flexShrink: 0 }}>{on ? 'ВКЛ' : 'ВЫКЛ'}</span>
    </div>
  );
}

const showBanner = async () => {
  try {
    const check = await bridge.send('VKWebAppCheckBannerAd');
    if (check.result) await bridge.send('VKWebAppShowBannerAd', { banner_location: 'bottom' });
  } catch (_) {}
};

const hideBanner = async () => {
  try { await bridge.send('VKWebAppHideBannerAd'); } catch (_) {}
};

const scheduleInterstitial = (vkId = null, point = null) => {
  // Точка рекламы выключена в админке — не показываем
  if (point && !isAdPointEnabled(point)) {
    return () => {};
  }
  const now = Date.now();
  if (now - lastInterstitialAt < 90_000) {
    if (vkId && point) logAd(vkId, point, { ad_type: 'interstitial', shown: false, reason: 'cooldown' });
    return () => {};
  }
  if (now - lastAnyAdAt < MIN_AD_INTERVAL_MS) {
    if (vkId && point) logAd(vkId, point, { ad_type: 'interstitial', shown: false, reason: 'cooldown' });
    return () => {};
  }
  let cancelled = false;
  const timer = setTimeout(async () => {
    if (cancelled) return;
    try {
      await bridge.send('VKWebAppShowNativeAds', { ad_format: 'interstitial' });
      lastInterstitialAt = Date.now();
      lastAnyAdAt = Date.now();
      if (vkId && point) logAd(vkId, point, { ad_type: 'interstitial', shown: true, reason: 'ok' });
    } catch (_) {
      if (vkId && point) logAd(vkId, point, { ad_type: 'interstitial', shown: false, reason: 'not_available' });
    }
  }, 2000);
  return () => { cancelled = true; clearTimeout(timer); };
};

// Блокирующий показ interstitial. Соблюдает оба кулдауна:
// 1) VK правило 90 сек для interstitial
// 2) VK правило 5.1.5.2.Г — 30 сек между ЛЮБОЙ рекламой (мы делаем 33)
const showInterstitialBlocking = async () => {
  const now = Date.now();
  // Глобальный 30-сек кулдаун — строже всего
  if (now - lastAnyAdAt < MIN_AD_INTERVAL_MS) {
    const wait = Math.ceil((MIN_AD_INTERVAL_MS - (now - lastAnyAdAt)) / 1000);
    return { shown: false, reason: 'global_cooldown', waitSeconds: wait };
  }
  // VK правило 90 сек для interstitial
  if (now - lastInterstitialAt < 90_000) {
    return { shown: false, reason: 'cooldown', waitSeconds: Math.ceil((90_000 - (now - lastInterstitialAt)) / 1000) };
  }
  try {
    const result = await bridge.send('VKWebAppShowNativeAds', { ad_format: 'interstitial' });
    if (result && result.result === true) {
      lastInterstitialAt = Date.now();
      lastAnyAdAt = Date.now();
      return { shown: true };
    }
    return { shown: false, reason: 'not_available' };
  } catch (e) {
    return { shown: false, reason: 'error', error: e };
  }
};

const showRewarded = async () => {
  const now = Date.now();
  // Тот же глобальный 30-сек кулдаун для rewarded — соблюдаем правило ВК
  if (now - lastAnyAdAt < MIN_AD_INTERVAL_MS) {
    return false;
  }
  try {
    const result = await bridge.send('VKWebAppShowNativeAds', {
      ad_format: 'reward', use_waterfall: true,
    });
    if (result && result.result === true) {
      lastAnyAdAt = Date.now();
      return true;
    }
    return false;
  } catch (_) { return false; }
};

// ------------------------------------------------------------------
// Helpers
// ------------------------------------------------------------------
function track(event, params = {}) {
  if (window.ym) window.ym(108680563, 'reachGoal', event, params);
}

// Русское склонение по числу. pluralize(5, 'человек', 'человека', 'человек') → 'человек'
function pluralize(n, one, two, many) {
  const abs = Math.abs(n) % 100;
  const last = abs % 10;
  if (abs > 10 && abs < 20) return many;
  if (last > 1 && last < 5) return two;
  if (last === 1) return one;
  return many;
}

function getFallbackId() {
  let id = localStorage.getItem('fallback_user_id');
  if (!id) {
    id = 'u_' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
    localStorage.setItem('fallback_user_id', id);
  }
  return id;
}

function ruRecipesWord(n) {
  const mod10 = n % 10, mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return "рецепт";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return "рецепта";
  return "рецептов";
}

function ruCookedWord(n) {
  const mod10 = n % 10, mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return "приготовил";
  return "приготовили";
}

function ruSavedPeopleWord(n) {
  const mod10 = n % 10, mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return "сохранил";
  return "сохранили";
}

// Умный подбор эмодзи по названию блюда (вместо случайного — чтобы картинка соответствовала)
function recipeEmoji(name) {
  const n = (name || "").toLowerCase();
  // порядок важен: более специфичные сверху
  const map = [
    [/суп|борщ|щи|солянк|бульон|похлёбк|уха/, "🍲"],
    [/макарон|паст|спагетти|лапш|вермишел|лазань/, "🍝"],
    [/пюре|картоф|картош|драник|жарен.* картоф/, "🥔"],
    [/салат|винегрет|нарезк/, "🥗"],
    [/кур-?гриль|курин|курица|куриц|индейк|птиц/, "🍗"],
    [/котлет|тефтел|фрикадел|биточк|фарш/, "🍖"],
    [/говядин|свинин|мясо|стейк|гуляш|бефстроган|шашлык|буженин/, "🥩"],
    [/рыб|лосось|треск|сёмг|форел|минтай|селёдк|селедк|скумбри/, "🐟"],
    [/креветк|кальмар|мидии|морепродукт/, "🦐"],
    [/рис|плов|ризотто|паэль/, "🍚"],
    [/греч|каша|овсян|пшён|перлов|булгур|кускус/, "🥣"],
    [/блин|оладь|сырник/, "🥞"],
    [/яичниц|омлет|яйц|глазунь/, "🍳"],
    [/пирог|выпечк|булочк|кекс|маффин|печень|пицц|тарт|запеканк/, "🥧"],
    [/торт|десерт|пирожн|тирамису|чизкейк|мусс/, "🍰"],
    [/блюд.* овощ|овощ|рагу|тушён.* овощ|кабачк|баклажан|брокколи|цветн.* капуст/, "🥘"],
    [/грибы|шампиньон|вёшенк|опят/, "🍄"],
    [/сыр|творог|запеканк.* твор/, "🧀"],
    [/напит|смузи|коктейл|компот|морс|лимонад/, "🥤"],
    [/суши|ролл/, "🍣"],
    [/бутерброд|сэндвич|тост|гренк/, "🥪"],
    [/блюд|обед|ужин|завтрак/, "🍽️"],
  ];
  for (const [re, emoji] of map) {
    if (re.test(n)) return emoji;
  }
  return "🍲";  // дефолт — тарелка супа (нейтрально-еда)
}

function withName(name, text) {
  if (!name) return text.charAt(0).toUpperCase() + text.slice(1);
  return `${name}, ${text}`;
}

function vkBdateToISO(bdate) {
  if (!bdate || typeof bdate !== 'string') return null;
  const parts = bdate.split('.');
  if (parts.length < 2) return null;
  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  const year = parts.length >= 3 ? parseInt(parts[2], 10) : 1900;
  if (!day || !month) return null;
  if (day < 1 || day > 31 || month < 1 || month > 12) return null;
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

// ------------------------------------------------------------------
// КБЖУ компонент
// ------------------------------------------------------------------
// Метки болячек — русское название → эмодзи
const CONDITION_LABELS = {
  "давление": "🫀 При давлении",
  "суставы": "🦴 При суставах",
  "сердце": "❤️ Для сердца",
  "анемия": "🩸 При анемии",
  "холестерин": "💊 При холестерине",
  "желудок": "🫃 Для желудка",
  "память": "🧠 Для памяти",
  "иммунитет": "🛡️ Для иммунитета",
  "сон": "💤 При бессоннице",
  "кости": "🦴 Для костей",
  "зрение": "👁️ Для зрения",
};

function NutritionBlock({ recipe }) {
  // НОВЫЕ поля (на 100г + всё блюдо), с фолбэком на старые (calories_per_serving)
  const cal100 = recipe.calories_per_100g;
  const p100 = recipe.proteins_100g;
  const f100 = recipe.fats_100g;
  const c100 = recipe.carbs_100g;
  const totalCal = recipe.total_calories;
  const totalW = recipe.total_weight_g;
  const xe100 = recipe.bread_units_100g;
  const xeTotal = recipe.bread_units_total;
  const gi = recipe.glycemic_index;
  // старые поля (фолбэк для совместимости со старыми рецептами)
  const calOld = recipe.calories_per_serving;
  const pOld = recipe.proteins_g, fOld = recipe.fats_g, cOld = recipe.carbs_g;
  const xeOld = recipe.bread_units;

  const tags = Array.isArray(recipe.health_tags) ? recipe.health_tags : [];
  const note = recipe.health_note;
  const conditions = Array.isArray(recipe.health_conditions) ? recipe.health_conditions : [];
  const caution = recipe.health_caution || "";

  const hasNew = cal100 > 0;
  const hasOld = calOld > 0;
  // Не показываем если совсем нет КБЖУ
  if (!hasNew && !hasOld && !p100 && !pOld) return null;

  // итоговое ХЕ (новое или старое)
  const xe = (xe100 != null) ? xe100 : xeOld;
  const xeWhole = xeTotal;

  return (
    <div style={styles.nutritionCard}>
      {hasNew ? (
        <>
          {/* КБЖУ на 100 г — крупная цифра + бары (макет В) */}
          <div style={{ textAlign: "center", fontSize: 40, fontWeight: 800, color: "#1c2a1c", lineHeight: 1 }}>{cal100}</div>
          <div style={{ textAlign: "center", fontSize: 14, color: "#7a8a7a", marginBottom: 14 }}>ккал на 100 г</div>
          {(() => {
            const pv = Number(p100) || 0, fv = Number(f100) || 0, cv = Number(c100) || 0;
            const mx = Math.max(pv, fv, cv, 1);
            const Bar = ({ label, val, color }) => (
              <div style={{ marginBottom: 9 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 15, color: "#33523f", marginBottom: 4 }}>
                  <span>{label}</span><span style={{ fontWeight: 600 }}>{val} г</span>
                </div>
                <div style={{ height: 7, background: "#edf1e8", borderRadius: 4, boxShadow: "inset 0 1px 2px rgba(28,36,27,0.07)" }}>
                  <div style={{ width: `${Math.round((Number(val) || 0) / mx * 100)}%`, height: 7, background: color, borderRadius: 4, transition: "width 0.4s ease", boxShadow: "inset 0 1px 1px rgba(255,255,255,0.45)" }} />
                </div>
              </div>
            );
            return (
              <>
                <Bar label="Белки" val={p100} color="#1d9e75" />
                <Bar label="Жиры" val={f100} color="#97c459" />
                <Bar label="Углеводы" val={c100} color="#ef9f27" />
              </>
            );
          })()}
          {/* Всё блюдо + ХЕ + ГИ */}
          {(totalCal > 0 || xe != null || gi != null) && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, justifyContent: "center", marginTop: 10 }}>
              {totalCal > 0 && totalW > 0 && (
                <span style={{ fontSize: 14, color: "#4a5c4a", background: "#eef8f2", borderRadius: 8, padding: "4px 10px" }}>
                  🍲 Всё блюдо: <b>{totalCal}</b> ккал ({totalW} г)
                </span>
              )}
              {xe != null && (
                <span style={{ fontSize: 14, color: "#2e6da4", background: "#eaf2fb", borderRadius: 8, padding: "4px 10px" }}
                  title="Хлебные единицы — для людей с диабетом">
                  🍞 <b>{xe}</b> ХЕ/100г{xeWhole != null ? ` · ${xeWhole} всё` : ""}
                </span>
              )}
              {gi != null && (
                <span style={{ fontSize: 14, color: "#8a6d1b", background: "#fbf6e3", borderRadius: 8, padding: "4px 10px" }}
                  title="Гликемический индекс">
                  📈 ГИ <b>{gi}</b>
                </span>
              )}
            </div>
          )}
        </>
      ) : (
        /* СТАРЫЙ формат — фолбэк (на порцию) */
        calOld > 0 && (
          <div style={styles.nutritionRow}>
            <div style={styles.nutritionItem}>
              <div style={styles.nutritionValue}>{calOld}</div>
              <div style={styles.nutritionLabel}>ккал</div>
              <div style={styles.nutritionPerServing}>на порцию</div>
            </div>
            <div style={styles.nutritionDivider} />
            <div style={styles.nutritionItem}>
              <div style={styles.nutritionValue}>{pOld}</div>
              <div style={styles.nutritionLabel}>белки</div>
            </div>
            <div style={styles.nutritionDivider} />
            <div style={styles.nutritionItem}>
              <div style={styles.nutritionValue}>{fOld}</div>
              <div style={styles.nutritionLabel}>жиры</div>
            </div>
            <div style={styles.nutritionDivider} />
            <div style={styles.nutritionItem}>
              <div style={styles.nutritionValue}>{cOld}</div>
              <div style={styles.nutritionLabel}>углеводы</div>
            </div>
          </div>
        )
      )}

      {/* Предупреждение 1: продуктов нет в базе → КБЖУ неполное */}
      {Array.isArray(recipe.unmatched_products) && recipe.unmatched_products.length > 0 && (
        <div style={{ fontSize: 13, color: "#9a6a1e", background: "linear-gradient(145deg,#fdf3e0,#fae8cb)",
                      boxShadow: "inset 0 1px 1px rgba(255,255,255,0.6)",
                      borderRadius: 12, padding: "8px 11px", marginTop: 10, lineHeight: 1.4 }}>
          ⚠️ КБЖУ примерное — нет в базе: {recipe.unmatched_products.join(", ")}. Их калории пока не учтены. Добавьте {recipe.unmatched_products.length > 1 ? "продукты" : "продукт"} в базу — расчёт станет точным.
        </div>
      )}
      {/* Предупреждение 2: продукт в базе есть, но не указано количество */}
      {Array.isArray(recipe.no_amount_products) && recipe.no_amount_products.length > 0 && (
        <div style={{ fontSize: 13, color: "#9a6a1e", background: "linear-gradient(145deg,#fdf3e0,#fae8cb)",
                      boxShadow: "inset 0 1px 1px rgba(255,255,255,0.6)",
                      borderRadius: 12, padding: "8px 11px", marginTop: 8, lineHeight: 1.4 }}>
          ⚖️ Не указано количество: {recipe.no_amount_products.join(", ")}. Укажите граммы или штуки (например «100 г» или «2 шт») — и {recipe.no_amount_products.length > 1 ? "эти продукты войдут" : "продукт войдёт"} в расчёт КБЖУ.
        </div>
      )}

      {/* Теги здоровья — цветная сетка (макет В) */}
      {tags.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: "#1c2a1c", marginBottom: 8 }}>Польза для здоровья</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {tags.map((tag, i) => {
              let display = tag;
              if (typeof tag === 'object' && tag !== null) {
                display = `${tag.emoji || ''} ${tag.label || ''}`.trim();
              }
              if (typeof display !== 'string' || !display) return null;
              const PAL = [
                { bg: "linear-gradient(145deg,#f3a789,#ec8a64)", fg: "#4a1b0c" },
                { bg: "linear-gradient(145deg,#b0e8d5,#8fd8bf)", fg: "#04342c" },
                { bg: "linear-gradient(145deg,#cde3a6,#b5d488)", fg: "#173404" },
                { bg: "linear-gradient(145deg,#9cc4ef,#7fabe0)", fg: "#042c53" },
              ];
              const c = PAL[i % PAL.length];
              return (
                <div key={i} style={{ background: c.bg, color: c.fg, borderRadius: 16, padding: "12px 13px",
                                      fontSize: 15, fontWeight: 600, lineHeight: 1.3,
                                      boxShadow: "inset 0 1px 1px rgba(255,255,255,0.4), 0 2px 6px rgba(28,36,27,0.07)" }}>
                  {display}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Заметка о пользе */}
      {note && (
        <div style={styles.healthNote}>
          <span style={styles.healthNoteIcon}>💚</span>
          <span style={styles.healthNoteText}>{note}</span>
        </div>
      )}

      {/* ПОЛЕЗНО ПРИ — главный раздел */}
      {conditions.length > 0 && (
        <div style={styles.conditionsBlock}>
          <div style={styles.conditionsTitle}>💚 Может быть полезно при:</div>
          <div style={styles.conditionsList}>
            {conditions.map((cond, i) => {
              // Если condition в нашем словаре — берём правильный лейбл с эмодзи.
              // Иначе нормализуем: с большой буквы + префикс "Для" если уместно.
              const lower = String(cond || '').trim().toLowerCase();
              if (!lower) return null;
              if (CONDITION_LABELS[lower]) {
                return (
                  <span key={i} style={styles.conditionTag}>
                    {CONDITION_LABELS[lower]}
                  </span>
                );
              }
              // Fallback — нормализуем неизвестное значение
              const normalized = lower.charAt(0).toUpperCase() + lower.slice(1);
              return (
                <span key={i} style={styles.conditionTag}>
                  {normalized}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Осторожно */}
      {caution && (
        <div style={styles.cautionBlock}>
          <span style={styles.cautionIcon}>⚠️</span>
          <span style={styles.cautionText}>{caution}</span>
        </div>
      )}

      {/* Дисклеймер */}
      <div style={styles.healthDisclaimer}>
        Носит исключительно ознакомительный характер. Не является медицинской консультацией. При заболеваниях обращайтесь к врачу.
      </div>
    </div>
  );
}

// ------------------------------------------------------------------
// Shared UI
// ------------------------------------------------------------------
function Toast({ message, action, onAction, onClose }) {
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(onClose, action ? 5000 : 4000);
    return () => clearTimeout(t);
  }, [message, action, onClose]);
  if (!message) return null;
  return (
    <div style={styles.toast}>
      <span style={{ flex: 1 }}>{message}</span>
      {action && <button style={styles.toastBtn} onClick={onAction}>{action}</button>}
    </div>
  );
}

function ConfirmModal({ title, text, confirmText = "Да", cancelText = "Отмена", onConfirm, onCancel, loading }) {
  if (!title) return null;
  return (
    <div style={styles.modalOverlay} className="povar-fade" onClick={loading ? null : onCancel}>
      <div style={styles.modalCard} className="povar-pop" onClick={e => e.stopPropagation()}>
        <div style={styles.modalTitle}>{title}</div>
        {text && <div style={styles.modalText}>{text}</div>}
        <div style={styles.modalButtons}>
          <button style={styles.modalBtnCancel} className="povar-press" onClick={onCancel} disabled={loading}>{cancelText}</button>
          <button style={styles.modalBtnConfirm} className="povar-press" onClick={onConfirm} disabled={loading}>
            {loading ? "..." : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

function InfoModal({ title, text, buttonText = "Понятно", onClose }) {
  if (!title) return null;
  return (
    <div style={styles.modalOverlay} className="povar-fade" onClick={onClose}>
      <div style={styles.modalCard} className="povar-pop" onClick={e => e.stopPropagation()}>
        <div style={styles.modalTitle}>{title}</div>
        {text && <div style={styles.modalText}>{text}</div>}
        <button style={styles.modalBtnInfo} className="povar-press" onClick={onClose}>{buttonText}</button>
      </div>
    </div>
  );
}

// Плавающая кнопка «назад» — фиксирована справа внизу. Появляется при прокрутке
// вниз (когда шапочная кнопка «назад» уже уехала вверх). Повторяет действие назад
// конкретного экрана (передаётся через onClick).
function FloatingBack({ onClick }) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 300);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  if (!show) return null;
  return (
    <button onClick={onClick} aria-label="Назад" className="povar-press povar-fade"
      style={{
        position: "fixed", right: 16, bottom: 20, zIndex: 50,
        width: 52, height: 52, borderRadius: "50%", border: "none",
        background: "#6b3410", color: "#fff", fontSize: 24, cursor: "pointer",
        boxShadow: "0 4px 14px rgba(0,0,0,0.3)", display: "flex",
        alignItems: "center", justifyContent: "center",
      }}>
      ‹
    </button>
  );
}

// Знаки зодиака — соответствие дата → знак.
// При выборе юзером ставим серверу любую дату в нужный период (год 1900 не важен).
const ZODIACS = [
  { key: "aries",       label: "Овен",        emoji: "♈", iso: "1900-04-01" },
  { key: "taurus",      label: "Телец",       emoji: "♉", iso: "1900-05-01" },
  { key: "gemini",      label: "Близнецы",    emoji: "♊", iso: "1900-06-01" },
  { key: "cancer",      label: "Рак",         emoji: "♋", iso: "1900-07-01" },
  { key: "leo",         label: "Лев",         emoji: "♌", iso: "1900-08-01" },
  { key: "virgo",       label: "Дева",        emoji: "♍", iso: "1900-09-15" },
  { key: "libra",       label: "Весы",        emoji: "♎", iso: "1900-10-01" },
  { key: "scorpio",     label: "Скорпион",    emoji: "♏", iso: "1900-11-01" },
  { key: "sagittarius", label: "Стрелец",     emoji: "♐", iso: "1900-12-01" },
  { key: "capricorn",   label: "Козерог",     emoji: "♑", iso: "1900-01-01" },
  { key: "aquarius",    label: "Водолей",     emoji: "♒", iso: "1900-02-01" },
  { key: "pisces",      label: "Рыбы",        emoji: "♓", iso: "1900-03-01" },
];

function BirthdayPrompt({ onSubmit, onSkip, initialZodiac }) {
  const [selected, setSelected] = useState(initialZodiac || null);

  function handleSave() {
    if (!selected) return;
    const z = ZODIACS.find(z => z.key === selected);
    if (z) onSubmit(z.iso);
  }

  return (
    <div style={styles.modalOverlay}>
      <div style={{ ...styles.modalCard, maxWidth: 360, padding: "20px 18px 16px", position: "relative" }} onClick={e => e.stopPropagation()}>
        {/* Крестик закрытия */}
        <button
          onClick={onSkip}
          style={{
            position: "absolute",
            top: 10, right: 10,
            background: "none",
            border: "none",
            fontSize: 22,
            color: "#9b8270",
            cursor: "pointer",
            width: 32, height: 32,
            display: "flex", alignItems: "center", justifyContent: "center",
            borderRadius: 18,
          }}
          aria-label="Закрыть"
        >
          ✕
        </button>
        <div style={{ ...styles.modalTitle, paddingRight: 28 }}>✨ Ваш знак зодиака</div>
        <div style={styles.modalText}>
          Выберите знак — буду делать персональный совет каждое утро.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 14 }}>
          {ZODIACS.map(z => {
            const isSel = selected === z.key;
            return (
              <button
                key={z.key}
                onClick={() => setSelected(z.key)}
                style={{
                  padding: "10px 4px",
                  background: isSel ? "#0f6e56" : "#ffffff",
                  color: isSel ? "#fff" : "#1c2a1c",
                  border: isSel ? "2px solid #0f6e56" : "2px solid transparent",
                  borderRadius: 12,
                  cursor: "pointer",
                  fontSize: 15,
                  fontWeight: 600,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 3,
                  transition: "all 0.15s",
                }}>
                <span style={{ fontSize: 22 }}>{z.emoji}</span>
                <span>{z.label}</span>
              </button>
            );
          })}
        </div>
        <button
          style={{
            ...styles.modalBtnInfo,
            width: "100%",
            opacity: selected ? 1 : 0.5,
            cursor: selected ? "pointer" : "not-allowed",
          }}
          onClick={handleSave}
          disabled={!selected}>
          Сохранить
        </button>
      </div>
    </div>
  );
}

function DailyAdviceCard({ advice, userName, onPromptBirthday }) {
  // Если совет ещё не загружен — показываем простую плашку с приветствием
  if (!advice) {
    if (!userName) return null;
    return (
      <div style={styles.adviceCard}>
        <div style={styles.adviceHeader}>
          <span style={{ ...styles.adviceMoon, display: "inline-flex" }}><UiIcon name="sparkles" size={26} /></span>
          <span style={styles.adviceTitle}>Здравствуйте, {userName}!</span>
        </div>
      </div>
    );
  }
  return (
    <div style={styles.adviceCard}>
      <div style={styles.adviceHeader}>
        <span style={{ ...styles.adviceMoon, display: "inline-flex" }}><UiIcon name="sparkles" size={26} /></span>
        <span style={styles.adviceTitle}>{advice.greeting}</span>
        {advice.zodiac_emoji && <span style={styles.adviceZodiac}>{advice.zodiac_emoji}</span>}
      </div>
      <div style={styles.advicePhrase}>{advice.phrase}.</div>
      {advice.lifehack && (
        <div style={styles.adviceLifehack}>
          <span style={styles.adviceLifehackLabel}><UiIcon name="bulb" size={22} style={{ marginRight: 3, marginTop: -2 }} />Лайфхак: </span>
          {advice.lifehack}
        </div>
      )}
      {!advice.has_birthday && onPromptBirthday && (
        <button style={styles.adviceBdayLink} onClick={onPromptBirthday}>
          Совет по знаку зодиака →
        </button>
      )}
      {advice.has_birthday && onPromptBirthday && (
        <button style={styles.adviceBdayLink} onClick={onPromptBirthday}>
          Изменить знак зодиака
        </button>
      )}
    </div>
  );
}

// Псевдо-прогресс генерации картинки: круг плавно наполняется (НЕ крутится).
// Время рисования непредсказуемо, поэтому это не точный расчёт — круг быстро доходит
// до ~70-85%, дальше еле ползёт и не замирает на 100%. Верхние карточки быстрее (index меньше).
function DishProgress({ size = 56, index = 0, withText = false }) {
  const dur = 8 + index * 3;  // сек: верхняя карточка быстрее, нижние — медленнее (но не глядя медленно)
  const ring = withText ? 46 : Math.round(size * 0.62);
  return (
    <div style={{
      width: withText ? "100%" : size,
      height: withText ? 180 : size,
      borderRadius: withText ? 14 : 12,
      flexShrink: 0, background: "#eef3ea",
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center", gap: withText ? 12 : 0,
    }}>
      <svg viewBox="0 0 40 40" width={ring} height={ring} style={{ transform: "rotate(-90deg)" }}>
        <circle cx="20" cy="20" r="16" fill="none" stroke="#dbe5d6" strokeWidth="4" />
        <circle cx="20" cy="20" r="16" fill="none" stroke="#0f6e56" strokeWidth="4"
          strokeLinecap="round" strokeDasharray="100.5" strokeDashoffset="100.5"
          style={{ animation: `povarFill ${dur}s ease-out forwards` }} />
      </svg>
      {withText && <div style={{ fontSize: 15, color: "#7a8a7a", fontWeight: 600 }}>Готовим картинку…</div>}
    </div>
  );
}

function LoadingScreen({ isFresh = false }) {
  const messages = [
    "Перебираю 12 000 рецептов...",
    "Подбираю под ваши продукты...",
    "Проверяю, что всё есть дома...",
    "Почти готово, ещё секунда...",
    "Вот-вот придумаю что-то вкусное!",
  ];
  const [msgIndex, setMsgIndex] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const msgTimer = setInterval(() => setMsgIndex(p => (p + 1) % messages.length), 2000);
    const progTimer = setInterval(() => setProgress(p => p < 90 ? p + 2 : p), 300);
    return () => { clearInterval(msgTimer); clearInterval(progTimer); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div style={{ flex: 1, textAlign: "center" }}>
          <div style={styles.headerTitle}>Секундочку...</div>
          <div style={styles.headerSubtitle}>{isFresh ? "✨ Генерирую свежий рецепт" : "Собираю рецепты"}</div>
        </div>
      </div>
      <div style={{ ...styles.card, textAlign: "center", padding: "32px 20px" }}>
        <img src={FRIDGE_IMG} alt="" style={{
          width: 100, height: 100, objectFit: "contain",
          marginBottom: 22, animation: "bounceSlow 1.2s infinite", display: "inline-block"
        }} />
        <div style={styles.loadingMsg}>{isFresh ? "✨ Готовлю для вас новый рецепт, момент…" : messages[msgIndex]}</div>
        <div style={styles.progressBar}>
          <div style={{ ...styles.progressFill, width: `${progress}%` }} />
        </div>
        <div style={styles.progressText}>{progress}%</div>
        <style>{`@keyframes bounceSlow { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }`}</style>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------
// App
// ------------------------------------------------------------------
function ReplacingOverlay({ visible }) {
  if (!visible) return null;
  return (
    <div style={styles.overlayBg}>
      <div style={styles.overlayCard}>
        <img src={FRIDGE_IMG} alt=""
          style={{ width: 70, height: 70, objectFit: "contain", marginBottom: 14, animation: "bounceSlow 1.2s infinite" }} />
        <div style={styles.overlayTitle}>Пересчитываю рецепт...</div>
        <div style={styles.overlaySub}>Это займёт пару секунд</div>
        <div style={styles.overlayDots}>
          <span style={{ ...styles.overlayDot, animationDelay: "0s" }}>•</span>
          <span style={{ ...styles.overlayDot, animationDelay: "0.2s" }}>•</span>
          <span style={{ ...styles.overlayDot, animationDelay: "0.4s" }}>•</span>
        </div>
        <style>{`@keyframes overlayDotBounce { 0%,80%,100%{opacity:0.3} 40%{opacity:1} }`}</style>
      </div>
    </div>
  );
}

export default function App() {
  const [screen, setScreen] = useState("onboarding");
  const [previousScreen, setPreviousScreen] = useState("results");
  const [productReturnScreen, setProductReturnScreen] = useState(null);  // откуда зашли в карточку продукта (рецепт)
  const [onboardingStep, setOnboardingStep] = useState(0);
  const [products, setProducts] = useState("");
  const [voicePopup, setVoicePopup] = useState(false);  // попап интереса к голос-вводу (fake door тест)
  const [voiceVoted, setVoiceVoted] = useState(false);  // уже проголосовал «хочу»
  const [context, setContext] = useState(null);
  const [recipes, setRecipes] = useState([]);
  const [unusualInfo, setUnusualInfo] = useState({ is: false, reason: "" });
  const [recipeSource, setRecipeSource] = useState("");  // "base" = из готовой базы, "fresh" = свежая Гига
  const [isPremium, setIsPremium] = useState(false);     // премиум-подписка (реклама off, безлимит)
  const [recipesLeft, setRecipesLeft] = useState(null);  // остаток свежих генераций на сегодня (null = не знаем)
  const [limitEnabled, setLimitEnabled] = useState(false); // включён ли дневной лимит (на тестах — нет)
  const [loadingIsFresh, setLoadingIsFresh] = useState(false); // на экране загрузки: генерация тянется = свежая Гига
  // Догруженные картинки блюд (название → url). Картинки рисуются в фоне, фронт их подтягивает.
  const [dishImages, setDishImages] = useState({});
  // true пока идёт догрузка картинок — на это время вместо фото показываем спиннер «рисуем фото».
  const [imagesLoading, setImagesLoading] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [recipeUnverified, setRecipeUnverified] = useState(false);  // в рецепте есть непроверенный продукт
  const [selectedRecipeId, setSelectedRecipeId] = useState(null);
  const [savedRecipes, setSavedRecipes] = useState([]);
  // Общие рецепты (Этап 1)
  const [publicRecipes, setPublicRecipes] = useState([]);       // список из /recipes_list
  const [publicRecipesLoaded, setPublicRecipesLoaded] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("");  // выбранная категория-фильтр в базе ("" = все)
  const [publicView, setPublicView] = useState(null);            // открытый публичный рецепт {id,name,recipe,photos,...}
  const [publishBusy, setPublishBusy] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);   // загрузка фото в галерею
  const [galleryCaptionModal, setGalleryCaptionModal] = useState(null); // {publicId, photoUrl} — модалка коммента
  const [galleryCaption, setGalleryCaption] = useState("");
  const [editPhotoModal, setEditPhotoModal] = useState(null);  // {publicId, photoId, currentCaption} — редактирование фото
  const [editPhotoCaption, setEditPhotoCaption] = useState("");
  const [editPhotoBusy, setEditPhotoBusy] = useState(false);
  const [publishOwnModal, setPublishOwnModal] = useState(null);  // {savedId, name} — публикация своего рецепта в базу
  const [publishOwnBusy, setPublishOwnBusy] = useState(false);
  const [replaceAnyModal, setReplaceAnyModal] = useState(null);  // {original} — замена ингредиента на любой продукт
  const [replaceAnyQuery, setReplaceAnyQuery] = useState("");
  const [addProductModal, setAddProductModal] = useState(null);  // {name, fromReplace} — форма добавления продукта
  const [addProductData, setAddProductData] = useState({ calories_per_100g: "", proteins_g: "", fats_g: "", carbs_g: "", emoji: "", category: "" });
  const [addProductBusy, setAddProductBusy] = useState(false);
  const [ingredientFocusIdx, setIngredientFocusIdx] = useState(-1);  // какое поле ингредиента в фокусе (для автодополнения)
  const [chipsPaused, setChipsPaused] = useState(false);  // (не используется, оставлено для совместимости)
  const chipsVpRef = useRef(null);     // viewport (маска)
  const chipsTrackRef = useRef(null);  // лента чипов (двигаем transform-ом — субпиксельно, GPU)
  const audVpRef = useRef(null);       // viewport «Кому подходит»
  const audTrackRef = useRef(null);    // лента карточек аудиторий

  useGsapMarquee(chipsVpRef, chipsTrackRef, screen === "recipes_base", publicRecipesLoaded, 28, 3000);
  // Редактор рецептов (Фича 5)
  const [editRecipe, setEditRecipe] = useState(null);   // {id, name, ingredients[], steps[], note, recipe_type, ...}
  const [editBusy, setEditBusy] = useState(false);
  // Диктовка: {key, phase: "rec"|"proc"} | null. key: "step-N" | "products"
  const [sttState, setSttState] = useState(null);
  const [sttLevel, setSttLevel] = useState(0);  // громкость 0..1 для живого индикатора
  const sttRef = useRef(null); // {recorder, stream, chunks, timer, actx, raf}

  function sttStop() {
    const s = sttRef.current;
    if (s && s.recorder && s.recorder.state !== "inactive") {
      try { s.recorder.stop(); } catch (e) {}
    }
  }

  async function sttToggle(key) {
    // повторный тап во время записи = стоп
    if (sttState && sttState.phase === "rec") { sttStop(); return; }
    if (sttState) return; // идёт распознавание — ждём
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = ["audio/mp4", "audio/webm", "audio/ogg"].find(m =>
        typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(m)) || "";
      const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const chunks = [];

      // живой индикатор громкости: RMS с микрофона ~12 раз/сек
      let actx = null, raf = 0;
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (AC) {
          actx = new AC();
          const srcNode = actx.createMediaStreamSource(stream);
          const an = actx.createAnalyser();
          an.fftSize = 256;
          srcNode.connect(an);
          const buf = new Uint8Array(an.fftSize);
          let fr = 0;
          const loop = () => {
            an.getByteTimeDomainData(buf);
            let sum = 0;
            for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; }
            const rms = Math.sqrt(sum / buf.length);
            if ((fr++ % 5) === 0) setSttLevel(Math.min(1, rms * 3.5));
            raf = requestAnimationFrame(loop);
          };
          loop();
        }
      } catch (e) {}

      recorder.ondataavailable = ev => { if (ev.data && ev.data.size) chunks.push(ev.data); };
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        if (raf) cancelAnimationFrame(raf);
        if (actx) { try { actx.close(); } catch (e) {} }
        setSttLevel(0);
        if (sttRef.current && sttRef.current.timer) clearTimeout(sttRef.current.timer);
        sttRef.current = null;
        const blob = new Blob(chunks, { type: mime || "audio/webm" });
        if (blob.size < 1500) { setSttState(null); return; } // случайный тап
        setSttState({ key, phase: "proc" });
        try {
          const fd = new FormData();
          fd.append("vk_id", vkId);
          fd.append("file", blob, mime === "audio/mp4" ? "voice.mp4" : "voice.webm");
          const r = await fetch(`${API}/stt`, {
            method: "POST", headers: { "X-VK-Launch-Params": VK_LAUNCH_PARAMS }, body: fd,
          });
          const d = await r.json();
          if (d.status === "ok" && d.text) {
            if (key === "products") {
              setProducts(prev => (prev ? prev.replace(/\s+$/, "") + ", " : "") + d.text.replace(/\.$/, ""));
            } else {
              const idx = parseInt(key.slice(5), 10);
              const cur = (editRecipe && editRecipe.steps && editRecipe.steps[idx]) || "";
              editSetStep(idx, (cur ? cur.replace(/\s+$/, "") + " " : "") + d.text);
            }
          } else {
            setInfo({ title: "Не расслышал", text: d.message || "Попробуйте ещё раз или напечатайте." });
          }
        } catch (e) {
          setInfo({ title: "Не расслышал", text: "Проблема с сетью — попробуйте ещё раз." });
        }
        setSttState(null);
      };
      sttRef.current = { recorder, stream, chunks, timer: setTimeout(sttStop, 60000), actx, raf }; // потолок 60 сек
      recorder.start();
      setSttState({ key, phase: "rec" });
    } catch (e) {
      setSttState(null);
      setInfo({ title: "Микрофон недоступен", text: "Разрешите доступ к микрофону в настройках ВК — или продиктуйте через 🎤 на клавиатуре." });
    }
  }
  const [editAskMode, setEditAskMode] = useState(false); // показать выбор «заменить / новый»
  const [photoUploading, setPhotoUploading] = useState(false); // загрузка фото на ВК
  const [stepPhotoUploading, setStepPhotoUploading] = useState(-1); // индекс шага, чьё фото грузится (-1 = нет)
  const [savedLoading, setSavedLoading] = useState(false);
  const [error, setError] = useState("");
  const [vkId, setVkId] = useState(null);
  const [userName, setUserName] = useState(null);
  const [birthday, setBirthday] = useState(null);
  // Пуш «совет дня»: 'enabled' если ВК уже разрешил (из launch-параметра), иначе 'off'.
  const [notifyState, setNotifyState] = useState(() => {
    try {
      return new URLSearchParams(VK_LAUNCH_PARAMS).get('vk_are_notifications_enabled') === '1' ? 'enabled' : 'off';
    } catch (_) {
      return 'off';
    }
  });
  // Dev-режим рекламы (виден только владельцу). devTick — форсит перерисовку плашек после переключения.
  const [devMode, setDevMode] = useState(false);
  const [devTick, setDevTick] = useState(0);
  const [advice, setAdvice] = useState(null);
  const [showBdayPrompt, setShowBdayPrompt] = useState(false);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [toast, setToast] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [info, setInfo] = useState(null);
  const [productInput, setProductInput] = useState("");
  const [productInfo, setProductInfo] = useState(null);
  useGsapMarquee(audVpRef, audTrackRef, screen === "product_ref" && !!productInfo, productInfo, 16, 6000);
  const [productLoading, setProductLoading] = useState(false);
  const [productError, setProductError] = useState("");
  // Справочник: база продуктов (каталог) + подсказки при вводе
  const [productsBase, setProductsBase] = useState([]);      // весь список из /products_list
  const [lightbox, setLightbox] = useState(null);            // URL картинки для просмотра на весь экран (или null)
  const [productsBaseLoaded, setProductsBaseLoaded] = useState(false);
  useEffect(() => { loadProductsBase(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [showBase, setShowBase] = useState(false);           // открыт ли экран «База»
  const [replacingIdx, setReplacingIdx] = useState(-1);
  const [replaceOverlay, setReplaceOverlay] = useState(null);  // {ingredient, alternative} — оверлей замены
  const [healthConditions, setHealthConditions] = useState([]);
  const [selectedCondition, setSelectedCondition] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [healthError, setHealthError] = useState("");

  // Фича 6 — Мои продукты
  const [myProducts, setMyProducts] = useState([]);
  const [myProductsLoading, setMyProductsLoading] = useState(false);
  const [mpName, setMpName] = useState("");          // название для ввода
  const [mpCal, setMpCal] = useState("");
  const [mpProt, setMpProt] = useState("");
  const [mpFat, setMpFat] = useState("");
  const [mpCarb, setMpCarb] = useState("");
  const [mpNote, setMpNote] = useState("");          // личная заметка (до 200)
  const [mpMode, setMpMode] = useState(null);        // null | 'form' | 'edit'
  const [mpEditId, setMpEditId] = useState(null);    // id редактируемого продукта
  const [mpBusy, setMpBusy] = useState(false);
  const [mpError, setMpError] = useState("");
  const [mpFound, setMpFound] = useState(null);      // {product} — найден в базе, спросить «сохранить?»
  const [mpCooldownUntil, setMpCooldownUntil] = useState(0);  // timestamp до которого таймер
  // Челлендж
  const [challengeState, setChallengeState] = useState(null);
  const [challengeLoading, setChallengeLoading] = useState(false);
  const [challengeError, setChallengeError] = useState("");
  const [challengeThemes, setChallengeThemes] = useState([]);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const bridgeInited = useRef(false);

  useEffect(() => {
    if (bridgeInited.current) return;
    bridgeInited.current = true;
    bridge.send('VKWebAppInit').catch(() => {});
    loadAdConfig();  // грузим какие точки рекламы включены (из админки)
    bridge.send('VKWebAppSetViewSettings', { status_bar_style: 'light', action_bar_color: '#0f6e56' }).catch(() => {});
    bridge.send('VKWebAppGetUserInfo')
      .then(data => {
        setVkId(String(data.id));
        if (data.first_name) setUserName(data.first_name.trim().split(' ')[0].slice(0, 50));
        const iso = vkBdateToISO(data.bdate);
        if (iso) setBirthday(iso);
      })
      .catch(() => setVkId(getFallbackId()));
    const seen = localStorage.getItem('onboarding_done');
    if (seen) { track('app_open'); setScreen("main"); }
  }, []);

  // Глобальные анимации/интерактив (hover, нажатие, плавное появление).
  // Вставляем CSS один раз — inline-стили React не умеют :hover, поэтому классами.
  useEffect(() => {
    if (document.getElementById("povar-anim-styles")) return;
    const el = document.createElement("style");
    el.id = "povar-anim-styles";
    el.textContent = `
      @keyframes povarFadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
      @keyframes povarPulse { 0%,100% { transform: scale(1); } 50% { transform: scale(1.12); } }
      /* Каскад появления: дети экрана всплывают по очереди. backwards = после анимации transform снимается */
      #root > div > * { animation: povarFadeIn 0.38s cubic-bezier(0.22, 1, 0.36, 1) backwards; }
      #root > div > *:nth-child(2) { animation-delay: 0.04s; }
      #root > div > *:nth-child(3) { animation-delay: 0.08s; }
      #root > div > *:nth-child(4) { animation-delay: 0.12s; }
      #root > div > *:nth-child(5) { animation-delay: 0.16s; }
      #root > div > *:nth-child(6) { animation-delay: 0.20s; }
      #root > div > *:nth-child(7) { animation-delay: 0.24s; }
      #root > div > *:nth-child(n+8) { animation-delay: 0.28s; }
      @keyframes povarPopIn { from { opacity: 0; transform: scale(0.94); } to { opacity: 1; transform: scale(1); } }
      @keyframes povarFill { 0% { stroke-dashoffset: 100.5; } 35% { stroke-dashoffset: 30; } 65% { stroke-dashoffset: 15; } 100% { stroke-dashoffset: 3; } }
      /* v4: фактура-«пыль» на карточках (grain) */
      .povar-grain { position: relative; }
      .povar-grain::before {
        content: ''; position: absolute; inset: 0; pointer-events: none; opacity: 0.035;
        border-radius: inherit; z-index: 0;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100' height='100' filter='url(%23n)'/%3E%3C/svg%3E");
      }
      .povar-grain > * { position: relative; z-index: 1; }
      /* v4: градиентная плашка-иконка с бликом */
      .povar-icon {
        border-radius: 15px; display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        box-shadow: inset 0 1px 2px rgba(255,255,255,0.55), inset 0 -2px 4px rgba(0,0,0,0.06), 0 2px 6px rgba(28,36,27,0.08);
      }
      .povar-fade { animation: povarFadeIn 0.28s cubic-bezier(0.23, 1, 0.32, 1) both; }
      .povar-pop { animation: povarPopIn 0.2s ease both; }
      /* Доступность: при «уменьшить движение» (часто у пожилых) убираем сдвиги/масштаб, оставляем проявление */
      @keyframes povarFadeInRM { from { opacity: 0; } to { opacity: 1; } }
      @media (prefers-reduced-motion: reduce) {
        .povar-fade { animation: povarFadeInRM 0.2s ease both; }
        .povar-pop { animation: povarFadeInRM 0.2s ease both; }
        .povar-tap:hover { transform: none; }
      }
      /* интерактивные элементы: плавность + отклик на наведение/нажатие */
      .povar-tap { transition: transform 0.12s ease, box-shadow 0.18s ease, background 0.18s ease, border-color 0.18s ease, opacity 0.18s ease; }
      @media (hover: hover) and (pointer: fine) {
        .povar-tap:hover { transform: translateY(-2px); box-shadow: 0 4px 14px rgba(15,110,86,0.18); }
      }
      .povar-tap:active { transform: translateY(0) scale(0.98); }
      /* кнопки-кружки и мелкие — только лёгкое нажатие без подъёма */
      .povar-press { transition: transform 0.1s ease, opacity 0.15s ease; }
      .povar-press:active { transform: scale(0.92); }
      /* первичная кнопка — подсветка фона на hover */
      .povar-primary { transition: filter 0.18s ease, transform 0.12s ease; }
      .povar-primary:hover { filter: brightness(1.08); }
      .povar-primary:active { transform: scale(0.985); }
      /* чип-фильтр: плавное увеличение на hover (как плашки) */
      .povar-chip { transition: transform 0.18s ease, box-shadow 0.18s ease, filter 0.18s ease; }
      .povar-chip:hover { transform: scale(1.08); box-shadow: 0 3px 10px rgba(15,110,86,0.18); filter: brightness(1.03); z-index: 2; }
      .povar-chip:active { transform: scale(0.96); }
      /* лента чипов — нативный скролл (авто-движение через JS scrollLeft) */
      .povar-chips-viewport { overflow-x: hidden; overflow-y: hidden; scrollbar-width: none; -ms-overflow-style: none; padding: 4px 2px 12px; user-select: none; -webkit-user-select: none; overscroll-behavior-x: contain; -webkit-mask-image: linear-gradient(90deg, transparent 0, #000 24px, #000 calc(100% - 24px), transparent 100%); mask-image: linear-gradient(90deg, transparent 0, #000 24px, #000 calc(100% - 24px), transparent 100%); }
      .povar-chips-viewport::-webkit-scrollbar { display: none; }
    `;
    document.head.appendChild(el);
  }, []);

  useEffect(() => {
    if (screen === "main") showBanner();
    else hideBanner();
  }, [screen]);

  // Автоматически очищаем toast через 4 секунды + при смене экрана
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  // При смене экрана — сразу убираем toast (чтобы не висел в новом разделе)
  useEffect(() => {
    setToast(null);
  }, [screen]);

  useEffect(() => {
    if (!vkId) return;
    apiFetch(`${API}/daily_advice/${vkId}`)
      .then(r => r.json())
      .then(data => { if (data && data.greeting) setAdvice(data); })
      .catch(() => {});
  }, [vkId, birthday]);

  useEffect(() => {
    if (!vkId) return;
    loadChallengeState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vkId]);

  useEffect(() => {
    if (screen !== "main") return;
    if (!vkId || birthday) return;
    if (advice && advice.has_birthday) return;
    const asked = localStorage.getItem('bday_prompt_shown');
    if (asked) return;
    const t = setTimeout(() => {
      setShowBdayPrompt(true);
      localStorage.setItem('bday_prompt_shown', '1');
    }, 1500);
    return () => clearTimeout(t);
  }, [screen, vkId, birthday, advice]);

  function finishOnboarding() {
    localStorage.setItem('onboarding_done', '1');
    track('app_open'); track('onboarding_done');
    setScreen("main");
  }

  async function submitBirthday(iso) {
    setShowBdayPrompt(false);
    if (!vkId) return;
    try {
      const res = await apiFetch(`${API}/birthday`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ vk_id: vkId, birthday: iso, name: userName }),
      });
      const data = await res.json();
      if (data.status === "ok") {
        setBirthday(iso); track('birthday_saved');
        setToast({ message: "Спасибо! Совет станет персональным." });
      }
    } catch (_) {}
  }

  function skipBirthday() { setShowBdayPrompt(false); track('birthday_skipped'); }

  const handleGenerate = useCallback(async (extraPrompt = "", isRegeneration = false) => {
    if (!products.trim()) { setError("Напишите, что есть в холодильнике!"); return; }
    if (!vkId) { setError("Подождите секунду, приложение ещё загружается..."); return; }
    setError("");
    setScreen("loading");
    setLoadingIsFresh(false);

    // Минимальное время экрана загрузки — чтобы interstitial реклама успела показаться,
    // даже если ответ пришёл из архива за 50мс.
    const loadingStartedAt = Date.now();
    const MIN_LOADING_MS = 2500;

    // ПАРАЛЛЕЛЬНО: запускаем рекламу + запрос к бэку.
    // Сначала пытаемся interstitial. Если он в кулдауне (90 сек правило VK) —
    // показываем rewarded video. Так каждый запрос приносит рекламу.
    const adPromise = (async () => {
      if (isPremium) {
        return { type: 'none', shown: false, reason: 'premium' };
      }
      if (!isAdPointEnabled('generate')) {
        return { type: 'none', shown: false, reason: 'point_disabled' };
      }
      const inter = await showInterstitialBlocking();
      if (inter.shown) {
        return { type: 'interstitial', shown: true };
      }
      // Interstitial недоступен (кулдаун) — пробуем rewarded
      if (inter.reason === 'cooldown') {
        try {
          const rewardedShown = await showRewarded();
          return { type: 'rewarded', shown: rewardedShown };
        } catch (_) {
          return { type: 'rewarded', shown: false };
        }
      }
      return { type: 'interstitial', shown: false, reason: inter.reason };
    })();

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), GENERATE_TIMEOUT_MS);
    // Если ответ тянется дольше ~1.2с — это свежая генерация Гигой (база вернулась бы мгновенно).
    // Тогда на экране загрузки честно показываем «генерирую свежий рецепт».
    const freshHintTimer = setTimeout(() => setLoadingIsFresh(true), 1200);

    try {
      const res = await apiFetch(`${API}/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal: controller.signal,
        body: JSON.stringify({
          vk_id: vkId,
          products: extraPrompt ? products + ". " + extraPrompt : products,
          persons: 2, restrictions: "ничего", level: "средне", time: 30,
          is_regeneration: isRegeneration,
          context: context, name: userName, birthday: birthday,
        }),
      });

      if (!res.ok) throw new Error(`status ${res.status}`);
      const data = await res.json();

      if (data.user_name && data.user_name !== userName) setUserName(data.user_name);

      if (data.error === "not_food") {
        track('not_food_blocked'); setScreen("input");
        setToast({ message: data.message || "Хм, не получилось разобрать список 🤔 Попробуйте через запятую — например: курица, картошка, лук" });
        try { bridge.send('VKWebAppTapticNotificationOccurred', { type: 'warning' }); } catch (_) {}
        return;
      }
      if (data.error === "kids_safety") {
        track('kids_safety_blocked');
        setScreen("input");
        setToast({ message: data.message || "Для детей нельзя готовить с алкоголем 🚫" });
        try { bridge.send('VKWebAppTapticNotificationOccurred', { type: 'warning' }); } catch (_) {}
        return;
      }
      if (data.error === "too_fast") {
        setScreen("input");
        setToast({ message: data.message || "Подождите секунду и попробуйте снова" });
        return;
      }
      if (data.error === "daily_limit") {
        track('daily_limit_reached');
        if (typeof data.recipes_left === 'number') setRecipesLeft(data.recipes_left);
        if (typeof data.is_premium === 'boolean') setIsPremium(data.is_premium);
        setScreen("input");
        setToast({ message: data.message || "На сегодня свежие рецепты закончились. Готовые из базы — без ограничений 🌙" });
        try { bridge.send('VKWebAppTapticNotificationOccurred', { type: 'warning' }); } catch (_) {}
        return;
      }
      if (data.error) {
        let msg = data.message || "Попробуйте ещё раз.";
        if (/gigachat|некорректн|неожиданн|не удалось получить/i.test(msg)) {
          msg = "Что-то пошло не так. Попробуйте ещё раз через секунду 🌿";
        }
        setError(msg); setScreen("input"); return;
      }

      if (data.recipes && data.recipes.length > 0) {
        // Дожидаемся рекламы — это важно для монетизации
        const adResult = await adPromise;
        track('ad_shown', {
          type: adResult.type,
          shown: adResult.shown,
          reason: adResult.reason || 'ok',
        });
        // Лог в /ad_log для дашборда аналитики (M2)
        logAd(vkId, isRegeneration ? 'regenerate' : 'generate', {
          ad_type: adResult.type || 'none',
          shown: !!adResult.shown,
          reason: adResult.reason || (adResult.shown ? 'ok' : 'not_available'),
        });

        // Искусственная задержка нужна, только чтобы не проскочить мимо рекламы при быстром ответе.
        // Если рецепт из ГОТОВОЙ БАЗЫ — не тянем: реклама уже показана, всё готово, отдаём сразу.
        if (data.source !== "base") {
          const elapsed = Date.now() - loadingStartedAt;
          const remaining = MIN_LOADING_MS - elapsed;
          if (remaining > 0) await new Promise(r => setTimeout(r, remaining));
        }

        setRecipes(data.recipes);
        setRecipeSource(data.source || "");  // "base" = готовая база (мгновенно), "fresh" = свежая Гига
        if (typeof data.is_premium === 'boolean') setIsPremium(data.is_premium);
        if (typeof data.recipes_left === 'number') setRecipesLeft(data.recipes_left);
        setLimitEnabled(!!data.limit_enabled);
        loadDishImages(data.recipes);  // догружаем картинки блюд (рисуются в фоне на сервере)
        setUnusualInfo({
          is: !!data.is_unusual,
          reason: data.unusual_reason || "",
        });
        track('recipe_generated', {
          regeneration: isRegeneration,
          context: context || 'none',
          unusual: !!data.is_unusual,
        });
        setScreen("results");
      } else {
        setError("Не удалось получить рецепты. Попробуйте ещё раз.");
        setScreen("input");
      }
    } catch (e) {
      const msg = e.name === 'AbortError'
        ? "Запрос занял слишком долго. Попробуйте ещё раз."
        : "Что-то пошло не так. Проверьте интернет и попробуйте ещё раз.";
      setError(msg); setScreen("input");
    } finally {
      clearTimeout(timeoutId);
      clearTimeout(freshHintTimer);
    }
  }, [products, vkId, recipes.length, context, userName, birthday]);

  async function handleSave(recipe) {
    if (saving || !vkId) return;
    setSaving(true);
    // Показываем рекламу при сохранении (если не показывалась последние 90 сек)
    scheduleInterstitial(vkId, 'recipe_save');
    try {
      const res = await apiFetch(`${API}/save`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ vk_id: vkId, name: recipe.name, recipe_json: JSON.stringify(recipe), user_name: userName }),
      });
      const data = await res.json();
      if (data.error === "save_limit") {
        track('save_limit_reached');
        setInfo({
          title: "📖 Книга заполнена",
          text: `В вашей книге уже ${data.saved_limit || FREE_SAVED_LIMIT} рецептов.\n\nУдалите что-то ненужное чтобы сохранить новый.`,
          buttonText: "Открыть книгу", onClose: () => { setInfo(null); openBook(); },
        });
        try { bridge.send('VKWebAppTapticNotificationOccurred', { type: 'warning' }); } catch (_) {}
        return;
      }
      if (data.error === "already_saved") {
        setToast({
          message: "Рецепт уже есть в вашей книге 📖",
          action: "Открыть", onAction: () => { setToast(null); openBook(); }
        });
        try { bridge.send('VKWebAppTapticNotificationOccurred', { type: 'warning' }); } catch (_) {}
        return;
      }
      if (!res.ok || data.error) throw new Error();
      track('recipe_saved', { name: recipe.name });
      const count = data.saved_count || 0;
      const limit = data.saved_limit || FREE_SAVED_LIMIT;
      setToast({
        message: withName(userName, `рецепт сохранён · ${count}/${limit}`),
        action: "Открыть", onAction: () => { setToast(null); openBook(); }
      });
      try { bridge.send('VKWebAppTapticNotificationOccurred', { type: 'success' }); } catch (_) {}
    } catch (_) {
      setToast({ message: "Не удалось сохранить" });
    } finally { setSaving(false); }
  }

  // ===== Редактор рецептов (Фича 5) =====
  // ===== Общие рецепты (Этап 1) =====
  async function loadPublicRecipes(force) {
    if (publicRecipesLoaded && !force) return;
    try {
      const r = await apiFetch(`${API}/recipes_list?vk_id=${encodeURIComponent(vkId || "")}`);
      const d = await r.json();
      if (d && Array.isArray(d.recipes)) {
        setPublicRecipes(d.recipes);
      }
      setPublicRecipesLoaded(true);  // всегда снимаем загрузку (даже если пусто)
    } catch (e) {
      setPublicRecipesLoaded(true);
      setInfo({ title: "Не загрузилось", text: "База рецептов недоступна. Попробуйте позже." });
    }
  }

  function openRecipesBase() {
    track('recipes_base_open');
    scheduleInterstitial(vkId, 'recipes_base_open');
    setScreen("recipes_base");
    loadPublicRecipes(true);
  }

  async function openPublicRecipe(publicId) {
    try {
      const r = await apiFetch(`${API}/recipe/public/${publicId}?vk_id=${encodeURIComponent(vkId || "")}`);
      const d = await r.json();
      if (d && d.id) {
        setPublicView(d);
        setScreen("public_recipe");
      }
    } catch (_) {
      setInfo({ title: "Не открылось", text: "Не удалось загрузить рецепт." });
    }
  }

  // Опубликовать рецепт в общую базу («Поделиться с сообществом»)
  async function publishRecipe(savedId) {
    if (publishBusy) return;
    setPublishBusy(true);
    try {
      const r = await apiFetch(`${API}/recipe/publish`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ vk_id: vkId, saved_id: savedId }),
      });
      const d = await r.json();
      if (d.error === "bad_ingredients") {
        setInfo({ title: "Нельзя опубликовать", text: "В рецепте есть несъедобный ингредиент." });
      } else if (d.error === "bad_text") {
        setInfo({ title: "Текст не подходит", text: d.message || "Название или шаги содержат недопустимый текст." });
      } else if (d.error === "moderation_failed") {
        setInfo({ title: "Не удалось проверить", text: "Попробуйте позже." });
      } else if (d.error) {
        setInfo({ title: "Не получилось", text: d.message || "Попробуйте ещё раз." });
      } else if (d.status === "exists") {
        track('recipe_publish_exists');
        setInfo({ title: "Уже в базе", text: "Такой рецепт уже есть в общей базе. Скоро можно будет добавить своё фото к нему!" });
      } else {
        track('recipe_published');
        setInfo({ title: "Опубликовано! 🎉", text: "Ваш рецепт теперь в общей базе — другие смогут его найти." });
        setPublicRecipesLoaded(false);  // обновить список при следующем заходе
      }
    } catch (_) {
      setInfo({ title: "Нет связи", text: "Не удалось опубликовать, проверьте интернет." });
    } finally {
      setPublishBusy(false);
    }
  }

  // Сохранить публичный рецепт себе в книгу
  async function savePublicToBook(publicId) {
    try {
      const r = await apiFetch(`${API}/recipe/save_public`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ vk_id: vkId, public_id: publicId }),
      });
      const d = await r.json();
      if (d.error === "save_limit") {
        setInfo({ title: "Книга полна", text: d.message || "Удалите что-то ненужное." });
      } else if (d.error) {
        setInfo({ title: "Не получилось", text: d.message || "Попробуйте ещё раз." });
      } else if (d.status === "already") {
        setInfo({ title: "Уже в книге", text: "Этот рецепт уже сохранён у вас." });
      } else {
        track('public_recipe_saved');
        if (d.saves_count != null) {
          setPublicView(prev => prev ? { ...prev, saves_count: d.saves_count } : prev);
        }
        loadSavedRecipes();  // обновим книгу
        setToast({ message: "Сохранено в вашу книгу!" });
      }
    } catch (_) {
      setInfo({ title: "Нет связи", text: "Не удалось сохранить." });
    }
  }

  // Лайк рецепта (toggle: поставить/убрать). Оптимистично обновляем UI.
  async function toggleLike(publicId, fromScreen) {
    if (!publicId) return;
    try {
      const r = await apiFetch(`${API}/recipe/like`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ vk_id: vkId, public_id: publicId }),
      });
      const d = await r.json();
      if (d.error) return;
      // обновляем в просмотре
      setPublicView(prev => (prev && prev.id === publicId)
        ? { ...prev, liked: d.liked, likes_count: d.likes_count } : prev);
      // обновляем в списке базы
      setPublicRecipes(prev => Array.isArray(prev)
        ? prev.map(x => x.id === publicId ? { ...x, liked: d.liked, likes_count: d.likes_count } : x) : prev);
      // обновляем в книге (по public_id)
      setSavedRecipes(prev => Array.isArray(prev)
        ? prev.map(x => x.public_id === publicId ? { ...x, liked: d.liked, likes_count: d.likes_count } : x) : prev);
      if (d.liked) track('recipe_liked');
    } catch (_) { /* молча — лайк не критичен */ }
  }

  // Добавить фото в галерею публичного рецепта (та же загрузка на ВК, что и для рецепта)
  async function addGalleryPhoto(publicId, file) {
    if (!file || !publicId || galleryUploading) return;
    if (file.size > 15 * 1024 * 1024) {
      setInfo({ title: "Файл большой", text: "Фото до 15 МБ." });
      return;
    }
    setGalleryUploading(true);
    try {
      // 1. токен
      const auth = await bridge.send('VKWebAppGetAuthToken', { app_id: 54552158, scope: 'photos' });
      const token = auth && auth.access_token;
      if (!token) throw new Error('no_token');
      // 2. upload server
      const srv = await bridge.send('VKWebAppCallAPIMethod', {
        method: 'photos.getWallUploadServer', params: { access_token: token, v: '5.131' },
      });
      const uploadUrl = srv && srv.response && srv.response.upload_url;
      if (!uploadUrl) throw new Error('no_url');
      // 3. файл через наш прокси (используем saved-прокси с любым нашим рецептом? нет — нужен общий)
      //    Прокси /saved/{id}/photo_upload требует владения рецептом. Для галереи используем
      //    отдельный путь: грузим через прокси без привязки к saved (сервер вернёт server/photo/hash).
      const fd = new FormData();
      fd.append('vk_id', vkId);
      fd.append('upload_url', uploadUrl);
      fd.append('file', file, file.name || 'photo.jpg');
      const r = await fetch(`${API}/photo_upload_public`, {
        method: "POST", headers: { "X-VK-Launch-Params": VK_LAUNCH_PARAMS }, body: fd,
      });
      const d = await r.json();
      if (d.error) { setInfo({ title: "Не загрузилось", text: d.message || "Попробуйте ещё раз." }); setGalleryUploading(false); return; }
      // 4. saveWallPhoto на клиенте
      const saved = await bridge.send('VKWebAppCallAPIMethod', {
        method: 'photos.saveWallPhoto',
        params: { server: d.server, photo: d.photo, hash: d.hash, access_token: token, v: '5.131' },
      });
      const photoObj = saved && saved.response && saved.response[0];
      if (!photoObj) throw new Error('save_failed');
      let picUrl = '';
      if (Array.isArray(photoObj.sizes) && photoObj.sizes.length) {
        const best = photoObj.sizes.reduce((a, b) => (b.width || 0) > (a.width || 0) ? b : a);
        picUrl = best.url || '';
      }
      if (!picUrl) throw new Error('no_pic');
      // 5. показываем модалку коммента (фото загружено, осталось опц. коммент)
      setGalleryUploading(false);
      setGalleryCaption("");
      setGalleryCaptionModal({ publicId, photoUrl: picUrl });
    } catch (e) {
      const reason = e && e.error_data && (e.error_data.error_reason);
      if (reason === 'User denied' || (e && e.error_type === 'client_error')) { setGalleryUploading(false); return; }
      setInfo({ title: "Не удалось загрузить", text: "Попробуйте ещё раз или другой снимок." });
      setGalleryUploading(false);
    }
  }

  // Отправить фото в галерею с комментарием
  async function submitGalleryCaption() {
    const m = galleryCaptionModal;
    if (!m) return;
    try {
      const r = await apiFetch(`${API}/recipe/public/${m.publicId}/add_photo`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ vk_id: vkId, photo_url: m.photoUrl, caption: galleryCaption || "" }),
      });
      const d = await r.json();
      setGalleryCaptionModal(null);
      if (d.error === "bad_text") {
        setInfo({ title: "Комментарий не подходит", text: "Уберите недопустимый текст." });
      } else if (d.error) {
        setInfo({ title: "Не сохранилось", text: d.message || "Попробуйте ещё раз." });
      } else {
        track('gallery_photo_added');
        // обновим галерею в открытом рецепте
        if (Array.isArray(d.photos)) {
          setPublicView(prev => prev ? { ...prev, photos: d.photos } : prev);
        }
        setToast({ message: "Фото добавлено в галерею!" });
      }
    } catch (_) {
      setGalleryCaptionModal(null);
      setInfo({ title: "Нет связи", text: "Не удалось сохранить." });
    }
  }

  // Удалить СВОЁ фото из галереи (с подтверждением)
  async function deleteGalleryPhoto(publicId, photoId) {
    setConfirm({
      title: "Удалить фото?",
      text: "Ваше фото будет убрано из ленты этого рецепта.",
      confirmText: "Удалить",
      onConfirm: async () => {
        setConfirm(null);
        try {
          const r = await apiFetch(`${API}/recipe/public/${publicId}/delete_photo`, {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ vk_id: vkId, photo_id: photoId }),
          });
          const d = await r.json();
          if (d.error === "not_owner") {
            setInfo({ title: "Нельзя", text: "Можно удалить только своё фото." });
          } else if (d.error) {
            setInfo({ title: "Не удалилось", text: d.message || "Попробуйте ещё раз." });
          } else {
            track('gallery_photo_deleted');
            if (Array.isArray(d.photos)) {
              setPublicView(prev => prev ? { ...prev, photos: d.photos } : prev);
            }
            setToast({ message: "Фото удалено" });
          }
        } catch (_) {
          setInfo({ title: "Нет связи", text: "Не удалось удалить." });
        }
      },
    });
  }

  // Открыть модалку редактирования своего фото
  function openEditPhoto(publicId, ph) {
    setEditPhotoModal({ publicId, photoId: ph.id, currentUrl: ph.photo_url });
    setEditPhotoCaption(ph.caption || "");
  }

  // Заменить картинку (перезалить новое фото на ВК → обновить в ленте)
  async function editPhotoNewImage(file) {
    const m = editPhotoModal;
    if (!m || !file || editPhotoBusy) return;
    if (file.size > 15 * 1024 * 1024) { setInfo({ title: "Файл большой", text: "Фото до 15 МБ." }); return; }
    setEditPhotoBusy(true);
    try {
      const auth = await bridge.send('VKWebAppGetAuthToken', { app_id: 54552158, scope: 'photos' });
      const token = auth && auth.access_token;
      if (!token) throw new Error('no_token');
      const srv = await bridge.send('VKWebAppCallAPIMethod', {
        method: 'photos.getWallUploadServer', params: { access_token: token, v: '5.131' },
      });
      const uploadUrl = srv && srv.response && srv.response.upload_url;
      if (!uploadUrl) throw new Error('no_url');
      const fd = new FormData();
      fd.append('vk_id', vkId); fd.append('upload_url', uploadUrl);
      fd.append('file', file, file.name || 'photo.jpg');
      const r = await fetch(`${API}/photo_upload_public`, {
        method: "POST", headers: { "X-VK-Launch-Params": VK_LAUNCH_PARAMS }, body: fd,
      });
      const d = await r.json();
      if (d.error) { setInfo({ title: "Не загрузилось", text: d.message || "Попробуйте ещё раз." }); setEditPhotoBusy(false); return; }
      const saved = await bridge.send('VKWebAppCallAPIMethod', {
        method: 'photos.saveWallPhoto',
        params: { server: d.server, photo: d.photo, hash: d.hash, access_token: token, v: '5.131' },
      });
      const photoObj = saved && saved.response && saved.response[0];
      let picUrl = '';
      if (photoObj && Array.isArray(photoObj.sizes) && photoObj.sizes.length) {
        const best = photoObj.sizes.reduce((a, b) => (b.width || 0) > (a.width || 0) ? b : a);
        picUrl = best.url || '';
      }
      if (!picUrl) throw new Error('no_pic');
      // сразу применяем замену фото на сервере
      const res = await apiFetch(`${API}/recipe/public/${m.publicId}/edit_photo`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ vk_id: vkId, photo_id: m.photoId, new_photo_url: picUrl, caption: editPhotoCaption || "" }),
      });
      const ed = await res.json();
      if (Array.isArray(ed.photos)) setPublicView(prev => prev ? { ...prev, photos: ed.photos } : prev);
      setEditPhotoBusy(false);
      setToast({ message: "Фото обновлено" });
    } catch (e) {
      setInfo({ title: "Не удалось заменить", text: "Попробуйте ещё раз." });
      setEditPhotoBusy(false);
    }
  }

  // Сохранить изменения (только коммент, без замены картинки)
  async function submitEditPhoto() {
    const m = editPhotoModal;
    if (!m) return;
    try {
      const r = await apiFetch(`${API}/recipe/public/${m.publicId}/edit_photo`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ vk_id: vkId, photo_id: m.photoId, caption: editPhotoCaption || "" }),
      });
      const d = await r.json();
      setEditPhotoModal(null);
      if (d.error === "bad_text") {
        setInfo({ title: "Комментарий не подходит", text: "Уберите недопустимый текст." });
      } else if (d.error) {
        setInfo({ title: "Не сохранилось", text: d.message || "Попробуйте ещё раз." });
      } else {
        if (Array.isArray(d.photos)) setPublicView(prev => prev ? { ...prev, photos: d.photos } : prev);
        setToast({ message: "Изменено" });
      }
    } catch (_) {
      setEditPhotoModal(null);
      setInfo({ title: "Нет связи", text: "Не удалось сохранить." });
    }
  }

  // Открыть модалку публикации своего рецепта в базу (выбор фото)
  function openPublishOwn(item) {
    setPublishOwnModal({ savedId: item.id, name: item.name });
  }

  // Опубликовать свой рецепт со СГЕНЕРИРОВАННЫМ фото
  async function publishOwnGenerated() {
    const m = publishOwnModal;
    if (!m || publishOwnBusy) return;
    setPublishOwnBusy(true);
    try {
      const r = await apiFetch(`${API}/recipe/publish_own`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ vk_id: vkId, saved_id: m.savedId, use_generated: true }),
      });
      const d = await r.json();
      setPublishOwnBusy(false);
      setPublishOwnModal(null);
      if (d.error === "exists") {
        setInfo({ title: "Уже в базе", text: "Такой рецепт уже есть в общей базе." });
      } else if (d.error) {
        setInfo({ title: "Не получилось", text: d.message || "Попробуйте ещё раз." });
      } else {
        track('own_recipe_published_generated');
        setInfo({ title: "Готово! 🎉", text: "Ваш рецепт добавлен в общую базу с картинкой блюда." });
      }
    } catch (_) {
      setPublishOwnBusy(false); setPublishOwnModal(null);
      setInfo({ title: "Нет связи", text: "Не удалось опубликовать." });
    }
  }

  // Опубликовать свой рецепт со СВОИМ фото (загрузка на ВК → публикация)
  async function publishOwnWithPhoto(file) {
    const m = publishOwnModal;
    if (!m || !file || publishOwnBusy) return;
    if (file.size > 15 * 1024 * 1024) { setInfo({ title: "Файл большой", text: "Фото до 15 МБ." }); return; }
    setPublishOwnBusy(true);
    try {
      const auth = await bridge.send('VKWebAppGetAuthToken', { app_id: 54552158, scope: 'photos' });
      const token = auth && auth.access_token;
      if (!token) throw new Error('no_token');
      const srv = await bridge.send('VKWebAppCallAPIMethod', {
        method: 'photos.getWallUploadServer', params: { access_token: token, v: '5.131' },
      });
      const uploadUrl = srv && srv.response && srv.response.upload_url;
      if (!uploadUrl) throw new Error('no_url');
      const fd = new FormData();
      fd.append('vk_id', vkId); fd.append('upload_url', uploadUrl);
      fd.append('file', file, file.name || 'photo.jpg');
      const up = await fetch(`${API}/photo_upload_public`, {
        method: "POST", headers: { "X-VK-Launch-Params": VK_LAUNCH_PARAMS }, body: fd,
      });
      const ud = await up.json();
      if (ud.error) { setInfo({ title: "Не загрузилось", text: ud.message || "Попробуйте ещё раз." }); setPublishOwnBusy(false); return; }
      const saved = await bridge.send('VKWebAppCallAPIMethod', {
        method: 'photos.saveWallPhoto',
        params: { server: ud.server, photo: ud.photo, hash: ud.hash, access_token: token, v: '5.131' },
      });
      const photoObj = saved && saved.response && saved.response[0];
      let picUrl = '';
      if (photoObj && Array.isArray(photoObj.sizes) && photoObj.sizes.length) {
        const best = photoObj.sizes.reduce((a, b) => (b.width || 0) > (a.width || 0) ? b : a);
        picUrl = best.url || '';
      }
      if (!picUrl) throw new Error('no_pic');
      const r = await apiFetch(`${API}/recipe/publish_own`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ vk_id: vkId, saved_id: m.savedId, use_generated: false, own_photo_url: picUrl }),
      });
      const d = await r.json();
      setPublishOwnBusy(false); setPublishOwnModal(null);
      if (d.error === "exists") {
        setInfo({ title: "Уже в базе", text: "Такой рецепт уже есть." });
      } else if (d.error) {
        setInfo({ title: "Не получилось", text: d.message || "Попробуйте ещё раз." });
      } else {
        track('own_recipe_published_photo');
        setInfo({ title: "Готово! 🎉", text: "Ваш рецепт добавлен в общую базу с вашим фото." });
      }
    } catch (e) {
      setPublishOwnBusy(false); setPublishOwnModal(null);
      setInfo({ title: "Не удалось", text: "Попробуйте ещё раз." });
    }
  }

  // Модалка публикации своего рецепта. Рендерится на НЕСКОЛЬКИХ экранах (книга + просмотр),
  // поэтому вынесена в общую функцию — иначе на просмотре кнопка «Поделиться» ставит состояние,
  // но окна нет (раньше модалка жила только в экране книги).
  function renderPublishModal() {
    if (!publishOwnModal) return null;
    return (
      <div style={styles.modalOverlay} className="povar-fade" onClick={() => !publishOwnBusy && setPublishOwnModal(null)}>
        <div style={styles.modalCard} className="povar-pop" onClick={ev => ev.stopPropagation()}>
          <div style={styles.modalTitle}>Поделиться в общей базе</div>
          {publishOwnBusy ? (
            <div style={{ textAlign: "center", padding: "18px 8px 6px" }}>
              <img src={FRIDGE_IMG} alt="" style={{ width: 72, height: 72, animation: "pubFridge 1.1s infinite", display: "inline-block" }} />
              <div style={{ fontSize: 16, color: "#1c2a1c", fontWeight: 600, marginTop: 14 }}>Проверяю продукты и готовлю картинку</div>
              <div style={{ fontSize: 14, color: "#8a9a8a", marginTop: 4 }}>Это займёт несколько секунд…</div>
              <div style={{ display: "flex", justifyContent: "center", gap: 6, marginTop: 14 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#0f6e56", display: "inline-block", animation: "pubDot 1s infinite 0s" }} />
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#0f6e56", display: "inline-block", animation: "pubDot 1s infinite 0.15s" }} />
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#0f6e56", display: "inline-block", animation: "pubDot 1s infinite 0.3s" }} />
              </div>
              <style>{`
                @keyframes pubFridge { 0%,100%{transform:translateY(0) rotate(-3deg)} 50%{transform:translateY(-7px) rotate(3deg)} }
                @keyframes pubDot { 0%,100%{opacity:0.3;transform:translateY(0)} 50%{opacity:1;transform:translateY(-5px)} }
              `}</style>
            </div>
          ) : (
            <>
              <div style={styles.modalText}>
                «{publishOwnModal.name}» появится в общей базе рецептов. Какое фото показать?
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
                <button style={styles.modalBtnInfo} className="povar-press"
                  onClick={() => publishOwnGenerated()}>
                  🎨 Сгенерировать картинку блюда
                </button>
                <label style={{ ...styles.secondaryButton, textAlign: "center", display: "block", cursor: "pointer" }}>
                  📷 Загрузить своё фото
                  <input type="file" accept="image/*" style={{ display: "none" }}
                    onChange={ev => { const f = ev.target.files && ev.target.files[0]; if (f) publishOwnWithPhoto(f); ev.target.value = ""; }} />
                </label>
                <button style={styles.modalBtnCancel} className="povar-press"
                  onClick={() => setPublishOwnModal(null)}>Отмена</button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // Открыть модалку редактирования своего фото
  function openEditor(item) {
    const r = item.recipe || {};
    setEditRecipe({
      id: item.id,
      recipe_type: item.recipe_type || "ai",
      name: r.name || item.name || "",
      ingredients: Array.isArray(r.ingredients)
        ? r.ingredients.map(i => (typeof i === "string"
            ? { name: i, amount: "" }
            : { name: i.name || "", amount: i.amount || "" }))
        : [],
      steps: Array.isArray(r.steps) ? r.steps.map(s => String(s)) : [],
      step_photos: Array.isArray(r.step_photos) ? r.step_photos.map(s => String(s || "")) : [],
      note: r.user_note || "",
      photo_url: item.photo_url || "",
      _orig: r,  // сохраняем оригинал чтобы не потерять прочие поля (time, difficulty, health и т.д.)
    });
    setEditAskMode(false);
    setScreen("editor");
  }

  // Создать свой рецепт с нуля — пустой редактор (recipe_type=own, id=null)
  function openCreateOwn() {
    track('create_own_open');
    loadProductsBase();  // подгрузим базу для автодополнения ингредиентов
    setEditRecipe({
      id: null,           // нет id = создание нового
      recipe_type: "own",
      name: "",
      ingredients: [{ name: "", amount: "" }],
      steps: [""],
      step_photos: [""],
      note: "",
      photo_url: "",
      _orig: {},
    });
    setEditAskMode(false);
    setScreen("editor");
  }

  function editSetIngredient(idx, field, value) {
    setEditRecipe(prev => {
      const ings = [...prev.ingredients];
      ings[idx] = { ...ings[idx], [field]: value };
      return { ...prev, ingredients: ings };
    });
  }
  function editAddIngredient() {
    setEditRecipe(prev => ({ ...prev, ingredients: [...prev.ingredients, { name: "", amount: "" }] }));
  }
  function editRemoveIngredient(idx) {
    setEditRecipe(prev => ({ ...prev, ingredients: prev.ingredients.filter((_, i) => i !== idx) }));
  }
  function editSetStep(idx, value) {
    setEditRecipe(prev => { const s = [...prev.steps]; s[idx] = value; return { ...prev, steps: s }; });
  }
  function editAddStep() {
    setEditRecipe(prev => ({ ...prev, steps: [...prev.steps, ""], step_photos: [...(prev.step_photos || []), ""] }));
  }
  function editRemoveStep(idx) {
    setEditRecipe(prev => ({
      ...prev,
      steps: prev.steps.filter((_, i) => i !== idx),
      step_photos: (prev.step_photos || []).filter((_, i) => i !== idx),
    }));
  }
  function editMoveStep(idx, dir) {
    setEditRecipe(prev => {
      const s = [...prev.steps];
      const ph = [...(prev.step_photos || [])];
      const j = idx + dir;
      if (j < 0 || j >= s.length) return prev;
      [s[idx], s[j]] = [s[j], s[idx]];            // шаги местами
      while (ph.length < s.length) ph.push("");    // выровнять длину фото
      [ph[idx], ph[j]] = [ph[j], ph[idx]];         // фото двигаем вместе с шагом
      return { ...prev, steps: s, step_photos: ph };
    });
  }
  function editInsertStepAt(idx) {
    setEditRecipe(prev => {
      const s = [...prev.steps];
      const ph = [...(prev.step_photos || [])];
      s.splice(idx + 1, 0, "");                     // пустой шаг ПОСЛЕ idx
      while (ph.length < s.length - 1) ph.push("");
      ph.splice(idx + 1, 0, "");                    // пустое фото на ту же позицию
      return { ...prev, steps: s, step_photos: ph };
    });
  }
  // Загрузка фото ШАГА. Тот же VK-флоу, что и фото блюда, но через ОБЩИЙ прокси
  // (без id рецепта — работает и при создании). URL ложится в step_photos[idx] внутри recipe_json.
  async function uploadStepPhoto(idx, file) {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      setInfo({ title: "Файл большой", text: "Фото до 15 МБ. Выберите снимок поменьше." });
      return;
    }
    setStepPhotoUploading(idx);
    try {
      const auth = await bridge.send('VKWebAppGetAuthToken', { app_id: 54552158, scope: 'photos' });
      const token = auth && auth.access_token;
      if (!token) throw new Error('no_token');
      const srv = await bridge.send('VKWebAppCallAPIMethod', {
        method: 'photos.getWallUploadServer',
        params: { access_token: token, v: '5.131' },
      });
      const uploadUrl = srv && srv.response && srv.response.upload_url;
      if (!uploadUrl) throw new Error('no_upload_url');
      const fd = new FormData();
      fd.append('vk_id', vkId);
      fd.append('upload_url', uploadUrl);
      fd.append('file', file, file.name || 'photo.jpg');
      const r = await fetch(`${API}/photo_upload_public`, {
        method: "POST",
        headers: { "X-VK-Launch-Params": VK_LAUNCH_PARAMS },
        body: fd,
      });
      const d = await r.json();
      if (d.error) { setInfo({ title: "Не сохранилось", text: d.message || "Попробуйте ещё раз." }); setStepPhotoUploading(-1); return; }
      const saved = await bridge.send('VKWebAppCallAPIMethod', {
        method: 'photos.saveWallPhoto',
        params: { server: d.server, photo: d.photo, hash: d.hash, access_token: token, v: '5.131' },
      });
      const photoObj = saved && saved.response && saved.response[0];
      if (!photoObj) throw new Error('save_failed');
      let picUrl = '';
      if (Array.isArray(photoObj.sizes) && photoObj.sizes.length) {
        const best = photoObj.sizes.reduce((a, b) => (b.width || 0) > (a.width || 0) ? b : a);
        picUrl = best.url || '';
      }
      if (!picUrl) throw new Error('no_pic_url');
      setEditRecipe(prev => {
        if (!prev) return prev;
        const sp = [...(prev.step_photos || [])];
        while (sp.length <= idx) sp.push("");
        sp[idx] = picUrl;
        return { ...prev, step_photos: sp };
      });
      track('step_photo_added');
      setToast({ message: "Фото шага добавлено!" });
    } catch (e) {
      const reason = e && e.error_data && (e.error_data.error_reason || e.error_data.error_msg);
      if (reason === 'User denied' || (e && e.error_type === 'client_error')) { setStepPhotoUploading(-1); return; }
      setInfo({ title: "Не удалось загрузить", text: "Фото шага не загрузилось. Попробуйте ещё раз." });
    } finally {
      setStepPhotoUploading(-1);
    }
  }
  function removeStepPhoto(idx) {
    setEditRecipe(prev => {
      if (!prev) return prev;
      const sp = [...(prev.step_photos || [])];
      if (idx < sp.length) sp[idx] = "";
      return { ...prev, step_photos: sp };
    });
  }

  // Собрать recipe_json из состояния редактора (сохранив прочие поля оригинала)
  function buildEditedRecipe() {
    const e = editRecipe;
    const ingredients = e.ingredients
      .map(i => ({ name: (i.name || "").trim(), amount: (i.amount || "").trim() }))
      .filter(i => i.name);
    // нулёвки (соль/вода/специи) без количества не пилим — их «по вкусу» норм
    const ZERO_OK = ["соль", "вода", "перец", "специ", "приправ", "лёд", "лед"];
    const noAmount = ingredients
      .filter(i => !i.amount && !ZERO_OK.some(z => i.name.toLowerCase().includes(z)))
      .map(i => i.name);
    // шаги фильтруем от пустых, фото шагов держим выровненными по индексу (пара текст+фото)
    const stepPairs = e.steps
      .map((s, i) => ({ text: (s || "").trim(), photo: ((e.step_photos || [])[i] || "") }))
      .filter(p => p.text);
    const steps = stepPairs.map(p => p.text);
    const step_photos = stepPairs.map(p => p.photo);
    return {
      ...(e._orig || {}),
      name: (e.name || "").trim(),
      ingredients,
      steps,
      step_photos,
      user_note: (e.note || "").slice(0, 300),
      _noAmount: noAmount,  // служебное, вырезается перед отправкой
    };
  }

  // Сохранить правку. saveAsNew: true → копия, false → заменить.
  async function saveEditedRecipe(saveAsNew, thenShare = false) {
    const e = editRecipe;
    if (!e) return;
    const name = (e.name || "").trim();
    if (name.length < 2) { setInfo({ title: "Нужно название", text: "Введите название рецепта (хотя бы 2 символа)." }); return; }
    const built = buildEditedRecipe();
    if (built.ingredients.length === 0 && built.steps.length === 0) {
      setInfo({ title: "Пустой рецепт", text: "Добавьте хотя бы один ингредиент или шаг." });
      return;
    }
    if (built._noAmount && built._noAmount.length > 0) {
      setInfo({ title: "Не хватает количества", text: `Укажите сколько класть: ${built._noAmount.join(", ")}. Можно в граммах («100 г») или штуках («2 шт») — тогда КБЖУ посчитается точно.` });
      return;
    }
    delete built._noAmount;
    setEditBusy(true);
    try {
      const res = await apiFetch(`${API}/saved/${e.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          vk_id: vkId,
          recipe_json: JSON.stringify(built),
          save_as_new: saveAsNew,
          persons: 4,
        }),
      });
      const d = await res.json();
      if (d.error === "bad_text") {
        setInfo({ title: "Текст не подходит", text: d.message || "Проверьте название и шаги — есть недопустимый текст." });
      } else if (d.error === "moderation_failed") {
        setInfo({ title: "Не удалось проверить", text: "Проверка текста не прошла, попробуйте ещё раз." });
      } else if (d.error === "save_limit") {
        setInfo({ title: "Книга полна", text: d.message || "Удалите что-то ненужное." });
      } else if (d.error) {
        setInfo({ title: "Не получилось", text: d.message || "Попробуйте ещё раз." });
      } else {
        // успех
        track('recipe_edited', { mode: saveAsNew ? 'new' : 'replace' });
        setEditRecipe(null);
        setEditAskMode(false);
        setScreen("book");
        loadSavedRecipes();
        if (thenShare && e.id != null) {
          openPublishOwn({ id: e.id, name });  // публикуем свежесохранённую версию (модалка поверх книги)
        }
      }
    } catch (_) {
      setInfo({ title: "Нет связи", text: "Не удалось сохранить, проверьте интернет." });
    } finally {
      setEditBusy(false);
    }
  }

  // Создать новый свой рецепт (id=null) через create_own
  async function createOwnRecipe() {
    const e = editRecipe;
    if (!e) return;
    const name = (e.name || "").trim();
    if (name.length < 2) { setInfo({ title: "Нужно название", text: "Введите название рецепта." }); return; }
    const built = buildEditedRecipe();
    if (built.ingredients.length === 0 && built.steps.length === 0) {
      setInfo({ title: "Пустой рецепт", text: "Добавьте хотя бы один ингредиент или шаг." });
      return;
    }
    if (built._noAmount && built._noAmount.length > 0) {
      setInfo({ title: "Не хватает количества", text: `Укажите сколько класть: ${built._noAmount.join(", ")}. Можно в граммах («100 г») или штуках («2 шт») — тогда КБЖУ посчитается точно.` });
      return;
    }
    delete built._noAmount;
    setEditBusy(true);
    try {
      const res = await apiFetch(`${API}/recipe/create_own`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ vk_id: vkId, recipe_json: JSON.stringify(built), persons: 4 }),
      });
      const d = await res.json();
      if (d.error === "bad_text") {
        setInfo({ title: "Текст не подходит", text: d.message || "Проверьте название и шаги." });
      } else if (d.error === "moderation_failed") {
        setInfo({ title: "Не удалось проверить", text: "Попробуйте ещё раз." });
      } else if (d.error === "save_limit") {
        setInfo({ title: "Книга полна", text: d.message || "Удалите что-то ненужное." });
      } else if (d.error) {
        setInfo({ title: "Не получилось", text: d.message || "Попробуйте ещё раз." });
      } else {
        track('own_recipe_created');
        setEditRecipe(null);
        setScreen("book");
        loadSavedRecipes();
        setToast({ message: "Рецепт создан!" });
      }
    } catch (_) {
      setInfo({ title: "Нет связи", text: "Не удалось сохранить." });
    } finally {
      setEditBusy(false);
    }
  }

  // Нажатие «Сохранить» в редакторе: для ai-рецепта спрашиваем заменить/новый,
  // для edited/own — просто заменяем. Для нового (id=null) — создаём.
  function onEditorSave() {
    if (!editRecipe) return;
    if (editRecipe.id == null) {
      createOwnRecipe();  // новый свой рецепт с нуля
    } else if (editRecipe.recipe_type === "ai") {
      setEditAskMode(true);  // показать выбор заменить/новый
    } else {
      saveEditedRecipe(false);  // свой/изменённый — просто перезаписать
    }
  }

  async function loadSavedRecipes() {
    if (!vkId) return;
    setSavedLoading(true);
    try {
      const res = await apiFetch(`${API}/saved/${vkId}`);
      if (!res.ok) throw new Error();
      const data = await res.json();
      const parsed = (data.recipes || []).map(r => {
        let recipe = null;
        try { recipe = JSON.parse(r.recipe_json); } catch (_) {}
        return { id: r.id, name: r.name, recipe, recipe_type: r.recipe_type || "ai", photo_url: r.photo_url || "", image_url: r.image_url || "" };
      });
      setSavedRecipes(parsed);
    } catch (_) { setSavedRecipes([]); }
    finally { setSavedLoading(false); }
  }

  function openBook() { track('book_open'); scheduleInterstitial(vkId, 'book_open'); setScreen("book"); loadSavedRecipes(); }

  function openProductRef() { track('product_ref_open'); scheduleInterstitial(vkId, 'product_ref_open'); setProductReturnScreen(null); setScreen("product_ref"); setShowBase(false); loadProductsBase(); }

  // Открыть карточку продукта из рецепта (тап по ингредиенту). Запоминаем экран рецепта для возврата.
  function openProductFromRecipe(name) {
    const nm = (name || "").trim();
    if (!nm) return;
    track('product_from_recipe', { product: nm });
    setProductReturnScreen(screen);
    setShowBase(false);
    setProductError("");
    setProductInput(nm);
    loadProductsBase();
    setScreen("product_ref");
    fetchProductInfo(nm);
  }

  // Dev-оверлей: переключить точку рекламы (только владелец). Меняет локально сразу + пишет на сервер.
  const toggleAdPoint = (point) => {
    const cur = adPointsConfig ? (adPointsConfig[point] !== false) : true;
    const next = !cur;
    if (!adPointsConfig) adPointsConfig = {};
    adPointsConfig[point] = next;     // мгновенно — чтобы isAdPointEnabled сразу видел
    setDevTick((t) => t + 1);          // форсим перерисовку плашек
    try {
      apiFetch(`${API}/ad_point_toggle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vk_id: vkId, point, enabled: next }),
      }).catch(() => {});
    } catch (_) {}
  };

  // Догрузка картинок блюд: они рисуются в фоне на сервере (~30-40 сек), поэтому
  // запрашиваем повторно, пока не появятся (или пока не выйдет лимит попыток).
  // Кладём в dishImages (название→url) — список и карточка читают оттуда.
  const loadDishImages = (recipeList) => {
    const names = (recipeList || [])
      .map(r => (r && r.name ? String(r.name) : ""))
      .filter(Boolean);
    if (!names.length) return;
    const got = new Set();
    let attempts = 0;
    const maxAttempts = 8;     // ~8 попыток
    const stepMs = 10000;      // каждые 10 сек → до ~80 сек
    const tick = async () => {
      attempts += 1;
      try {
        const res = await apiFetch(`${API}/dish_images`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ names }),
        });
        const data = await res.json();
        const imgs = (data && data.images) || {};
        const found = {};
        for (const n of names) {
          if (imgs[n]) { found[n] = imgs[n]; got.add(n); }
        }
        if (Object.keys(found).length) {
          setDishImages(prev => ({ ...prev, ...found }));
        }
      } catch (_) { /* сеть моргнула — просто попробуем ещё раз */ }
      if (got.size >= names.length || attempts >= maxAttempts) { setImagesLoading(false); return; }  // всё есть или лимит
      setTimeout(tick, stepMs);
    };
    setImagesLoading(true);  // пока опрашиваем — показываем спиннер вместо фото
    tick();  // первый запрос сразу
  };

  // ===== Фича 6 — Мои продукты =====
  function openMyProducts() {
    track('my_products_open');
    setScreen("my_products");
    setMpMode(null); setMpError("");
    loadMyProducts();
  }

  async function loadMyProducts() {
    if (!vkId) return;
    setMyProductsLoading(true);
    try {
      const r = await apiFetch(`${API}/my_products/list`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ vk_id: vkId }),
      });
      const d = await r.json();
      setMyProducts(Array.isArray(d.products) ? d.products : []);
    } catch (_) {
      setMyProducts([]);
    } finally {
      setMyProductsLoading(false);
    }
  }

  function resetMpForm() {
    setMpName(""); setMpCal(""); setMpProt(""); setMpFat(""); setMpCarb(""); setMpNote("");
    setMpError(""); setMpMode(null); setMpEditId(null); setMpFound(null);
  }

  // Калории по формуле Этуотера из Б/Ж/У (белки*4 + жиры*9 + углеводы*4).
  function calcCaloriesFromMacros(prot, fat, carb) {
    const p = parseFloat(String(prot).replace(",", ".")) || 0;
    const f = parseFloat(String(fat).replace(",", ".")) || 0;
    const c = parseFloat(String(carb).replace(",", ".")) || 0;
    return Math.round(p * 4 + f * 9 + c * 4);
  }

  // При изменении Б/Ж/У — автообновляем калории (формула). Юзер может перебить вручную.
  function updateMacro(which, value) {
    const v = value.slice(0, 6);
    let prot = mpProt, fat = mpFat, carb = mpCarb;
    if (which === "prot") { setMpProt(v); prot = v; }
    if (which === "fat") { setMpFat(v); fat = v; }
    if (which === "carb") { setMpCarb(v); carb = v; }
    // пересчитываем калории автоматом
    const auto = calcCaloriesFromMacros(prot, fat, carb);
    if (auto > 0) setMpCal(String(auto));
  }

  async function removeMyProduct(productId) {
    if (!vkId) return;
    setMyProducts(prev => prev.filter(p => p.id !== productId));
    try {
      await apiFetch(`${API}/my_products/remove`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ vk_id: vkId, product_id: productId }),
      });
      track('my_product_removed');
    } catch (_) {
      loadMyProducts();
    }
  }

  // Единый путь сохранения продукта. Бэк сам решает: найден в базе / новый (Гига-проверка).
  // confirmExisting — подтвердить сохранение найденного; forceNew — «вариант 2» (свой при совпадении).
  async function saveProduct({ confirmExisting = false, forceNew = false, adWatched = false } = {}) {
    const name = mpName.trim();
    if (name.length < 2) { setMpError("Введите название продукта"); return; }
    const cal = parseFloat(String(mpCal).replace(",", "."));
    if (isNaN(cal)) { setMpError("Введите калории (число)"); return; }

    setMpBusy(true); setMpError("");
    try {
      const r = await apiFetch(`${API}/my_products/add_manual`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          vk_id: vkId, name, cal,
          proteins: parseFloat(String(mpProt).replace(",", ".")) || 0,
          fats: parseFloat(String(mpFat).replace(",", ".")) || 0,
          carbs: parseFloat(String(mpCarb).replace(",", ".")) || 0,
          note: mpNote.slice(0, 200),
          confirm_existing: confirmExisting,
          force_new: forceNew,
          ad_watched: adWatched,
        }),
      });
      const d = await r.json();

      if (d.status === "found_in_base") {
        // продукт уже в базе — спросим «сохранить себе?»
        setMpFound(d.product);
        setMpBusy(false);
        return;
      }
      if (d.error === "ad_required") {
        // новый продукт — показываем reward-рекламу, затем повтор с ad_watched
        let adOk = false;
        try {
          await bridge.send("VKWebAppShowNativeAds", { ad_format: "reward" });
          adOk = true;
          logAd(vkId, "generate", { ad_type: "rewarded", shown: true, reason: "ok" });
        } catch (_) {
          logAd(vkId, "generate", { ad_type: "rewarded", shown: false, reason: "not_available" });
        }
        setMpBusy(false);
        if (adOk) {
          return saveProduct({ confirmExisting, forceNew, adWatched: true });
        } else {
          setMpError("Реклама недоступна, попробуйте позже.");
        }
        return;
      }
      if (d.error === "cooldown") {
        const wait = d.wait_seconds || 120;
        setMpCooldownUntil(Date.now() + wait * 1000);
        setMpError(d.message || `Подождите ${wait} сек`);
      } else if (d.error) {
        setMpError(d.message || "Не удалось сохранить");
      } else {
        // успех
        track('my_product_added');
        if (d.is_public === false) {
          setToast({ message: "Сохранено только в вашей базе (не прошло проверку). Рецепты с ним нельзя репостить." });
        }
        resetMpForm();
        loadMyProducts();
      }
    } catch (_) {
      setMpError("Ошибка сети, попробуйте ещё раз");
    } finally {
      setMpBusy(false);
    }
  }

  // Начать редактирование своего продукта
  function startEditProduct(p) {
    setMpEditId(p.id);
    setMpName(p.name);
    setMpCal(String(p.calories_per_100g ?? ""));
    setMpProt(String(p.proteins_g ?? ""));
    setMpFat(String(p.fats_g ?? ""));
    setMpCarb(String(p.carbs_g ?? ""));
    setMpNote(p.note || "");
    setMpError("");
    setMpMode("edit");
  }

  async function saveEditProduct() {
    if (!mpEditId) return;
    const cal = parseFloat(String(mpCal).replace(",", "."));
    if (isNaN(cal)) { setMpError("Введите калории (число)"); return; }
    setMpBusy(true); setMpError("");
    try {
      const r = await apiFetch(`${API}/my_products/edit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          vk_id: vkId, product_id: mpEditId, cal,
          proteins: parseFloat(String(mpProt).replace(",", ".")) || 0,
          fats: parseFloat(String(mpFat).replace(",", ".")) || 0,
          carbs: parseFloat(String(mpCarb).replace(",", ".")) || 0,
          note: mpNote.slice(0, 200),
        }),
      });
      const d = await r.json();
      if (d.error) { setMpError(d.message || "Не удалось изменить"); }
      else { track('my_product_edited'); resetMpForm(); loadMyProducts(); }
    } catch (_) {
      setMpError("Ошибка сети, попробуйте ещё раз");
    } finally {
      setMpBusy(false);
    }
  }

  // приготовить из продукта (Мои продукты / справочник) — подставляем в инпут и генерим
  function cookFromProduct(productName) {
    track('cook_from_my_product');
    setProducts(productName);
    setScreen("input");
  }

  // Загружает каталог продуктов из базы (один раз) — для экрана «База» и подсказок
  async function loadProductsBase() {
    if (productsBaseLoaded) return;
    try {
      const r = await apiFetch(`${API}/products_list`);
      const d = await r.json();
      if (d && Array.isArray(d.products)) {
        setProductsBase(d.products);
        setProductsBaseLoaded(true);
      }
    } catch (_) {}
  }

  async function fetchProductInfo(productName) {
    if (!productName.trim()) {
      setProductInput("");
      return;
    }
    if (productLoading) return;  // защита от двойного нажатия
    setProductLoading(true);
    // НЕ очищаем productInfo — если новый запрос упадёт, старый показывается
    setProductError("");
    try {
      const res = await apiFetch(`${API}/product_info`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ vk_id: vkId, product: productName.trim() }),
      });
      const data = await res.json();
      if (data.error === "not_product") {
        setInfo({ title: "Это не продукт 🤔", text: "Похоже, это не еда. Попробуйте написать название продукта — например: творог, гречка, яблоко." });
        return;
      }
      if (data.error === "too_fast") {
        setInfo({ title: "Слишком быстро", text: data.message || "Подождите секунду и попробуйте снова." });
        return;
      }
      if (data.error) {
        setInfo({ title: "Не получилось", text: data.message || "Что-то пошло не так, попробуйте ещё раз." });
        return;
      }
      setProductInfo(data);
      track('product_viewed', { name: productName });
    } catch (e) {
      setInfo({ title: "Нет связи", text: "Не удалось получить данные. Проверьте интернет и попробуйте снова." });
    } finally {
      setProductLoading(false);
    }
  }

  function askDeleteRecipe(item) {
    setConfirm({
      title: "Удалить рецепт?",
      text: `«${item.name}» будет удалён из вашей книги.`,
      confirmText: "Удалить",
      onConfirm: () => deleteRecipe(item.id),
    });
  }

  async function openHealthConditions() {
    track('health_open');
    scheduleInterstitial(vkId, 'health_open');
    setScreen("healthConditionsList");
    if (healthConditions.length > 0) return;
    setHealthLoading(true);
    setHealthError("");
    try {
      const res = await apiFetch(`${API}/health_conditions`);
      const data = await res.json();
      if (Array.isArray(data.conditions)) {
        setHealthConditions(data.conditions);
      }
    } catch (e) {
      setHealthError("Не удалось загрузить. Проверьте интернет.");
    } finally {
      setHealthLoading(false);
    }
  }

  async function selectHealthCondition(condition) {
    track('health_select', { key: condition.key });
    scheduleInterstitial(vkId, 'health_open');
    setHealthLoading(true);
    setHealthError("");
    setSelectedCondition(null);
    setScreen("healthConditionDetail");
    try {
      const res = await apiFetch(`${API}/health_condition`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ vk_id: vkId, condition_key: condition.key }),
      });
      const data = await res.json();
      if (data.error === "too_fast") {
        setHealthError(data.message || "Подождите секунду");
        return;
      }
      if (data.error) {
        setHealthError(data.message || "Что-то пошло не так");
        return;
      }
      setSelectedCondition(data);
    } catch (e) {
      setHealthError("Не удалось загрузить. Проверьте интернет.");
    } finally {
      setHealthLoading(false);
    }
  }

  function cookFromCondition() {
    if (!selectedCondition || !Array.isArray(selectedCondition.products)) return;
    const top = selectedCondition.products.slice(0, 3).map(p => p.name).join(", ");
    if (!top) return;
    track('health_to_recipe', { key: selectedCondition.key });
    scheduleInterstitial(vkId, 'health_to_recipe');
    setProducts(top);
    setContext(null);
    setError("");
    setScreen("input");
  }

  function getClientDate() {
    // Локальная календарная дата юзера в формате YYYY-MM-DD
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  async function loadChallengeState() {
    if (!vkId) return;
    try {
      const res = await apiFetch(`${API}/challenge/state`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ vk_id: vkId, client_date: getClientDate() }),
      });
      const data = await res.json();
      if (!data.error) setChallengeState(data);
    } catch (_) {}
  }

  async function openChallengeIntro() {
    track('challenge_view_themes');
    scheduleInterstitial(vkId, 'challenge_open');
    setChallengeError("");
    setScreen("challengeIntro");
    if (challengeThemes.length > 0) return;
    setChallengeLoading(true);
    try {
      const res = await apiFetch(`${API}/challenge/themes`);
      const data = await res.json();
      if (Array.isArray(data.themes)) setChallengeThemes(data.themes);
    } catch (_) {
      setChallengeError("Не удалось загрузить. Проверьте интернет.");
    } finally {
      setChallengeLoading(false);
    }
  }

  function openConsent() {
    setConsentChecked(false);
    setShowConsentModal(true);
  }

  async function confirmStartChallenge() {
    if (!consentChecked || !vkId) return;
    setShowConsentModal(false);
    setChallengeLoading(true);
    setChallengeError("");
    try {
      const res = await apiFetch(`${API}/challenge/start`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          vk_id: vkId,
          theme: "lightness",
          consent: true,
          client_date: getClientDate(),
        }),
      });
      const data = await res.json();
      if (data.error) {
        setChallengeError(data.message || "Не получилось начать");
        return;
      }
      // start теперь возвращает полный state с active=true
      if (data.active) {
        track('challenge_started', { theme: 'lightness' });
        setChallengeState(data);
        setScreen("challengeDay");
      } else {
        setChallengeError("Челлендж создан, но не удалось загрузить день. Попробуйте ещё раз.");
      }
    } catch (_) {
      setChallengeError("Не удалось начать. Проверьте интернет.");
    } finally {
      setChallengeLoading(false);
    }
  }

  async function openChallengeDay() {
    if (!vkId) return;
    setChallengeError("");
    setChallengeLoading(true);
    setScreen("challengeDay");
    try {
      const res = await apiFetch(`${API}/challenge/state`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ vk_id: vkId, client_date: getClientDate() }),
      });
      const data = await res.json();
      if (data.error) {
        setChallengeError(data.message || "Не удалось загрузить");
        return;
      }
      // Если активного челленджа нет — отправляем на интро
      if (!data.active) {
        setChallengeState(data);
        setScreen("challengeIntro");
        return;
      }
      setChallengeState(data);
    } catch (_) {
      setChallengeError("Не удалось загрузить. Проверьте интернет.");
    } finally {
      setChallengeLoading(false);
    }
  }

  async function checkinChallengeDay() {
    if (!vkId || !challengeState) return;
    // Interstitial реклама при отметке
    scheduleInterstitial(vkId, 'checkin');
    setChallengeLoading(true);
    try {
      const res = await apiFetch(`${API}/challenge/checkin`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ vk_id: vkId, client_date: getClientDate() }),
      });
      const data = await res.json();
      if (data.error) {
        if (data.error === "reflection_required") {
          setScreen("challengeReflection");
          return;
        }
        if (data.error === "day_locked_today") {
          setToast({ message: data.message || "Возвращайтесь завтра 💚" });
          await loadChallengeState();
          return;
        }
        setToast({ message: data.message || "Не получилось отметить" });
        return;
      }
      track('challenge_checkin', { day: challengeState.current_day });
      if (data.finished) {
        track('challenge_finished');
        setScreen("challengeFinish");
        await loadChallengeState();
      } else {
        try { bridge.send('VKWebAppTapticImpactOccurred', { style: 'medium' }); } catch (_) {}
        setToast({ message: "Молодец! +1 шаг к лёгкости 🌿" });
        await loadChallengeState();
      }
    } catch (_) {
      setToast({ message: "Не удалось отметить. Попробуйте ещё раз." });
    } finally {
      setChallengeLoading(false);
    }
  }

  async function submitWeek1Decision(decision) {
    if (!vkId) return;
    setChallengeLoading(true);
    try {
      const res = await apiFetch(`${API}/challenge/week1`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ vk_id: vkId, decision }),
      });
      const data = await res.json();
      if (data.error) {
        setToast({ message: data.message || "Не получилось" });
        return;
      }
      track('challenge_week1_' + decision);
      if (decision === "quit") {
        setChallengeState(null);
        setScreen("main");
        setToast({ message: "Челлендж завершён. Спасибо что попробовали 💚" });
      } else {
        // Теперь делаем checkin на день 7
        await checkinChallengeDay();
      }
    } catch (_) {
      setToast({ message: "Не получилось. Попробуйте ещё раз." });
    } finally {
      setChallengeLoading(false);
    }
  }

  async function deleteRecipe(recipeId) {
    if (!vkId) return;
    setConfirm(c => c ? { ...c, loading: true } : null);
    try {
      const res = await apiFetch(`${API}/saved/${recipeId}`, {
        method: "DELETE", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ vk_id: vkId }),
      });
      if (!res.ok) throw new Error();
      track('recipe_deleted');
      setSavedRecipes(prev => prev.filter(r => r.id !== recipeId));
      setConfirm(null);
      setToast({ message: "Рецепт удалён" });
      // Если юзер сейчас смотрит этот рецепт — возвращаем его в книгу
      if (selectedRecipeId === recipeId) {
        setSelectedRecipe(null);
        setSelectedRecipeId(null);
        setScreen("book");
      }
    } catch (_) { setConfirm(null); setToast({ message: "Не удалось удалить." }); }
  }

  function openRecipe(recipe, fromScreen, savedId = null) {
    setSelectedRecipe(recipe); setSelectedRecipeId(savedId);
    setPreviousScreen(fromScreen); setScreen("recipe");
    // Проверяем есть ли непроверенные продукты (для пометки в рецепте + блок репоста)
    setRecipeUnverified(false);
    if (vkId && recipe && Array.isArray(recipe.ingredients)) {
      const ings = recipe.ingredients.map(i => (typeof i === "string" ? i : (i && i.name) || "")).filter(Boolean);
      apiFetch(`${API}/recipe/can_share`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ vk_id: vkId, ingredients: ings }),
      }).then(r => r.json()).then(d => {
        if (d && d.can_share === false) setRecipeUnverified(true);
      }).catch(() => {});
    }
  }

  async function handleReplaceIngredient(original, alternative, idx) {
    if (replacingIdx !== -1 || !vkId || !selectedRecipe) return;
    setReplacingIdx(idx);
    setReplaceOverlay({ ingredient: original, alternative });  // понятный оверлей вместо троеточия
    // Минимальное время процесса — чтобы interstitial успела показаться
    const startedAt = Date.now();
    const MIN_LOADING_MS = 2500;
    // Реклама при замене
    scheduleInterstitial(vkId, 'replace_ingredient');
    try {
      const res = await apiFetch(`${API}/replace_ingredient`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          vk_id: vkId,
          original_recipe: selectedRecipe,
          original_ingredient: original,
          new_ingredient: alternative,
          persons: 2,
        }),
      });
      const data = await res.json();

      if (data.error === "too_fast") {
        setToast({ message: data.message || "Подождите секунду" });
        return;
      }
      if (data.error) {
        setToast({ message: data.message || "Не удалось пересчитать рецепт" });
        return;
      }

      const recipes = Array.isArray(data.recipes) ? data.recipes : [];
      if (recipes.length === 0) {
        setToast({ message: "Не удалось пересчитать рецепт" });
        return;
      }

      // Ждём минимальное время чтобы реклама показалась
      const elapsed = Date.now() - startedAt;
      const remaining = MIN_LOADING_MS - elapsed;
      if (remaining > 0) await new Promise(r => setTimeout(r, remaining));

      const newRecipe = recipes[0];
      setSelectedRecipe(newRecipe);
      track('ingredient_replaced', { original, alternative });
      setToast({ message: `Готово! ${original} → ${alternative}` });
      try { bridge.send('VKWebAppTapticNotificationOccurred', { type: 'success' }); } catch (_) {}
    } catch (_) {
      setToast({ message: "Не удалось пересчитать рецепт" });
    } finally {
      setReplacingIdx(-1);
      setReplaceOverlay(null);
    }
  }

  // Открыть модалку «заменить на любой продукт»
  function openReplaceAny(original) {
    loadProductsBase();  // подгрузим базу продуктов если ещё нет
    if (!myProducts.length && !myProductsLoading) loadMyProducts();  // и личные — для подсказок
    setReplaceAnyModal({ original });
    setReplaceAnyQuery("");
  }

  // Выбрать продукт из базы для замены → запускаем обычную замену
  function pickReplacementProduct(productName) {
    const m = replaceAnyModal;
    if (!m) return;
    setReplaceAnyModal(null);
    setReplaceAnyQuery("");
    // используем существующую замену (она сделает новый рецепт + картинку — Этап 4)
    handleReplaceIngredient(m.original, productName, -1);
  }

  // Открыть форму добавления нового продукта (из замены) + сразу спросить КБЖУ у Гиги
  async function openAddProduct(name) {
    setAddProductModal({ name, fromReplace: replaceAnyModal ? replaceAnyModal.original : null });
    setReplaceAnyModal(null);
    setAddProductData({ calories_per_100g: "", proteins_g: "", fats_g: "", carbs_g: "", emoji: "", category: "" });
    setAddProductBusy(true);
    // спрашиваем КБЖУ у Гиги (черновик)
    try {
      const r = await apiFetch(`${API}/product/ask_nutrition`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ vk_id: vkId, name }),
      });
      const d = await r.json();
      if (d.ok) {
        setAddProductData({
          calories_per_100g: String(d.calories_per_100g || ""),
          proteins_g: String(d.proteins_g || ""),
          fats_g: String(d.fats_g || ""),
          carbs_g: String(d.carbs_g || ""),
          emoji: d.emoji || "", category: d.category || "",
        });
      } else if (d.error === "not_food") {
        setAddProductModal(null);
        setInfo({ title: "Не продукт", text: "Это не похоже на съедобный продукт." });
      }
      // если giga_failed — оставляем поля пустыми, юзер введёт сам
    } catch (_) { /* юзер введёт вручную */ }
    setAddProductBusy(false);
  }

  // Сохранить новый продукт в базу
  async function submitAddProduct() {
    const m = addProductModal;
    if (!m || addProductBusy) return;
    const cal = parseFloat(addProductData.calories_per_100g);
    if (!cal || cal <= 0) {
      setInfo({ title: "Укажите калории", text: "Калорийность должна быть больше 0." });
      return;
    }
    setAddProductBusy(true);
    try {
      const r = await apiFetch(`${API}/product/add`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          vk_id: vkId, name: m.name,
          calories_per_100g: cal,
          proteins_g: parseFloat(addProductData.proteins_g) || 0,
          fats_g: parseFloat(addProductData.fats_g) || 0,
          carbs_g: parseFloat(addProductData.carbs_g) || 0,
          emoji: addProductData.emoji || "", category: addProductData.category || "",
        }),
      });
      const d = await r.json();
      setAddProductBusy(false);
      if (d.error) {
        setInfo({ title: "Не добавилось", text: d.message || "Попробуйте ещё раз." });
        return;
      }
      track('product_added');
      setProductsBaseLoaded(false);  // сбросим чтобы перезагрузить базу с новым продуктом
      loadProductsBase();
      const replaceOrig = m.fromReplace;
      setAddProductModal(null);
      setToast({ message: `Продукт «${m.name}» добавлен!` });
      // если добавляли из замены — сразу заменяем на новый продукт
      if (replaceOrig) {
        setTimeout(() => handleReplaceIngredient(replaceOrig, m.name, -1), 400);
      }
    } catch (_) {
      setAddProductBusy(false);
      setInfo({ title: "Нет связи", text: "Не удалось добавить." });
    }
  }

  async function handleDislike(reason) {
    if (regenerating) return;  // блокируем повторные клики во время загрузки
    setRegenerating(true);
    track('recipe_disliked', { reason });
    const prompts = {
      "долго": "приготовьте что-то быстрее, максимум 20 минут",
      "дорого": "используйте только простые и дешёвые продукты",
      "не ем это": "придумайте совершенно другое блюдо",
      "другое": "придумайте что-то совсем другое",
    };
    // handleGenerate сам покажет рекламу (interstitial или rewarded если interstitial в кулдауне)
    handleGenerate(prompts[reason] || "", true).finally(() => {
      setRegenerating(false);
    });
  }

  // Загрузка фото блюда на ВК CDN (ВК хранит и модерирует), сохранение URL к рецепту.
  // Полный цикл ВК: getWallUploadServer → POST файла → saveWallPhoto → URL картинки.
  async function uploadRecipePhoto(recipeId, file) {
    if (!file || !recipeId) return;
    if (file.size > 15 * 1024 * 1024) {
      setInfo({ title: "Файл большой", text: "Фото до 15 МБ. Выберите снимок поменьше." });
      return;
    }
    setPhotoUploading(true);
    try {
      // 1. Токен со scope photos
      const auth = await bridge.send('VKWebAppGetAuthToken', { app_id: 54552158, scope: 'photos' });
      const token = auth && auth.access_token;
      if (!token) throw new Error('no_token');

      // 2. upload server (getWallUploadServer не требует album_id)
      const srv = await bridge.send('VKWebAppCallAPIMethod', {
        method: 'photos.getWallUploadServer',
        params: { access_token: token, v: '5.131' },
      });
      const uploadUrl = srv && srv.response && srv.response.upload_url;
      if (!uploadUrl) throw new Error('no_upload_url');

      // 3. Загрузка файла через НАШ сервер (обход CORS — браузер не может на pu.vk.ru
      //    напрямую). Сервер вернёт server/photo/hash.
      const fd = new FormData();
      fd.append('vk_id', vkId);
      fd.append('upload_url', uploadUrl);
      fd.append('file', file, file.name || 'photo.jpg');
      const r = await fetch(`${API}/saved/${recipeId}/photo_upload`, {
        method: "POST",
        headers: { "X-VK-Launch-Params": VK_LAUNCH_PARAMS },
        body: fd,
      });
      const d = await r.json();
      if (d.error) { setInfo({ title: "Не сохранилось", text: d.message || "Попробуйте ещё раз." }); setPhotoUploading(false); return; }
      const upParts = { server: d.server, photo: d.photo, hash: d.hash };

      // 4. saveWallPhoto С КЛИЕНТА через Bridge (токен ВК привязан к IP клиента,
      //    с сервера было бы "access_token given to another ip")
      const saved = await bridge.send('VKWebAppCallAPIMethod', {
        method: 'photos.saveWallPhoto',
        params: { server: upParts.server, photo: upParts.photo, hash: upParts.hash, access_token: token, v: '5.131' },
      });
      const photoObj = saved && saved.response && saved.response[0];
      if (!photoObj) throw new Error('save_failed');

      let picUrl = '';
      if (Array.isArray(photoObj.sizes) && photoObj.sizes.length) {
        const best = photoObj.sizes.reduce((a, b) => (b.width || 0) > (a.width || 0) ? b : a);
        picUrl = best.url || '';
      }
      if (!picUrl) throw new Error('no_pic_url');

      // 5. Сохраняем итоговый URL у нас
      const r2 = await fetch(`${API}/saved/${recipeId}/photo_done`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-VK-Launch-Params": VK_LAUNCH_PARAMS },
        body: JSON.stringify({ vk_id: vkId, photo_url: picUrl }),
      });
      const d2 = await r2.json();
      if (d2.error) { setInfo({ title: "Не сохранилось", text: d2.message || "Попробуйте ещё раз." }); setPhotoUploading(false); return; }

      track('recipe_photo_added');
      setEditRecipe(prev => prev ? { ...prev, photo_url: picUrl } : prev);
      setSavedRecipes(prev => prev.map(it => it.id === recipeId ? { ...it, photo_url: picUrl } : it));
      setToast({ message: "Фото добавлено!" });
    } catch (e) {
      const reason = e && e.error_data && (e.error_data.error_reason || e.error_data.error_msg);
      if (reason === 'User denied' || (e && e.error_type === 'client_error')) {
        setPhotoUploading(false);
        return;
      }
      setInfo({ title: "Не удалось загрузить", text: "Фото не загрузилось. Попробуйте ещё раз или другой снимок." });
    } finally {
      setPhotoUploading(false);
    }
  }

  // Удалить фото рецепта
  async function removeRecipePhoto(recipeId) {
    try {
      await apiFetch(`${API}/saved/${recipeId}/photo`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ vk_id: vkId, photo_url: "" }),
      });
      setEditRecipe(prev => prev ? { ...prev, photo_url: "" } : prev);
      setSavedRecipes(prev => prev.map(it => it.id === recipeId ? { ...it, photo_url: "" } : it));
      setToast({ message: "Фото удалено" });
    } catch (_) {}
  }

  async function handleShare(recipe) {
    track('share_clicked', { name: recipe.name });
    const appUrl = 'https://vk.com/app54552158';

    // Модерация шаринга (Задача 5): репост только если все ингредиенты в базе (is_public).
    // Треш («глаз единорога») → блок, чтобы дичь не улетала в публичную ленту ВК.
    try {
      const ings = Array.isArray(recipe.ingredients)
        ? recipe.ingredients.map(i => (typeof i === "string" ? i : (i && i.name) || "")).filter(Boolean)
        : [];
      const r = await apiFetch(`${API}/recipe/can_share`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ingredients: ings }),
      });
      const d = await r.json();
      if (d && d.can_share === false) {
        setToast({
          message: "Репост недоступен: в рецепте есть продукт, не прошедший проверку. Рецепт доступен только вам.",
        });
        return;
      }
    } catch (_) {
      // при ошибке проверки — не блокируем (безопасный дефолт для UX)
    }

    const text = `🍴 ${recipe.name}\n\nГотовлю в приложении «Из холодильника»\n${appUrl}`;

    // Пост на стену. attachments НЕ передаём — VK часто падает с error_code:1
    // когда в attachments ссылка на само приложение. Ссылку кладём в текст —
    // VK сам распознает её и сделает превью-сниппет.
    try {
      await bridge.send('VKWebAppShowWallPostBox', { message: text });
      track('share_success', { method: 'wall' });
      return;
    } catch (e) {
      // Если стену юзер закрыл — это не ошибка
      if (e && e.error_data && e.error_data.error_reason === 'User denied') return;
      // ДИАГНОСТИКА: шлём реальную ошибку VK на сервер (чтобы увидеть почему падает)
      try {
        await apiFetch(`${API}/client_log`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            event: "wallpost_failed",
            vk_id: vkId,
            detail: JSON.stringify(e && e.error_data ? e.error_data : e),
          }),
        });
      } catch (_) {}
    }
    // Fallback — стандартный share
    try {
      await bridge.send('VKWebAppShare', { link: appUrl });
      return;
    } catch (_) {}
    // Последний фоллбек — копируем ссылку
    try {
      await navigator.clipboard.writeText(`${recipe.name} — ${appUrl}`);
      setToast({ message: "Ссылка скопирована — вставьте в чат" });
    } catch (_) {
      setToast({ message: "Не удалось поделиться" });
    }
  }

  function handleContextSelect(key) {
    if (context === key) { setContext(null); track('context_deselected', { key }); }
    else { setContext(key); track('context_selected', { key }); }
  }

  const difficultyStyle = (d) => {
    if (!d) return { bg: "#f0f0f0", color: "#999" };
    const dl = d.toLowerCase();
    if (dl.includes("прост") || dl.includes("легк")) return { bg: "#c8e6c9", color: "#2e7d32" };
    if (dl.includes("средн")) return { bg: "#faeeda", color: "#ba7515" };
    return { bg: "#faece7", color: "#c62828" };
  };

  const getCounterColor = (count) => {
    if (count >= FREE_SAVED_LIMIT) return "#c62828";
    if (count >= FREE_SAVED_LIMIT - 2) return "#ba7515";
    return "rgba(255,255,255,0.82)";
  };

  const onboardingSlides = [
    { emoji: "🥕", title: "Не знаете, что приготовить?", text: "Скажите, что есть в холодильнике — подберу ужин из ваших продуктов" },
    { emoji: "⚡", title: "3 рецепта за 10 секунд", text: "Не понравилось — одной кнопкой подберу другие варианты" },
    { emoji: "❤️", title: "Ваша кулинарная книга", text: "Сохраняйте рецепты и делитесь находками с близкими" },
  ];

  if (screen === "onboarding") {
    const slide = onboardingSlides[onboardingStep];
    const isLast = onboardingStep === onboardingSlides.length - 1;
    return (
      <div style={styles.page}>
        <div style={styles.header}>
          {/* Стрелка ← всегда видна со 2-го слайда — кликабельна, с явным аффордансом */}
          {onboardingStep > 0 ? (
            <button
              style={{
                ...styles.headerBack,
                cursor: "pointer",
                opacity: 1,
              }}
              onClick={() => setOnboardingStep(onboardingStep - 1)}
              aria-label="Назад"
            >‹</button>
          ) : (
            <div style={{ width: 28, flexShrink: 0 }} />
          )}
          <div style={{ flex: 1 }}><div style={styles.headerTitle}>Из холодильника</div></div>
          <span style={styles.slideCounter}>{onboardingStep + 1} / {onboardingSlides.length}</span>
        </div>
        {/* Фиксированная высота карточки — чтобы не прыгало при переключении слайдов */}
        <div style={{
          ...styles.card,
          textAlign: "center",
          padding: "40px 22px",
          minHeight: 260,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}>
          <div style={{ fontSize: 80, marginBottom: 18, lineHeight: 1 }}>{slide.emoji}</div>
          <div style={styles.slideTitle}>{slide.title}</div>
          <div style={styles.slideText}>{slide.text}</div>
        </div>
        <div style={styles.dots}>
          {onboardingSlides.map((_, i) => (
            <button
              key={i}
              onClick={() => setOnboardingStep(i)}
              aria-label={`Слайд ${i + 1}`}
              style={{
                width: i === onboardingStep ? 22 : 8,
                height: 8,
                borderRadius: i === onboardingStep ? 4 : "50%",
                background: i === onboardingStep ? "#0f6e56" : "rgba(15,110,86,0.25)",
                border: "none",
                cursor: "pointer",
                padding: 0,
                transition: "all 0.2s",
              }}
            />
          ))}
        </div>
        <button style={styles.primaryButton}
          onClick={() => isLast ? finishOnboarding() : setOnboardingStep(onboardingStep + 1)}>
          {isLast ? "Начать готовить!" : "Дальше →"}
        </button>
      </div>
    );
  }

  if (screen === "main") {
    const cs = challengeState;
    const challengeActive = cs && cs.active;
    const challengeProgress = challengeActive && cs.total_days
      ? (cs.completed_days / cs.total_days) * 100 : 0;

    return (
      <div style={styles.page}>
        {/* Шапка: лёгкое приветствие (макет В) */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                      margin: "2px 4px 14px" }}>
          <div>
            <div style={{ fontSize: 15, color: "#7a8a7a" }}>
              {userName ? `Привет, ${userName} 👋` : "Привет! 👋"}
            </div>
            <div style={{ fontSize: 27, fontWeight: 800, color: "#1c241b", marginTop: 3, letterSpacing: "-0.5px" }}>
              Из холодильника
            </div>
          </div>
          <div className="povar-icon" style={{ width: 54, height: 54, borderRadius: "50%", background: "linear-gradient(145deg,#fbd28a,#f3b95c)" }}><UiIcon name="chef_avatar" size={47} /></div>
        </div>

        {/* Dev-режим рекламы — кнопка видна ТОЛЬКО владельцу */}
        {vkId === DEV_VK_ID && (
          <button
            onClick={() => setDevMode((d) => !d)}
            style={{
              width: '100%',
              background: devMode ? '#3e2a12' : '#f0e6d8',
              color: devMode ? '#ffd9a8' : '#0f6e56',
              border: '1px dashed #b08050',
              borderRadius: 12,
              padding: '8px 12px',
              cursor: 'pointer',
              fontSize: 15,
              fontWeight: 700,
              marginBottom: 12,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}>
            <span>🛠</span>
            <span>Реклама: {devMode ? 'скрыть подсветку' : 'показать где какая'}</span>
          </button>
        )}

        {/* Dev-тест микрофона в VK webview — виден ТОЛЬКО владельцу */}
        {vkId === DEV_VK_ID && (
          <button
            onClick={async () => {
              const report = [];
              try {
                report.push(`UA: ${navigator.userAgent.slice(0, 90)}`);
                if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                  setInfo({ title: "🎤 Тест: ПРОВАЛ", text: "getUserMedia недоступен в этом webview.\n" + report.join("\n") });
                  return;
                }
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                report.push("Доступ к микрофону: ЕСТЬ ✅");
                if (typeof MediaRecorder === "undefined") {
                  stream.getTracks().forEach(t => t.stop());
                  setInfo({ title: "🎤 Тест: наполовину", text: "Микрофон дали, но MediaRecorder отсутствует.\n" + report.join("\n") });
                  return;
                }
                const mime = ["audio/mp4", "audio/webm", "audio/ogg"].find(m => MediaRecorder.isTypeSupported(m)) || "";
                report.push(`Формат записи: ${mime || "дефолтный"}`);
                const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
                const chunks = [];
                rec.ondataavailable = ev => { if (ev.data && ev.data.size) chunks.push(ev.data); };
                rec.onstop = () => {
                  stream.getTracks().forEach(t => t.stop());
                  const blob = new Blob(chunks, { type: mime || "audio/webm" });
                  report.push(`Записано: ${(blob.size / 1024).toFixed(1)} КБ за 3 сек`);
                  const url = URL.createObjectURL(blob);
                  const audio = new Audio(url);
                  audio.play().catch(() => report.push("(автоплей запрещён — но запись есть)"));
                  setInfo({ title: blob.size > 2000 ? "🎤 Тест: РАБОТАЕТ ✅" : "🎤 Тест: запись пустая ⚠️", text: report.join("\n") });
                };
                rec.start();
                setInfo({ title: "🎤 Говорите!", text: "Пишу 3 секунды…" });
                setTimeout(() => { try { rec.stop(); } catch (e) {} }, 3000);
              } catch (e) {
                setInfo({ title: "🎤 Тест: ПРОВАЛ", text: `${e.name}: ${e.message}\n` + report.join("\n") });
              }
            }}
            style={{
              width: '100%', background: '#f0e6d8', color: '#0f6e56',
              border: '1px dashed #b08050', borderRadius: 12, padding: '8px 12px',
              cursor: 'pointer', fontSize: 15, fontWeight: 700, marginBottom: 12,
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
            <span>🎤</span>
            <span>Тест микрофона (3 сек записи + проигрыш)</span>
          </button>
        )}

        {/* Карточка 1: Совет дня (с приветствием) */}
        <DailyAdviceCard advice={advice} userName={userName} onPromptBirthday={() => setShowBdayPrompt(true)} />

        {/* Плашка: включить пуш «совет дня в 10:00» (фича Б3) */}
        {notifyState === 'enabled' ? (
          <div style={{
            display: "flex", alignItems: "center", gap: 8,
            fontSize: 14, color: "#7a8a7a", margin: "0 2px 12px",
          }}>
            <span>🌙</span>
            <span>Совет дня будет приходить в 10:00 — включено</span>
          </div>
        ) : (
          <button
            className="povar-tap"
            onClick={async () => {
              try {
                const res = await bridge.send('VKWebAppAllowNotifications');
                if (res && res.result) {
                  setNotifyState('enabled');
                  setNotifyPermission(vkId, true);
                  try { bridge.send('VKWebAppTapticNotificationOccurred', { type: 'success' }); } catch (_) {}
                }
              } catch (_) {
                // юзер отклонил или попап недоступен — оставляем плашку, ничего не ломаем
              }
            }}
            style={{
              width: "100%",
              background: "#fff",
              border: "none",
              borderRadius: 26,
              padding: "14px 16px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 12,
              marginBottom: 12,
              textAlign: "left",
              boxShadow: "0 1px 2px rgba(28,36,27,0.05), 0 4px 12px rgba(28,36,27,0.07)",
            }}>
            <span style={{ fontSize: 28, flexShrink: 0 }}>🌙</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: "#0f6e56", lineHeight: 1.2 }}>
                Включить совет дня
              </div>
              <div style={{ fontSize: 14, color: "#a1a39f", marginTop: 2, fontWeight: 500 }}>
                будем напоминать в 10 утра — что приготовить сегодня
              </div>
            </div>
            <span style={{ color: "#c9cdc4", fontSize: 22 }}>›</span>
          </button>
        )}

        {/* Карточка 2 (главная): Что приготовить — крупная */}
        <button
          onClick={() => setScreen("input")}
          className="povar-tap povar-grain"
          style={{
            width: "100%",
            background: "linear-gradient(140deg, #e97c4e 0%, #da6338 55%, #d05a2e 100%)",
            border: "none",
            borderRadius: 26,
            padding: "17px 18px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 13,
            marginBottom: 14,
            boxShadow: "0 2px 6px rgba(218,99,56,0.25), 0 12px 32px rgba(218,99,56,0.38), inset 0 1px 1px rgba(255,255,255,0.4)",
          }}>
          <img src={FRIDGE_IMG} alt="" style={{ width: 64, height: 64, objectFit: "contain", flexShrink: 0 }} />
          <div style={{ flex: 1, textAlign: "left" }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#fff", lineHeight: 1.2, letterSpacing: "-0.3px" }}>
              Что приготовить?
            </div>
            <div style={{ fontSize: 14, color: "#ffdcc9", marginTop: 3, fontWeight: 500 }}>
              скажи, что есть — придумаю за 10 секунд
            </div>
          </div>
          <span style={{ width: 44, height: 44, borderRadius: "50%", flexShrink: 0,
                         background: "linear-gradient(145deg,#f59a6b,#e0692f)",
                         display: "flex", alignItems: "center", justifyContent: "center",
                         fontSize: 22, color: "#fff", paddingBottom: 2,
                         boxShadow: "0 3px 8px rgba(160,60,20,0.35), inset 0 1px 1px rgba(255,255,255,0.45)" }}>›</span>
        </button>

        {/* Карточка 3 (главная): Челлендж */}
        <button
          onClick={() => {
            if (challengeActive) openChallengeDay();
            else openChallengeIntro();
          }}
          style={{
            width: "100%",
            background: "#fff",
            border: "none",
            borderRadius: 26,
            padding: "13px 15px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginBottom: 14,
            textAlign: "left",
            position: "relative",
          }}>
          <AdDevBadge point="challenge_open" devOn={devMode && vkId === DEV_VK_ID} onToggle={toggleAdPoint} />
          <div className="povar-icon" style={{ width: 54, height: 54, background: "linear-gradient(145deg,#cbe4a3,#b3d382)" }}><UiIcon name="sprout" size={47} /></div>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: "#1c2a1c", lineHeight: 1.2 }}>
                {challengeActive ? "Челлендж" : "30 дней лёгкости"}
              </div>
              <span style={{ fontSize: 13, color: "#4a7d3a", flexShrink: 0, marginLeft: 8, fontWeight: 600,
                             background: "linear-gradient(145deg,#eaf5dc,#ddedc8)", borderRadius: 11, padding: "5px 11px",
                             boxShadow: "inset 0 1px 1px rgba(255,255,255,0.7)" }}>
                {challengeActive
                  ? `день ${cs.current_day} из ${cs.total_days}`
                  : cs && cs.has_badge ? "есть значок 🌿" : "программа"}
              </span>
            </div>
            {challengeActive ? (
              <div style={{
                background: "#edf1e8", borderRadius: 5, height: 8,
                marginTop: 9, overflow: "hidden", boxShadow: "inset 0 1px 2px rgba(28,36,27,0.07)",
              }}>
                <div style={{
                  background: "linear-gradient(180deg,#aede6a 0%,#8bc34a 100%)", height: "100%", borderRadius: 5,
                  width: `${challengeProgress}%`, transition: "width 0.3s",
                  boxShadow: "0 1px 3px rgba(139,195,74,0.4), inset 0 1px 1px rgba(255,255,255,0.5)",
                }} />
              </div>
            ) : (
              <div style={{ fontSize: 13, color: "#7a8a7a", marginTop: 2 }}>лёгкая программа здоровья</div>
            )}
          </div>
        </button>

        {/* Сетка 2x2 — парами по смыслу: рецепты сверху, продукты в середине, здоровье внизу */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 10 }}>
          {/* Ряд 1: рецепты — личные + общая база */}
          <button
            onClick={openBook} className="povar-tap"
            style={{
              background: "#fff", border: "none", borderRadius: 26,
              padding: "14px 13px", cursor: "pointer",
              display: "flex", alignItems: "center", gap: 11,
              position: "relative", textAlign: "left",
              boxShadow: "0 1px 2px rgba(28,36,27,0.05), 0 5px 14px rgba(28,36,27,0.06), 0 14px 36px rgba(28,36,27,0.07)",
            }}>
            <AdDevBadge point="book_open" devOn={devMode && vkId === DEV_VK_ID} onToggle={toggleAdPoint} />
            <div className="povar-icon" style={{ width: 54, height: 54, background: "linear-gradient(145deg,#f3a17e,#e07a52)" }}><UiIcon name="book_recipes" size={47} /></div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#1c241b", lineHeight: 1.2, letterSpacing: "-0.2px" }}>Мои рецепты</div>
              <div style={{ fontSize: 13, color: "#a1a39f", marginTop: 2, fontWeight: 500 }}>сохранённые</div>
            </div>
            <span style={{ color: "#c9cdc4", fontSize: 17 }}>›</span>
          </button>

          <button
            onClick={openRecipesBase} className="povar-tap"
            style={{
              background: "#fff", border: "none", borderRadius: 26,
              padding: "14px 13px", cursor: "pointer",
              display: "flex", alignItems: "center", gap: 11,
              position: "relative", textAlign: "left",
              boxShadow: "0 1px 2px rgba(28,36,27,0.05), 0 5px 14px rgba(28,36,27,0.06), 0 14px 36px rgba(28,36,27,0.07)",
            }}>
            <AdDevBadge point="recipes_base_open" devOn={devMode && vkId === DEV_VK_ID} onToggle={toggleAdPoint} />
            <div className="povar-icon" style={{ width: 54, height: 54, background: "linear-gradient(145deg,#b5e3dd,#93cfc7)" }}><UiIcon name="pot_community" size={47} /></div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#1c241b", lineHeight: 1.2, letterSpacing: "-0.2px" }}>База рецептов</div>
              <div style={{ fontSize: 13, color: "#a1a39f", marginTop: 2, fontWeight: 500 }}>от сообщества</div>
            </div>
            <span style={{ color: "#c9cdc4", fontSize: 17 }}>›</span>
          </button>

          {/* Ряд 2: продукты — личные + общий справочник */}
          <button
            onClick={openMyProducts} className="povar-tap"
            style={{
              background: "#fff", border: "none", borderRadius: 26,
              padding: "14px 13px", cursor: "pointer",
              display: "flex", alignItems: "center", gap: 11,
              position: "relative", textAlign: "left",
              boxShadow: "0 1px 2px rgba(28,36,27,0.05), 0 5px 14px rgba(28,36,27,0.06), 0 14px 36px rgba(28,36,27,0.07)",
            }}>
            <div className="povar-icon" style={{ width: 54, height: 54, background: "linear-gradient(145deg,#a6c8ec,#8eb5e0)" }}><UiIcon name="salad_plate" size={47} /></div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#1c241b", lineHeight: 1.2, letterSpacing: "-0.2px" }}>Мои продукты</div>
              <div style={{ fontSize: 13, color: "#a1a39f", marginTop: 2, fontWeight: 500 }}>моя база КБЖУ</div>
            </div>
            <span style={{ color: "#c9cdc4", fontSize: 17 }}>›</span>
          </button>

          <button
            onClick={openProductRef} className="povar-tap"
            style={{
              background: "#fff", border: "none", borderRadius: 26,
              padding: "14px 13px", cursor: "pointer",
              display: "flex", alignItems: "center", gap: 11,
              position: "relative", textAlign: "left",
              boxShadow: "0 1px 2px rgba(28,36,27,0.05), 0 5px 14px rgba(28,36,27,0.06), 0 14px 36px rgba(28,36,27,0.07)",
            }}>
            <AdDevBadge point="product_ref_open" devOn={devMode && vkId === DEV_VK_ID} onToggle={toggleAdPoint} />
            <div className="povar-icon" style={{ width: 54, height: 54, background: "linear-gradient(145deg,#d6e8ae,#bfd88c)" }}><UiIcon name="books_catalog" size={47} /></div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#1c241b", lineHeight: 1.2, letterSpacing: "-0.2px" }}>Справочник</div>
              <div style={{ fontSize: 13, color: "#a1a39f", marginTop: 2, fontWeight: 500 }}>468 продуктов</div>
            </div>
            <span style={{ color: "#c9cdc4", fontSize: 17 }}>›</span>
          </button>

          {/* Низ: здоровье — широкая плитка */}
          <button
            onClick={openHealthConditions} className="povar-tap"
            style={{
              background: "#fff", border: "none", borderRadius: 26,
              padding: "13px 15px", cursor: "pointer",
              display: "flex", flexDirection: "row", alignItems: "center",
              gap: 12, gridColumn: "1 / -1", textAlign: "left",
              boxShadow: "0 1px 2px rgba(28,36,27,0.05), 0 5px 14px rgba(28,36,27,0.06), 0 14px 36px rgba(28,36,27,0.07)",
            }}>
            <div className="povar-icon" style={{ width: 54, height: 54, background: "linear-gradient(145deg,#fbd28a,#f3b95c)" }}><UiIcon name="heart_green" size={47} /></div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 16, fontWeight: 600, color: "#1c2a1c", lineHeight: 1.2 }}>
                Что полезно при...
              </div>
              <div style={{ fontSize: 13, color: "#7a8a7a", marginTop: 2 }}>13 тем по здоровью</div>
            </div>
            <span style={{ color: "#c9c9b0", fontSize: 20 }}>›</span>
          </button>
        </div>

        <Toast {...(toast || {})} onClose={() => setToast(null)} />
        <InfoModal {...(info || {})} onClose={info?.onClose || (() => setInfo(null))} />
        {showBdayPrompt && <BirthdayPrompt onSubmit={submitBirthday} onSkip={skipBirthday} />}
      </div>
    );
  }

  if (screen === "input") {
    return (
      <div style={styles.page}>
        <div style={styles.header}>
          <button style={styles.headerBack} onClick={() => setScreen("main")}>‹</button>
          <div style={{ flex: 1 }}>
            <div style={styles.headerTitle}>Что в холодильнике?</div>
            <div style={styles.headerSubtitle}>Укажите ингредиенты — я соберу рецепт</div>
          </div>
        </div>
        <div style={styles.contextLabel}>Для кого готовим? <span style={styles.contextOptional}>(необязательно)</span></div>
        <div style={styles.contextChips}>
          {CONTEXTS.map(c => {
            const CCOL = {
              family:   { bg: "linear-gradient(145deg,#fde8d8,#fbd9c0)", fg: "#8a4a1e" },
              kids:     { bg: "linear-gradient(145deg,#fdf3d5,#fae7b4)", fg: "#8a6a12" },
              guests:   { bg: "linear-gradient(145deg,#efe6fb,#e0d1f6)", fg: "#5b3a8a" },
              lunchbox: { bg: "linear-gradient(145deg,#ddeefb,#c8e0f5)", fg: "#1e5a8a" },
            }[c.key] || { bg: "#fff", fg: "#1c241b" };
            const on = context === c.key;
            return (
            <button key={c.key}
              style={{ ...styles.contextChip, background: CCOL.bg, color: CCOL.fg, border: "none",
                       boxShadow: on
                         ? "0 0 0 2.5px #159872, inset 0 1px 1px rgba(255,255,255,0.7), 0 2px 6px rgba(28,36,27,0.08)"
                         : "inset 0 1px 1px rgba(255,255,255,0.7), 0 2px 6px rgba(28,36,27,0.06)",
                       fontWeight: 600 }}
              onClick={() => handleContextSelect(c.key)}>
              {c.icon
                ? <UiIcon name={c.icon} size={72} style={{ marginRight: 12, marginTop: -4, marginBottom: -4 }} />
                : <span style={{ fontSize: 20, marginRight: 6 }}>{c.emoji}</span>}{c.label}
            </button>
          );
          })}
        </div>
        <div style={styles.inputCard}>
          <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 10 }}>
            <UiIcon name="pencil" size={24} />
            <span style={{ fontSize: 16, fontWeight: 700, color: "#1c241b" }}>Напиши продукты или выбери ниже</span>
          </div>
          <div style={{ background: "linear-gradient(140deg,#f5c9a8,#edd9a4,#c9dfae)", borderRadius: 20, padding: 2.5 }}>
          <div style={{ background: "#f7faf3", borderRadius: 18, padding: "13px 14px",
                        boxShadow: "inset 0 2px 5px rgba(28,36,27,0.07)", position: "relative" }}>
          <textarea style={styles.textarea} placeholder="Например: курица, картошка, лук..."
            value={products} onChange={e => setProducts(e.target.value.slice(0, 300))} />
          {products.length > 0 && <button style={styles.clearBtn} onClick={() => setProducts("")}>✕</button>}
          <div style={styles.charCount}>{products.length}/300</div>
          </div>
          </div>
        </div>
        {error && <div style={styles.error}>{error}</div>}
        <div style={styles.sectionLabel}>Быстрый выбор</div>
        <div style={styles.quickButtons}>
          {["курица", "картошка", "лук", "яйца", "морковь", "рис", "макароны", "кабачок"].map((p, qi) => {
            const hit = findProductByName(productsBase, p);
            const QPAL = ["linear-gradient(145deg,#fde8d8,#fbd9c0)", "linear-gradient(145deg,#fdf3d5,#fae7b4)",
                          "linear-gradient(145deg,#e2f2d8,#cfe6bc)", "linear-gradient(145deg,#ddeefb,#c8e0f5)"];
            return (
              <button key={p} style={{ ...styles.quickBtn, display: "inline-flex", alignItems: "center", gap: 9, padding: "7px 15px 7px 7px" }}
                className="povar-chip"
                onClick={() => setProducts(prev => prev ? prev + ", " + p : p)}>
                <span style={{ width: 38, height: 38, borderRadius: 13, background: QPAL[qi % 4],
                               display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                               boxShadow: "inset 0 1px 1px rgba(255,255,255,0.6)", overflow: "hidden" }}>
                  {hit && hit.image_url
                    ? <img src={hit.image_url} alt="" width={34} height={34}
                        style={{ borderRadius: 11, objectFit: "cover", display: "block",
                                 WebkitMaskImage: "radial-gradient(circle at 50% 50%, #000 46%, transparent 71%)",
                                 maskImage: "radial-gradient(circle at 50% 50%, #000 46%, transparent 71%)" }} />
                    : <span style={{ fontSize: 20 }}>🥗</span>}
                </span>{p}
              </button>
            );
          })}
        </div>
        <div style={styles.futureButtons}>
          <button style={{ ...styles.futureBtn, cursor: "pointer", opacity: 1,
              ...(sttState && sttState.key === "products" && sttState.phase === "rec"
                ? { background: "linear-gradient(145deg,#d94b3f,#bd2a2a)", color: "#fff",
                    boxShadow: `0 0 0 ${2 + Math.round(sttLevel * 10)}px rgba(217,75,63,${0.15 + sttLevel * 0.25})`,
                    transition: "box-shadow 0.09s linear" }
                : {}) }}
            onClick={() => { track('stt_products'); sttToggle("products"); }}
            disabled={sttState !== null && sttState.key !== "products"}>
            {sttState && sttState.key === "products" && sttState.phase === "rec" ? (
              <>
                <span style={{ display: "inline-flex", gap: 2, alignItems: "flex-end", height: 24, marginRight: 4 }}>
                  {[0, 1, 2, 3, 4].map(i => (
                    <span key={i} style={{ width: 3, borderRadius: 2, background: "#fff",
                      height: 5 + sttLevel * (12 + ((i * 7) % 11)), transition: "height 0.09s linear" }} />
                  ))}
                </span>
                Говорите… (тап = стоп)
              </>
            ) : sttState && sttState.key === "products" ? (
              <>… Распознаю</>
            ) : (
              <>
                <span className="povar-icon" style={{ width: 38, height: 38, background: "linear-gradient(145deg,#fbd9c8,#f3b096)" }}><UiIcon name="microphone" size={26} /></span>Голосовой ввод
              </>
            )}
          </button>
        </div>
        <button
          style={{
            ...styles.primaryButton,
            opacity: products.trim() ? 1 : 0.5,
            cursor: products.trim() ? "pointer" : "not-allowed",
          }}
          onClick={() => handleGenerate("", false)}
          disabled={!products.trim()}>
          🍳 Подобрать рецепт
        </button>
        <div style={styles.privacyNote}>🔒 Мы не сохраняем ваши данные</div>
        {voicePopup && (
          <div style={styles.modalOverlay} className="povar-fade" onClick={() => { track('voice_popup_close'); apiFetch(`${API}/track_interest`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ feature: "voice_input", action: "close", vk_id: vkId }) }).catch(() => {}); setVoicePopup(false); }}>
            <div style={styles.modalCard} className="povar-pop" onClick={ev => ev.stopPropagation()}>
              <div style={{ fontSize: 48, textAlign: "center", marginBottom: 8 }}>🎤</div>
              <div style={styles.modalTitle}>Голосовой ввод скоро!</div>
              {!voiceVoted ? (
                <>
                  <div style={styles.modalText}>
                    Скоро можно будет просто сказать, что есть в холодильнике — и не печатать руками. Удобно, когда руки заняты готовкой!
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 14 }}>
                    <button style={styles.modalBtnInfo} className="povar-press"
                      onClick={() => {
                        track('voice_want_faster');
                        apiFetch(`${API}/track_interest`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ feature: "voice_input", action: "want", vk_id: vkId }) }).catch(() => {});
                        setVoiceVoted(true);
                      }}>
                      🔥 Хочу побыстрее!
                    </button>
                    <button style={styles.modalBtnCancel} className="povar-press"
                      onClick={() => { track('voice_popup_close'); apiFetch(`${API}/track_interest`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ feature: "voice_input", action: "close", vk_id: vkId }) }).catch(() => {}); setVoicePopup(false); }}>
                      Закрыть
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ ...styles.modalText, textAlign: "center" }}>
                    Спасибо! 🙌 Записали ваш голос за эту функцию. Чем больше желающих — тем быстрее сделаем!
                  </div>
                  <button style={{ ...styles.modalBtnInfo, marginTop: 14 }} className="povar-press"
                    onClick={() => setVoicePopup(false)}>
                    Отлично!
                  </button>
                </>
              )}
            </div>
          </div>
        )}
        <Toast {...(toast || {})} onClose={() => setToast(null)} />
      </div>
    );
  }

  if (screen === "loading") return <LoadingScreen isFresh={loadingIsFresh} />;

  if (screen === "results") {
    return (
      <div style={styles.page}>
        <div style={styles.header}>
          <button style={styles.headerBack} onClick={() => { setError(""); setScreen("input"); }}>‹</button>
          <div style={{ flex: 1 }}>
            <div style={styles.headerTitle}>{userName ? `${userName}, вот варианты!` : "Вот что можно приготовить!"}</div>
            <div style={styles.headerSubtitle}>Выберите рецепт, который хочется попробовать</div>
          </div>
        </div>

        {/* Плашка источника: из готовой базы (бесплатно) или свежая генерация Гигой */}
        {recipeSource && (
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}>
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "6px 14px", borderRadius: 999, fontSize: 14, fontWeight: 600,
              background: recipeSource === "base" ? "#e7f5ec" : "#eef8f2",
              color: recipeSource === "base" ? "#1f7a44" : "#9a5a00",
              border: recipeSource === "base" ? "1px solid #b8e3c6" : "1px solid #f0c98a",
            }}>
              {recipeSource === "base"
                ? "⚡ Из базы · мгновенно и бесплатно"
                : (limitEnabled && !isPremium && recipesLeft !== null
                    ? `✨ Свежий рецепт · осталось ${recipesLeft} сегодня`
                    : "✨ Свежий рецепт от ИИ")}
            </div>
          </div>
        )}

        {/* Плашка для необычных запросов — броская, с юмором */}
        {unusualInfo.is && (
          <div style={{
            background: "linear-gradient(135deg, #eef8f2 0%, #d9ecd9 100%)",
            border: "2px solid #f0b264",
            borderRadius: 18,
            padding: "14px 16px",
            marginBottom: 14,
            boxShadow: "0 2px 8px rgba(240, 178, 100, 0.25)",
          }}>
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 6 }}>
              <span style={{ fontSize: 28 }}>🍴</span>
              <span style={{ fontSize: 16, fontWeight: 700, color: "#7a3f00" }}>
                Ну вы и шутник!
              </span>
            </div>
            <div style={{ fontSize: 15, color: "#6b3d10", lineHeight: 1.45 }}>
              Ладно, ради интереса — вот что у меня вышло.
              {unusualInfo.reason ? " " + unusualInfo.reason.replace(/[.!?]\s*$/, '') + "." : ""}
              {" "}Это просто игра воображения 😄
            </div>
          </div>
        )}

        {(recipes || []).map((recipe, i) => {
          const dc = difficultyStyle(recipe.difficulty);
          return (
            <div key={i} style={{ ...styles.recipeCard, animationDelay: `${i * 0.06}s` }} className="povar-fade">
              {(recipe.image_url || dishImages[recipe.name])
                ? <img src={`${API}${recipe.image_url || dishImages[recipe.name]}`} alt="" style={{ width: 68, height: 68, borderRadius: 16, objectFit: "cover", flexShrink: 0, boxShadow: "0 3px 10px rgba(28,36,27,0.12)" }} />
                : imagesLoading
                  ? <DishProgress size={68} index={i} />
                  : <div style={styles.recipeCardEmoji}>{recipeEmoji(recipe.name)}</div>}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={styles.recipeCardTitle}>{recipe.name}</div>
                {!(recipe.image_url || dishImages[recipe.name]) && imagesLoading && (
                  <div style={{ fontSize: 13, color: "#b07a45", marginTop: 2, fontWeight: 600 }}>
                    🎨 Картинка генерируется…
                  </div>
                )}
                {(() => {
                  const tm = recipe.time_minutes || recipe.time || 0;
                  const diff = recipe.difficulty || recipe.level || "";
                  const kcal = recipe.calories_per_100g || recipe.calories_per_serving || recipe.calories || 0;
                  const dc2 = difficultyStyle(diff);
                  return (
                    <div style={styles.recipeCardMeta}>
                      {tm > 0 && (
                        <span style={styles.timeBadge}><UiIcon name="clock" size={16} style={{ marginRight: 3, marginTop: -2 }} />{tm} мин</span>
                      )}
                      {diff ? (
                        <span style={{ ...styles.diffBadge, background: dc2.bg, color: dc2.color }}>{diff}</span>
                      ) : null}
                      {kcal > 0 && (
                        <span style={styles.calBadge}><UiIcon name="flame" size={16} style={{ marginRight: 3, marginTop: -2 }} />{kcal} ккал{recipe.calories_per_100g ? "/100г" : ""}</span>
                      )}
                    </div>
                  );
                })()}
                {/* Теги здоровья на карточке результатов */}
                {Array.isArray(recipe.health_tags) && recipe.health_tags.length > 0 && (
                  <div style={styles.recipeCardTags}>
                    {recipe.health_tags.slice(0, 2).map((tag, ti) => {
                      // Защита от объекта: если Гига вернула {emoji, label} — конвертируем
                      let display = tag;
                      if (typeof tag === 'object' && tag !== null) {
                        display = `${tag.emoji || ''} ${tag.label || ''}`.trim();
                      }
                      if (typeof display !== 'string' || !display) return null;
                      return (
                        <span key={ti} style={styles.recipeCardTag}>{display}</span>
                      );
                    })}
                  </div>
                )}
                <button style={styles.cardBtn} className="povar-primary" onClick={() => openRecipe(recipe, "results")}>
                  Открыть рецепт ›
                </button>
              </div>
            </div>
          );
        })}
        <div style={styles.dislikeBlock}>
          <div style={styles.dislikeTitle}>Не то, что искали?</div>
          <div style={styles.dislikeSubtitle}>Попробуйте поискать по-другому</div>
          <div style={styles.dislikeBtns}>
            {[
              { key: "долго", label: "Долго", sub: "больше 60 мин" },
              { key: "дорого", label: "Дорого", sub: "премиум рецепты" },
              { key: "не ем это", label: "Не ем это", sub: "исключить" },
              { key: "другое", label: "Другое", sub: "необычные идеи" },
            ].map(r => (
              <button key={r.key}
                style={{ ...styles.dislikeBtn, opacity: regenerating ? 0.5 : 1, cursor: regenerating ? 'not-allowed' : 'pointer' }}
                disabled={regenerating}
                onClick={() => handleDislike(r.key)}>
                <div style={styles.dislikeBtnLabel}>{r.label}</div>
                <div style={styles.dislikeBtnSub}>{r.sub}</div>
              </button>
            ))}
          </div>
        </div>
        <Toast {...(toast || {})} onClose={() => setToast(null)} />
      </div>
    );
  }

  if (screen === "recipe" && selectedRecipe) {
    const dc = difficultyStyle(selectedRecipe.difficulty);
    const fromBook = previousScreen === "book";
    const steps = selectedRecipe.steps || [];
    const stepPhotos = selectedRecipe.step_photos || [];
    // фото: личное (сохранённое) ИЛИ сгенерированное (из базы при генерации)
    const _savedItem = savedRecipes.find(it => it.id === selectedRecipeId) || {};
    const recipePhoto = _savedItem.photo_url || "";
    const _genImgUrl = selectedRecipe.image_url || _savedItem.image_url || dishImages[selectedRecipe.name] || "";
    const genImage = _genImgUrl ? `${API}${_genImgUrl}` : "";
    const heroPhoto = recipePhoto || genImage;
    const replacements = (Array.isArray(selectedRecipe.replacements) ? selectedRecipe.replacements : [])
      .filter(r => r && typeof r === "object" && r.original && r.alternative
                   && String(r.original).trim().length > 0
                   && String(r.alternative).trim().length > 0);
    const findProdImg = (nm) => {
      const n = String(nm || "").toLowerCase().trim();
      if (!n) return "";
      const hit = productsBase.find(x => (x.name || "").toLowerCase() === n ||
        ((x.aliases || "").toLowerCase().split("|").map(s => s.trim()).includes(n)));
      return hit ? hit.image_url : "";
    };

    return (
      <div style={styles.page}>
        {heroPhoto ? (
          <div style={{ position: "relative", marginBottom: 14 }}>
            <img src={heroPhoto} alt="Фото блюда" className="povar-fade"
              style={{ width: "100%", borderRadius: 26, objectFit: "cover", display: "block", boxShadow: "0 8px 28px rgba(28,36,27,0.16)",
                       ...(heroPhoto === genImage ? { aspectRatio: "1 / 1" } : { maxHeight: 300 }) }} />
            <button onClick={() => setScreen(previousScreen)} className="povar-press" aria-label="Назад"
              style={{ position: "absolute", top: 12, left: 12, width: 38, height: 38, borderRadius: "50%",
                       background: "rgba(255,255,255,0.75)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
                       border: "none", cursor: "pointer",
                       display: "flex", alignItems: "center", justifyContent: "center",
                       fontSize: 20, color: "#1c241b", lineHeight: 1, paddingBottom: 2,
                       boxShadow: "0 2px 8px rgba(28,36,27,0.18)" }}>‹</button>
            {heroPhoto === genImage && !recipePhoto && (
              <div style={{ position: "absolute", bottom: 10, right: 10, fontSize: 12, color: "#fff",
                            background: "rgba(0,0,0,0.45)", borderRadius: 6, padding: "2px 7px",
                            backdropFilter: "blur(2px)" }}>
                иллюстрация ИИ
              </div>
            )}
          </div>
        ) : (
          <div style={styles.header}>
            <button style={styles.headerBack} onClick={() => setScreen(previousScreen)}>‹</button>
            <div style={{ flex: 1 }}><div style={styles.headerTitle}>{selectedRecipe.name}</div></div>
          </div>
        )}
        {(!heroPhoto && imagesLoading && !recipePhoto) ? (
          <div style={{ marginBottom: 12 }}>
            <DishProgress withText index={0} />
          </div>
        ) : null}
        {heroPhoto && (
          <div style={{ fontSize: 24, fontWeight: 800, color: "#1c241b", lineHeight: 1.25,
                        margin: "0 4px 10px" }}>{selectedRecipe.name}</div>
        )}
        <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap",
                      margin: "0 4px 14px" }}>
          {selectedRecipe.time_minutes > 0 && (
            <span style={{ fontSize: 15, color: "#4a5c4a", display: "inline-flex", alignItems: "center", gap: 5 }}>
              <UiIcon name="clock" size={20} style={{ marginRight: 2 }} />{selectedRecipe.time_minutes} мин
            </span>
          )}
          {selectedRecipe.difficulty && (
            <span style={{ fontSize: 15, color: dc.color, display: "inline-flex", alignItems: "center", gap: 5, fontWeight: 600 }}>
              {selectedRecipe.difficulty}
            </span>
          )}
          {(selectedRecipe.calories_per_100g > 0 || selectedRecipe.calories_per_serving > 0) && (
            <span style={{ fontSize: 15, color: "#4a5c4a", display: "inline-flex", alignItems: "center", gap: 5 }}>
              <UiIcon name="flame" size={20} style={{ marginRight: 2 }} />{selectedRecipe.calories_per_100g > 0
                ? `${selectedRecipe.calories_per_100g} ккал/100г`
                : `${selectedRecipe.calories_per_serving} ккал/порц`}
            </span>
          )}
        </div>

        {/* Социальное доказательство — снижает фрустрацию "я первый кролик". Показываем только если ≥5 */}
        {selectedRecipe.saved_by_others >= 5 && (
          <div style={{
            background: "rgba(15,110,86,0.08)",
            borderRadius: 12,
            padding: "8px 14px",
            fontSize: 15,
            color: "#0f6e56",
            marginBottom: 12,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}>
            <span style={{ fontSize: 16 }}>📖</span>
            <span>Уже сохранили <b>{selectedRecipe.saved_by_others}</b> {pluralize(selectedRecipe.saved_by_others, 'человек', 'человека', 'человек')}</span>
          </div>
        )}

        {selectedRecipe.missing_ingredients?.length > 0 && (
          <div style={styles.missing}>🛒 Докупите: {selectedRecipe.missing_ingredients.join(", ")}</div>
        )}

        {/* КБЖУ и здоровье */}
        <NutritionBlock recipe={selectedRecipe} />

        {/* Пометка: в рецепте есть непроверенный продукт → только для автора, репост нельзя */}
        {recipeUnverified && (
          <div style={{ ...styles.card, padding: 14, marginBottom: 12,
                        background: "#f2f8f2", border: "1.5px solid #e8a87c" }}>
            <div style={{ fontSize: 15, color: "#7a8a7a", lineHeight: 1.4 }}>
              ⚠️ В рецепте есть продукт, не прошедший проверку (введён вами). Этот рецепт доступен только вам — поделиться им нельзя.
            </div>
          </div>
        )}

        <div style={{ ...styles.card, padding: 18, marginBottom: 12 }}>
          <div style={styles.sectionTitle}>Ингредиенты</div>
          {(selectedRecipe.ingredients || []).map((ing, i) => (
            <div key={i} style={{ padding: "9px 0", borderBottom: i < (selectedRecipe.ingredients.length - 1) ? "1px solid #eef4ee" : "none" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <ProdIcon img={findProdImg(ing.name)} emoji="🥗" size={38} />
                <span onClick={() => openProductFromRecipe(ing.name)}
                  className="povar-press"
                  style={{ ...styles.ingredientName, flex: 1, cursor: "pointer", fontWeight: 500 }}
                  title={`Узнать о продукте «${ing.name}»`}>
                  {ing.name}
                </span>
                <span style={{ ...styles.ingredientAmount, flexShrink: 0 }}>{ing.amount}</span>
                <button onClick={() => openReplaceAny(ing.name)} className="povar-press"
                  title={`Заменить «${ing.name}»`}
                  style={{ background: "linear-gradient(145deg,#f2faf5,#e2f2e9)", border: "1px solid #c4e0d0", color: "#0f6e56",
                           borderRadius: 11, padding: "7px 12px", fontSize: 14, fontWeight: 600,
                           cursor: "pointer", flexShrink: 0, fontFamily: "inherit",
                           boxShadow: "inset 0 1px 1px rgba(255,255,255,0.8), 0 1px 3px rgba(28,36,27,0.06)" }}
                  disabled={replacingIdx !== -1}>
                  Заменить
                </button>
              </div>
              {/* КБЖУ + ХЕ ингредиента (если посчитано) */}
              {ing.cal > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginLeft: 48, marginTop: 5 }}>
                  <span style={{ fontSize: 13, color: "#0f6e56", background: "#eef8f2", borderRadius: 6, padding: "2px 7px", fontWeight: 600 }}>
                    {ing.cal} ккал
                  </span>
                  <span style={{ fontSize: 13, color: "#7a6a4a", background: "#f7f2e8", borderRadius: 6, padding: "2px 7px" }}>
                    Б{ing.p} Ж{ing.f} У{ing.c}
                  </span>
                  {ing.xe != null && ing.xe > 0 && (
                    <span style={{ fontSize: 13, color: "#2e6da4", background: "#eaf2fb", borderRadius: 6, padding: "2px 7px" }}
                      title="Хлебные единицы — для диабетиков">
                      🍞 {ing.xe} ХЕ
                    </span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {replacements.length > 0 && (
          <div style={{ ...styles.card, padding: 18, marginBottom: 12 }}>
            <div style={styles.sectionTitle}>🔄 Быстрая замена (что советует ИИ)</div>
            {replacements.map((rep, i) => {
              const isThisLoading = replacingIdx === i;
              const isOtherLoading = replacingIdx !== -1 && !isThisLoading;
              return (
                <div key={i} style={styles.replacementRow}>
                  <div style={styles.replacementText}>
                    <span style={styles.replacementOriginal}>{rep.original}</span>
                    <span style={styles.replacementArrow}> → </span>
                    <span style={styles.replacementAlternative}>{rep.alternative}</span>
                  </div>
                  <button
                    style={{
                      ...styles.replaceBtn,
                      opacity: isOtherLoading ? 0.4 : (isThisLoading ? 0.7 : 1),
                      pointerEvents: (isThisLoading || isOtherLoading) ? "none" : "auto",
                    }}
                    onClick={() => handleReplaceIngredient(rep.original, rep.alternative, i)}
                    disabled={replacingIdx !== -1}
                  >
                    {isThisLoading ? "Готовлю…" : "Заменить →"}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <div style={{ ...styles.card, padding: 18, marginBottom: 12 }}>
          <div style={{ ...styles.sectionTitle, display: "flex", alignItems: "center", gap: 8 }}><UiIcon name="pan_cooking" size={26} />Как готовить</div>
          {steps.map((step, i) => (
            <div key={i}>
              <div style={styles.step}>
                <span style={styles.stepNum}>{i + 1}</span>
                <span>{step}</span>
              </div>
              {stepPhotos[i] && (
                <img src={stepPhotos[i]} alt={`шаг ${i + 1}`}
                  style={{ display: "block", maxWidth: "100%", borderRadius: 10, margin: "6px 0 12px 28px", border: "1px solid #d5e5d5" }} />
              )}
            </div>
          ))}
        </div>

        {selectedRecipe.tip && <div style={styles.tip}>💡 {selectedRecipe.tip}</div>}

        {/* Заметка автора (user_note) — была видна только в публичном просмотре, теперь и в своём */}
        {selectedRecipe.user_note && (
          <div style={{ ...styles.card, padding: 14, marginBottom: 12, background: "#f4faf4" }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#4a5c4a", marginBottom: 4, display: "flex", alignItems: "center", gap: 6 }}><UiIcon name="note" size={19} />Примечание автора</div>
            <div style={{ fontSize: 16, color: "#445a44", lineHeight: 1.5 }}>{selectedRecipe.user_note}</div>
          </div>
        )}

        {/* «Чем полезно» от Гигы — появляется после публикации в общую базу */}
        {selectedRecipe.benefit_note && (
          <div style={{ ...styles.card, padding: 14, marginBottom: 12, background: "#eef7ee", border: "1px solid #cfe6cf" }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#2e6e3a", marginBottom: 4, display: "flex", alignItems: "center", gap: 6 }}><UiIcon name="leaf" size={19} />Чем полезно</div>
            <div style={{ fontSize: 16, color: "#3a5a40", lineHeight: 1.5 }}>{selectedRecipe.benefit_note}</div>
          </div>
        )}

        {fromBook && _savedItem.recipe_type === "own" && (
          <button style={{ ...styles.primaryButton, width: "100%", marginBottom: 8, background: "#2e7d32" }}
            className="povar-press" onClick={() => openPublishOwn(_savedItem)}>
            <UiIcon name="megaphone" size={22} style={{ marginRight: 4, marginTop: -2 }} />Поделиться в общую базу
          </button>
        )}
        <div style={styles.actions}>
          {fromBook ? (
            <>
              <button style={{ ...styles.primaryButton, flex: 1, marginBottom: 0 }}
                onClick={() => { if (_savedItem && _savedItem.id != null) openEditor(_savedItem); }}>
                <UiIcon name="pencil" size={20} style={{ marginRight: 6 }} />Редактировать
              </button>
              <button style={{ ...styles.dangerButton, flex: 1 }}
                onClick={() => askDeleteRecipe({ id: selectedRecipeId, name: selectedRecipe.name })}>
                <UiIcon name="trash" size={20} style={{ marginRight: 6 }} />Удалить
              </button>
            </>
          ) : (
            <>
              <button style={{ ...styles.primaryButton, flex: 1, marginBottom: 0, opacity: saving ? 0.6 : 1 }}
                onClick={() => handleSave(selectedRecipe)} disabled={saving}>
                ❤️ {saving ? "Сохраняю..." : "Сохранить"}
              </button>
            </>
          )}
        </div>
        <button style={styles.homeBtn} onClick={() => setScreen("main")}>↩ На главную</button>
        {replaceOverlay && (
          <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(60,26,0,0.55)",
                        display: "flex", alignItems: "center", justifyContent: "center", padding: 20, backdropFilter: "blur(2px)" }}>
            <div style={{ background: "#fffaf3", borderRadius: 20, padding: "32px 26px", maxWidth: 320, width: "100%",
                          textAlign: "center", boxShadow: "0 12px 40px rgba(0,0,0,0.25)" }} className="povar-pop">
              <div style={{ fontSize: 56, marginBottom: 16, animation: "replSpin 1.4s ease-in-out infinite", display: "inline-block" }}>🍳</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#1c2a1c", marginBottom: 8 }}>Готовлю новое блюдо</div>
              <div style={{ fontSize: 16, color: "#4a5c4a", marginBottom: 18, lineHeight: 1.5 }}>
                Заменяю <b>{replaceOverlay.ingredient}</b> на <b>{replaceOverlay.alternative}</b> и пересчитываю рецепт…
              </div>
              <div style={{ display: "flex", justifyContent: "center", gap: 6 }}>
                <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#0f6e56", animation: "replDot 1.2s infinite", animationDelay: "0s" }} />
                <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#0f6e56", animation: "replDot 1.2s infinite", animationDelay: "0.2s" }} />
                <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#0f6e56", animation: "replDot 1.2s infinite", animationDelay: "0.4s" }} />
              </div>
              <div style={{ fontSize: 14, color: "#8a9a8a", marginTop: 16 }}>Это займёт несколько секунд</div>
              <style>{`
                @keyframes replSpin { 0%,100%{transform:rotate(-8deg) scale(1)} 50%{transform:rotate(8deg) scale(1.08)} }
                @keyframes replDot { 0%,100%{opacity:0.3;transform:translateY(0)} 50%{opacity:1;transform:translateY(-5px)} }
              `}</style>
            </div>
          </div>
        )}
        {replaceAnyModal && (() => {
          const q = replaceAnyQuery.trim().toLowerCase();
          // пул: общая база + ЛИЧНЫЕ продукты юзера (как в редакторе), личные помечены _mine
          const _baseLc = new Set(productsBase.map(p => (p.name || "").toLowerCase()));
          const pool = [
            ...productsBase,
            ...myProducts.filter(p => p.name && !_baseLc.has((p.name || "").toLowerCase())).map(p => ({ ...p, _mine: true })),
          ];
          const matches = q.length >= 1
            ? pool.filter(p => {
                const nm = (p.name || "").toLowerCase();
                const al = (p.aliases || "").toLowerCase();
                return nm.includes(q) || al.includes(q);
              }).slice(0, 8)
            : pool.slice(0, 8);
          const exactExists = pool.some(p => (p.name || "").toLowerCase() === q);
          return (
            <div style={styles.modalOverlay} className="povar-fade" onClick={() => setReplaceAnyModal(null)}>
              <div style={styles.modalCard} className="povar-pop" onClick={ev => ev.stopPropagation()}>
                <div style={styles.modalTitle}>Заменить «{replaceAnyModal.original}»</div>
                <div style={styles.modalText}>Выберите продукт из базы или добавьте новый.</div>
                <input style={{ ...styles.input, marginTop: 8 }} placeholder="Поиск продукта…" autoFocus
                  value={replaceAnyQuery} onChange={ev => setReplaceAnyQuery(ev.target.value.slice(0, 40))} />
                <div style={{ maxHeight: 220, overflowY: "auto", marginTop: 8 }}>
                  {matches.map((p, i) => (
                    <button key={i} className="povar-press" onClick={() => pickReplacementProduct(p.name)}
                      style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "10px 8px",
                               background: "#fff", border: "1px solid #eee", borderRadius: 8, marginBottom: 4, cursor: "pointer", textAlign: "left" }}>
                      <ProdIcon img={p.image_url} emoji={p.emoji} size={22} />
                      <span style={{ flex: 1, fontSize: 16, color: "#1c2a1c" }}>{p.name}</span>
                      {p._mine && (
                        <span style={{ fontSize: 12, color: "#2e7d32", background: "#eaf6ea", borderRadius: 6, padding: "2px 6px", marginRight: 4 }}>мой</span>
                      )}
                      <span style={{ fontSize: 13, color: "#8a9a8a" }}>{p.calories_per_100g} ккал</span>
                    </button>
                  ))}
                  {matches.length === 0 && q.length >= 1 && (
                    <div style={{ fontSize: 15, color: "#8a9a8a", padding: "8px 4px" }}>Ничего не нашлось — можно добавить новый продукт ниже.</div>
                  )}
                </div>
                {q.length >= 2 && !exactExists && (
                  <button className="povar-press" onClick={() => openAddProduct(replaceAnyQuery.trim())}
                    style={{ width: "100%", marginTop: 8, padding: "10px", borderRadius: 10, border: "1.5px solid #a5d6a7",
                             background: "#eaf6ea", cursor: "pointer", fontSize: 16, color: "#2e7d32", fontWeight: 600 }}>
                    ➕ Добавить «{replaceAnyQuery.trim()}» в базу
                  </button>
                )}
                <button style={{ ...styles.modalBtnCancel, marginTop: 8 }} className="povar-press"
                  onClick={() => setReplaceAnyModal(null)}>Отмена</button>
              </div>
            </div>
          );
        })()}
        {addProductModal && (
          <div style={styles.modalOverlay} className="povar-fade" onClick={() => !addProductBusy && setAddProductModal(null)}>
            <div style={styles.modalCard} className="povar-pop" onClick={ev => ev.stopPropagation()}>
              <div style={styles.modalTitle}>{addProductData.emoji || "🥗"} Добавить продукт</div>
              <div style={styles.modalText}>«{addProductModal.name}» добавится в ваши продукты и общую базу. Проверьте КБЖУ на 100 г.</div>
              {addProductBusy && !addProductData.calories_per_100g ? (
                <div style={{ textAlign: "center", padding: 16, color: "#8a9a8a" }}>Спрашиваю у ИИ КБЖУ…</div>
              ) : (
                <div style={{ marginTop: 10 }}>
                  <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, color: "#8a9a8a", marginBottom: 2 }}>Калории</div>
                      <input style={styles.input} type="number" inputMode="decimal" placeholder="ккал"
                        value={addProductData.calories_per_100g}
                        onChange={ev => setAddProductData({ ...addProductData, calories_per_100g: ev.target.value })} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, color: "#8a9a8a", marginBottom: 2 }}>Белки</div>
                      <input style={styles.input} type="number" inputMode="decimal" placeholder="г"
                        value={addProductData.proteins_g}
                        onChange={ev => setAddProductData({ ...addProductData, proteins_g: ev.target.value })} />
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, color: "#8a9a8a", marginBottom: 2 }}>Жиры</div>
                      <input style={styles.input} type="number" inputMode="decimal" placeholder="г"
                        value={addProductData.fats_g}
                        onChange={ev => setAddProductData({ ...addProductData, fats_g: ev.target.value })} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, color: "#8a9a8a", marginBottom: 2 }}>Углеводы</div>
                      <input style={styles.input} type="number" inputMode="decimal" placeholder="г"
                        value={addProductData.carbs_g}
                        onChange={ev => setAddProductData({ ...addProductData, carbs_g: ev.target.value })} />
                    </div>
                  </div>
                </div>
              )}
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
                <button style={styles.modalBtnInfo} className="povar-press" disabled={addProductBusy}
                  onClick={() => submitAddProduct()}>
                  {addProductBusy ? "Сохраняю…" : (addProductModal.fromReplace ? "Добавить и заменить" : "Добавить продукт")}
                </button>
                <button style={styles.modalBtnCancel} className="povar-press" disabled={addProductBusy}
                  onClick={() => setAddProductModal(null)}>Отмена</button>
              </div>
            </div>
          </div>
        )}
        <Toast {...(toast || {})} onClose={() => setToast(null)} />
        <ConfirmModal {...(confirm || {})} onCancel={() => setConfirm(null)} />
        <InfoModal {...(info || {})} onClose={info?.onClose || (() => setInfo(null))} />
        {renderPublishModal()}
        <FloatingBack onClick={() => setScreen(previousScreen)} />
      </div>
    );
  }

  if (screen === "product_ref") {
    const POPULAR_PRODUCTS = [
      { name: "Творог", emoji: "🥛" },
      { name: "Гречка", emoji: "🌾" },
      { name: "Куриная грудка", emoji: "🍗" },
      { name: "Лосось", emoji: "🐟" },
      { name: "Яйца", emoji: "🥚" },
      { name: "Картофель", emoji: "🥔" },
      { name: "Капуста", emoji: "🥬" },
      { name: "Морковь", emoji: "🥕" },
      { name: "Помидоры", emoji: "🍅" },
      { name: "Огурцы", emoji: "🥒" },
      { name: "Яблоко", emoji: "🍎" },
      { name: "Банан", emoji: "🍌" },
      { name: "Грецкие орехи", emoji: "🌰" },
      { name: "Овсянка", emoji: "🥣" },
      { name: "Свёкла", emoji: "🍠" },
      { name: "Чеснок", emoji: "🧄" },
    ];

    // подсказки при вводе: фильтр загруженной базы по тексту (локально, без запросов)
    const inputTrim = productInput.trim().toLowerCase();
    const baseSuggestions = (inputTrim.length >= 2 && !showBase)
      ? productsBase.filter(p => (p.name || "").toLowerCase().includes(inputTrim)).slice(0, 6)
      : [];
    const exactInBase = productsBase.some(p => (p.name || "").toLowerCase() === inputTrim);

    // алфавитные группы для экрана «База»
    const baseGroups = {};
    if (showBase) {
      for (const p of productsBase) {
        const letter = (p.name || "?")[0].toUpperCase();
        if (!baseGroups[letter]) baseGroups[letter] = [];
        baseGroups[letter].push(p);
      }
    }
    const baseLetters = Object.keys(baseGroups).sort((a, b) => a.localeCompare(b, "ru"));

    return (
      <div style={styles.page}>
        <div style={styles.header}>
          <button style={styles.headerBack} onClick={() => {
            if (productReturnScreen) { setScreen(productReturnScreen); setProductReturnScreen(null); setProductInfo(null); setProductInput(""); setProductError(""); return; }
            if (showBase) { setShowBase(false); return; }
            setScreen("main"); setProductInfo(null); setProductInput(""); setProductError("");
          }}>‹</button>
          <div style={{ flex: 1 }}>
            <div style={styles.headerTitle}>{productReturnScreen ? "О продукте" : (showBase ? "База продуктов" : "Справочник продуктов")}</div>
            <div style={styles.headerSubtitle}>{productReturnScreen ? "‹ назад в рецепт" : (showBase ? "Всё что есть в базе (по алфавиту)" : "Узнайте о пользе и КБЖУ")}</div>
          </div>
        </div>

        {/* ЭКРАН БАЗЫ: алфавитный список */}
        {showBase ? (
          <>
            {productsBase.length === 0 ? (
              <div style={{ ...styles.card, padding: 20, textAlign: "center", color: "#7a8a7a" }}>
                Загружаю базу…
              </div>
            ) : (
              baseLetters.map(letter => (
                <div key={letter} style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 19, fontWeight: 800, color: "#4a7d3a", margin: "10px 6px 8px", letterSpacing: "-0.2px" }}>{letter}</div>
                  {baseGroups[letter].map(p => (
                    <button key={p.name}
                      className="povar-tap"
                      style={{ background: "#fff", borderRadius: 18, padding: "12px 14px", marginBottom: 7, width: "100%",
                               display: "flex", alignItems: "center", gap: 11, cursor: "pointer",
                               border: "none", textAlign: "left", fontFamily: "inherit",
                               boxShadow: "0 1px 2px rgba(28,36,27,0.05), 0 4px 12px rgba(28,36,27,0.07)" }}
                      onClick={() => { setShowBase(false); setProductInput(p.name); fetchProductInfo(p.name); }}>
                      <ProdIcon img={p.image_url} emoji={p.emoji} size={34} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 17, fontWeight: 600, color: "#1c241b" }}>{p.name}</div>
                        <div style={{ fontSize: 14, color: "#a1a39f", marginTop: 1 }}>
                          {p.calories_per_100g} ккал · Б {p.proteins_g} · Ж {p.fats_g} · У {p.carbs_g}
                        </div>
                      </div>
                      <span style={{ color: "#c9cdc4", fontSize: 18 }}>›</span>
                    </button>
                  ))}
                </div>
              ))
            )}
          </>
        ) : productReturnScreen ? null : (
        <>
        {/* Кнопка БАЗА — v4 плитка */}
        <button className="povar-tap"
          style={{ width: "100%", marginBottom: 14, background: "#fff", border: "none", borderRadius: 26,
                   padding: "13px 15px", cursor: "pointer", display: "flex", alignItems: "center", gap: 12,
                   textAlign: "left", fontFamily: "inherit",
                   boxShadow: "0 1px 2px rgba(28,36,27,0.05), 0 5px 14px rgba(28,36,27,0.06), 0 14px 36px rgba(28,36,27,0.07)" }}
          onClick={() => { loadProductsBase(); setShowBase(true); }}>
          <div className="povar-icon" style={{ width: 54, height: 54, background: "linear-gradient(145deg,#d6e8ae,#bfd88c)" }}><UiIcon name="books_catalog" size={44} /></div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#1c241b", lineHeight: 1.2, letterSpacing: "-0.2px" }}>Вся база продуктов</div>
            <div style={{ fontSize: 13, color: "#a1a39f", marginTop: 2, fontWeight: 500 }}>по алфавиту, с КБЖУ</div>
          </div>
          <span style={{ color: "#c9cdc4", fontSize: 18 }}>›</span>
        </button>

        <div style={styles.contextLabel}>Популярные:</div>
        <div style={styles.contextChips}>
          {POPULAR_PRODUCTS.map(p => (
            <button key={p.name} className="povar-press"
              style={{ ...styles.contextChip, fontSize: 16,
                       boxShadow: "0 1px 2px rgba(28,36,27,0.05), 0 4px 12px rgba(28,36,27,0.07)" }}
              onClick={() => { setProductInput(p.name); fetchProductInfo(p.name); }}>
              <span style={{ marginRight: 5, display: "inline-flex", verticalAlign: "middle" }}><ProdIcon img={(findProductByName(productsBase, p.name) || {}).image_url || ""} emoji={p.emoji} size={22} /></span>{p.name}
            </button>
          ))}
        </div>

        <div style={styles.hint}>Или напишите свой продукт</div>
        <div style={styles.inputCard}>
          <textarea style={{ ...styles.textarea, minHeight: 50 }}
            placeholder="Например: киноа, авокадо..."
            value={productInput}
            onChange={e => setProductInput(e.target.value.slice(0, 100))} />
          {productInput.length > 0 && (
            <button style={styles.clearBtn} onClick={() => { setProductInput(""); setProductInfo(null); setProductError(""); }}>✕</button>
          )}
        </div>

        {/* Подсказки из базы при вводе */}
        {baseSuggestions.length > 0 && (
          <div style={{ marginBottom: 10 }}>
            {baseSuggestions.map(p => (
              <button key={p.name}
                className="povar-tap"
                style={{ background: "#fff", borderRadius: 16, padding: "10px 14px", marginBottom: 5, width: "100%",
                         display: "flex", alignItems: "center", gap: 9, cursor: "pointer",
                         border: "none", textAlign: "left", fontFamily: "inherit",
                         boxShadow: "0 1px 2px rgba(28,36,27,0.05), 0 4px 12px rgba(28,36,27,0.07)" }}
                onClick={() => { setProductInput(p.name); fetchProductInfo(p.name); }}>
                <ProdIcon img={p.image_url} emoji={p.emoji} size={24} />
                <span style={{ fontSize: 16, color: "#1c241b", fontWeight: 500 }}>{p.name}</span>
                <span style={{ fontSize: 12, fontWeight: 600, marginLeft: "auto", color: "#0f6e56",
                               background: "linear-gradient(145deg,#e2f3ec,#d0ebe0)", borderRadius: 9,
                               padding: "4px 9px", boxShadow: "inset 0 1px 1px rgba(255,255,255,0.7)" }}>в базе</span>
              </button>
            ))}
          </div>
        )}

        <button style={{ ...styles.primaryButton, opacity: productLoading ? 0.6 : 1 }} className="povar-primary"
          onClick={() => fetchProductInfo(productInput)}
          disabled={productLoading || !productInput.trim()}>
          {productLoading ? "Ищу..." : "Узнать о продукте"}
        </button>

        {/* Если ввёл что-то, точного совпадения в базе нет и подсказок нет — подсказываем Мои продукты */}
        {inputTrim.length >= 2 && !exactInBase && baseSuggestions.length === 0 && productsBaseLoaded && !productInfo && (
          <div style={{ padding: "12px 14px", marginTop: 10, borderRadius: 18,
                        background: "rgba(255,255,255,0.55)", border: "1.5px dashed rgba(15,110,86,0.3)" }}>
            <div style={{ fontSize: 15, color: "#4a5c4a", lineHeight: 1.4 }}>
              Такого продукта нет в базе. Можно узнать о нём у ИИ (кнопка выше) или занести свой со своими КБЖУ в разделе <b>«Мои продукты»</b>.
            </div>
          </div>
        )}
        </>
        )}

        {productInfo && (
          <>
            {lightbox && (
              <div onClick={() => setLightbox(null)}
                style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)",
                         display: "flex", alignItems: "center", justifyContent: "center",
                         zIndex: 99999, cursor: "zoom-out", padding: 16 }}>
                <img src={lightbox} alt=""
                  style={{ maxWidth: "94%", maxHeight: "88%", objectFit: "contain",
                           borderRadius: 18, boxShadow: "0 8px 40px rgba(0,0,0,0.5)" }} />
                <div style={{ position: "absolute", top: 18, right: 22, color: "#fff",
                              fontSize: 30, fontWeight: 300, lineHeight: 1 }}>✕</div>
              </div>
            )}
            {/* Заголовок продукта */}
            <div style={{ ...styles.card, padding: 0, overflow: "hidden" }}>
              {productInfo.product_image_url ? (
                <img src={productInfo.product_image_url} alt={productInfo.name}
                  onClick={() => setLightbox(productInfo.product_image_url)}
                  style={{ width: "100%", aspectRatio: "1 / 1", objectFit: "cover", display: "block", cursor: "zoom-in" }}
                  onError={(e) => {
                    e.target.style.display = "none";
                    if (e.target.nextSibling) e.target.nextSibling.style.display = "block";
                  }} />
              ) : null}
              <div style={{ fontSize: 64, textAlign: "center", padding: "20px 0 0",
                            display: productInfo.product_image_url ? "none" : "block" }}>
                {productInfo.emoji || "🥗"}
              </div>
              <div style={{ padding: "13px 16px 15px" }}>
                <div style={{ fontSize: 21, fontWeight: 700, color: "#1c2a1c", lineHeight: 1.2 }}>{productInfo.name}</div>
                {productInfo.category && (
                  <div style={{ fontSize: 15, color: "#7a8a7a", marginTop: 3 }}>{productInfo.category}</div>
                )}
              </div>
            </div>

            {/* КБЖУ */}
            {productInfo.calories_per_100g > 0 && (
              <div style={styles.nutritionCard}>
                <div style={{ textAlign: "center", fontSize: 38, fontWeight: 800, color: "#1c2a1c", lineHeight: 1 }}>{productInfo.calories_per_100g}</div>
                <div style={{ textAlign: "center", fontSize: 14, color: "#7a8a7a", marginBottom: 13 }}>ккал на 100 г</div>
                {(() => {
                  const pv = Number(productInfo.proteins_g) || 0, fv = Number(productInfo.fats_g) || 0, cv = Number(productInfo.carbs_g) || 0;
                  const mx = Math.max(pv, fv, cv, 1);
                  const Bar = ({ label, val, color }) => (
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 15, color: "#33523f", marginBottom: 4 }}>
                        <span>{label}</span><span style={{ fontWeight: 600 }}>{val} г</span>
                      </div>
                      <div style={{ height: 7, background: "#edf1e8", borderRadius: 4, boxShadow: "inset 0 1px 2px rgba(28,36,27,0.07)" }}>
                        <div style={{ width: `${Math.round(val / mx * 100)}%`, height: 7, background: color, borderRadius: 4, boxShadow: "inset 0 1px 1px rgba(255,255,255,0.45)" }} />
                      </div>
                    </div>
                  );
                  return (
                    <>
                      <Bar label="Белки" val={pv} color="#1d9e75" />
                      <Bar label="Жиры" val={fv} color="#97c459" />
                      <Bar label="Углеводы" val={cv} color="#ef9f27" />
                    </>
                  );
                })()}
              </div>
            )}

            {/* Расширенный профиль из локальной базы: ХЕ / ГИ / сахара / фруктоза / натрий */}
            {productInfo.nutrition_ext && (
              <div style={{ ...styles.card, padding: 16, marginBottom: 12 }}>
                <div style={styles.sectionTitle}>📊 Подробнее (на 100 г)</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
                  <div style={styles.extChip}>
                    <b>{productInfo.nutrition_ext.bread_units != null ? productInfo.nutrition_ext.bread_units : 0}</b> ХЕ
                    <span style={styles.extHint}>хлебные ед.</span>
                  </div>
                  <div style={styles.extChip}>
                    <b>{productInfo.nutrition_ext.glycemic_index != null ? productInfo.nutrition_ext.glycemic_index : "—"}</b> ГИ
                    <span style={styles.extHint}>гликем. индекс</span>
                  </div>
                  <div style={styles.extChip}>
                    <b>{productInfo.nutrition_ext.sugars_g != null ? productInfo.nutrition_ext.sugars_g : 0}</b> г
                    <span style={styles.extHint}>сахара</span>
                  </div>
                  <div style={styles.extChip}>
                    <b>{productInfo.nutrition_ext.fiber_g != null ? productInfo.nutrition_ext.fiber_g : 0}</b> г
                    <span style={styles.extHint}>клетчатка</span>
                  </div>
                  <div style={styles.extChip}>
                    <b>{productInfo.nutrition_ext.sodium_mg != null ? productInfo.nutrition_ext.sodium_mg : "—"}</b> мг
                    <span style={styles.extHint}>натрий</span>
                  </div>
                </div>
                <div style={{ fontSize: 13, color: "#8a9a8a", marginTop: 8 }}>«—» — данные уточняются</div>
              </div>
            )}

            {/* Глютен / лактоза — бейджи (из обогащения базы) */}
            {(productInfo.has_gluten != null || productInfo.has_lactose != null) && (
              <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
                {productInfo.has_gluten != null && (
                  <div style={{ flex: 1, minWidth: 140, padding: "10px 14px", borderRadius: 12,
                    background: productInfo.has_gluten ? "rgba(255,152,0,0.1)" : "rgba(46,125,50,0.1)",
                    border: `1.5px solid ${productInfo.has_gluten ? "rgba(255,152,0,0.3)" : "rgba(46,125,50,0.3)"}`,
                    display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 20 }}>🌾</span>
                    <span style={{ fontSize: 15, fontWeight: 600, color: productInfo.has_gluten ? "#ba7515" : "#2e7d32" }}>
                      {productInfo.has_gluten ? "Содержит глютен" : "Без глютена"}
                    </span>
                  </div>
                )}
                {productInfo.has_lactose != null && (
                  <div style={{ flex: 1, minWidth: 140, padding: "10px 14px", borderRadius: 12,
                    background: productInfo.has_lactose ? "rgba(255,152,0,0.1)" : "rgba(46,125,50,0.1)",
                    border: `1.5px solid ${productInfo.has_lactose ? "rgba(255,152,0,0.3)" : "rgba(46,125,50,0.3)"}`,
                    display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 20 }}>🥛</span>
                    <span style={{ fontSize: 15, fontWeight: 600, color: productInfo.has_lactose ? "#ba7515" : "#2e7d32" }}>
                      {productInfo.has_lactose ? "Содержит лактозу" : "Без лактозы"}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Профили под аудитории — GSAP-лента с дрейфом (фишка) */}
            {productInfo.audience && typeof productInfo.audience === "object" && Object.keys(productInfo.audience).length > 0 && (() => {
              const audList = [["веган","🌱"],["вегетарианец","🥗"],["худеющий","🏃"],["набор массы","💪"],
                ["диабетик","🩸"],["гипертоник","🧂"],["ЖКТ/язвенник","🩹"],["сердечник","❤️"]]
                .filter(([k]) => productInfo.audience[k]);
              const audLoop = audList.length >= 4;
              const renderAud = ([k, emoji], prefix) => {
                const a = productInfo.audience[k];
                const v = (a && a.verdict) || "";
                const st = v === "подходит" ? { bg: "linear-gradient(145deg,#e6f4e6,#d5ecd5)", fg: "#2e7d32", lbl: "✓ подходит" }
                  : v === "нельзя" ? { bg: "linear-gradient(145deg,#fdecea,#f8d9d5)", fg: "#c62828", lbl: "✕ нельзя" }
                  : { bg: "linear-gradient(145deg,#fdf3e0,#fae8cb)", fg: "#ba7515", lbl: "! осторожно" };
                return (
                  <div key={prefix + k} style={{ flexShrink: 0, width: 150,
                    borderRadius: 18, padding: 14, background: st.bg, border: "none",
                    boxShadow: "inset 0 1px 1px rgba(255,255,255,0.7), 0 2px 8px rgba(28,36,27,0.06)" }}>
                    <div style={{ fontSize: 28, marginBottom: 4 }}>{emoji}</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: "#1c241b", marginBottom: 6 }}>{k}</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: st.fg, marginBottom: 6 }}>{st.lbl}</div>
                    <div style={{ fontSize: 13, color: "#445a44", lineHeight: 1.4 }}>{(a && a.note) || ""}</div>
                  </div>
                );
              };
              return (
                <div style={{ ...styles.card, padding: 16, marginBottom: 12 }}>
                  <div style={styles.sectionTitle}>👥 Кому подходит</div>
                  {!audLoop && (
                    <div style={{ fontSize: 13, color: "#7a8a7a", marginTop: -4, marginBottom: 10 }}>
                      листайте вбок →
                    </div>
                  )}
                  <div ref={audVpRef} className="povar-chips-viewport"
                    style={{ overflowX: audLoop ? "hidden" : "auto", padding: "4px 0 6px", marginTop: audLoop ? 6 : 0 }}>
                    <div ref={audTrackRef} data-loop={audLoop ? "1" : "0"}
                      style={{ display: "flex", gap: 10, willChange: "transform", width: "max-content" }}>
                      {audLoop ? audList.map(a => renderAud(a, "")).concat(audList.map(a => renderAud(a, "b"))) : audList.map(a => renderAud(a, ""))}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Плашка: добавлено пользователем (брендовое/магазинное) */}
            {productInfo.is_user_content && (
              <div style={{ padding: "10px 14px", marginBottom: 12, borderRadius: 16,
                background: "linear-gradient(145deg,#fdf3e0,#fae8cb)", boxShadow: "inset 0 1px 1px rgba(255,255,255,0.6)",
                fontSize: 14, color: "#9a6a1e", lineHeight: 1.4 }}>
                ➕ Добавлено пользователем. Поварёшка не отвечает за достоверность данных по этому продукту.
              </div>
            )}

            {/* Польза */}
            {Array.isArray(productInfo.benefits) && productInfo.benefits.length > 0 && (
              <div style={{ ...styles.card, padding: 16, marginBottom: 12 }}>
                <div style={{ ...styles.sectionTitle, display: "flex", alignItems: "center", gap: 9 }}>
                  <UiIcon name="heart_green" size={28} />Чем полезен
                </div>
                {productInfo.benefits.map((b, i) => (
                  <div key={i} style={{ fontSize: 16, color: "#1c2a1c", lineHeight: 1.5, marginBottom: 8, display: "flex", gap: 8 }}>
                    <span style={{ color: "#2e7d32", flexShrink: 0 }}>•</span>
                    <span>{b}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Кому осторожно */}
            {Array.isArray(productInfo.cautions) && productInfo.cautions.length > 0 && (
              <div style={{ ...styles.card, padding: 16, marginBottom: 12, background: "rgba(255,152,0,0.08)", border: "1.5px solid rgba(255,152,0,0.25)" }}>
                <div style={{ ...styles.sectionTitle, color: "#ba7515" }}>⚠️ Кому осторожно</div>
                {productInfo.cautions.map((c, i) => (
                  <div key={i} style={{ fontSize: 15, color: "#ba7515", lineHeight: 1.5, marginBottom: 6, display: "flex", gap: 8 }}>
                    <span style={{ flexShrink: 0 }}>•</span>
                    <span>{c}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Хорошо сочетается */}
            {Array.isArray(productInfo.good_with) && productInfo.good_with.length > 0 && (
              <div style={{ ...styles.card, padding: 16, marginBottom: 12 }}>
                <div style={styles.sectionTitle}>🤝 Хорошо сочетается с</div>
                <div style={{ fontSize: 13, color: "#7a8a7a", marginTop: -4, marginBottom: 8 }}>
                  нажмите чтобы приготовить
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {productInfo.good_with.map((g, i) => (
                    <button
                      key={i}
                      onClick={() => {
                        track('product_cook_with', { product: productInfo.name, partner: g });
                        scheduleInterstitial(vkId, 'product_cook');
                        setProducts(`${productInfo.name}, ${g}`);
                        setContext(null);
                        setError("");
                        setScreen("input");
                      }}
                      style={{
                        ...styles.healthTag,
                        border: "none",
                        cursor: "pointer",
                        background: "linear-gradient(145deg,#e6f4e6,#d7ecd7)",
                        fontWeight: 600,
                        boxShadow: "inset 0 1px 1px rgba(255,255,255,0.7), 0 1px 3px rgba(28,36,27,0.06)",
                      }}>
                      {g} →
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Сочетания текстом (из обогащения базы) — когда нет кликабельных чипов good_with */}
            {productInfo.combos_text && !(Array.isArray(productInfo.good_with) && productInfo.good_with.length > 0) && (
              <div style={{ ...styles.card, padding: 16, marginBottom: 12 }}>
                <div style={styles.sectionTitle}>🤝 С чем сочетается</div>
                <div style={{ fontSize: 16, color: "#1c2a1c", lineHeight: 1.5, marginTop: 6 }}>
                  {productInfo.combos_text}
                </div>
              </div>
            )}

            {/* Кнопка-мост: приготовить с этим продуктом */}
            <button
              onClick={() => {
                track('product_cook_solo', { product: productInfo.name });
                scheduleInterstitial(vkId, 'product_cook');
                setProducts(productInfo.name);
                setContext(null);
                setError("");
                setScreen("input");
              }}
              style={{ ...styles.primaryButton, marginBottom: 12 }}>
              🍳 Приготовить с этим продуктом
            </button>

            {/* Дисклеймер */}
            <div style={{ ...styles.healthDisclaimer, marginTop: 4, marginBottom: 12 }}>
              {productInfo.disclaimer
                || "Носит исключительно ознакомительный характер. Не является медицинской консультацией. При заболеваниях обращайтесь к врачу."}
            </div>
          </>
        )}

        <Toast {...(toast || {})} onClose={() => setToast(null)} />
        <InfoModal {...(info || {})} onClose={info?.onClose || (() => setInfo(null))} />
        <FloatingBack onClick={() => {
          if (productReturnScreen) { setScreen(productReturnScreen); setProductReturnScreen(null); setProductInfo(null); setProductInput(""); setProductError(""); window.scrollTo(0, 0); return; }
          if (showBase) { setShowBase(false); window.scrollTo(0, 0); return; }
          setScreen("main"); setProductInfo(null); setProductInput(""); setProductError("");
        }} />
      </div>
    );
  }

  if (screen === "healthConditionsList") {
    return (
      <div style={styles.page}>
        <div style={styles.header}>
          <button style={styles.headerBack} onClick={() => setScreen("main")}>‹</button>
          <div style={{ flex: 1 }}>
            <div style={styles.headerTitle}>Что полезно при...</div>
            <div style={styles.headerSubtitle}>Выберите тему</div>
          </div>
        </div>

        <div style={{ ...styles.healthDisclaimer, marginBottom: 14 }}>
          Это информационный раздел. Не является медицинской консультацией. При заболеваниях обращайтесь к врачу.
        </div>

        {healthLoading && healthConditions.length === 0 && (
          <div style={{ textAlign: "center", padding: 40, color: "#7a8a7a" }}>Загружаю...</div>
        )}

        {healthError && healthConditions.length === 0 && (
          <div style={styles.error}>{healthError}</div>
        )}

        {healthConditions.length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
            {healthConditions.map(c => (
              <button key={c.key}
                onClick={() => selectHealthCondition(c)}
                style={{
                  background: "#fff",
                  border: "none",
                  borderRadius: 20,
                  padding: "14px 8px 10px",
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 4,
                  minHeight: 100,
                  fontFamily: "inherit",
                  boxShadow: "0 1px 2px rgba(28,36,27,0.05), 0 5px 14px rgba(28,36,27,0.07)",
                }}>
                <div style={{ fontSize: 30 }}>{c.emoji}</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#1c2a1c", textAlign: "center", lineHeight: 1.2 }}>
                  {c.name}
                </div>
                {c.subtitle && (
                  <div style={{ fontSize: 12, color: "#7a8a7a", textAlign: "center", lineHeight: 1.1 }}>
                    {c.subtitle}
                  </div>
                )}
              </button>
            ))}
          </div>
        )}

        <Toast {...(toast || {})} onClose={() => setToast(null)} />
      </div>
    );
  }

  if (screen === "healthConditionDetail") {
    return (
      <div style={styles.page}>
        <div style={styles.header}>
          <button style={styles.headerBack} onClick={() => { setScreen("healthConditionsList"); setSelectedCondition(null); setHealthError(""); }}>‹</button>
          <div style={{ flex: 1 }}>
            <div style={styles.headerTitle}>{selectedCondition ? selectedCondition.name : "Загрузка..."}</div>
            <div style={styles.headerSubtitle}>Полезные продукты</div>
          </div>
        </div>

        {healthLoading && !selectedCondition && (
          <div style={{ textAlign: "center", padding: 40, color: "#7a8a7a" }}>Загружаю...</div>
        )}

        {healthError && !selectedCondition && (
          <div style={styles.error}>{healthError}</div>
        )}

        {selectedCondition && (
          <>
            <div style={{ ...styles.card, textAlign: "center", padding: "20px 16px 16px" }}>
              <div style={{ fontSize: 64, marginBottom: 8 }}>{selectedCondition.emoji}</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: "#1c2a1c", marginBottom: 8 }}>
                {selectedCondition.name}
              </div>
              {selectedCondition.intro && (
                <div style={{ fontSize: 16, color: "#33523f", lineHeight: 1.45 }}>
                  {selectedCondition.intro}
                </div>
              )}
            </div>

            {Array.isArray(selectedCondition.products) && selectedCondition.products.length > 0 && (
              <div style={{ ...styles.card, padding: 16, marginBottom: 12 }}>
                <div style={styles.sectionTitle}>🥗 Что есть</div>
                {selectedCondition.products.map((p, i) => (
                  <div key={i} style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                    padding: "10px 0",
                    borderBottom: i < selectedCondition.products.length - 1 ? "1px solid rgba(28,36,27,0.07)" : "none",
                  }}>
                    <div style={{ fontSize: 26, lineHeight: 1, flexShrink: 0 }}>{p.emoji || "•"}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 17, fontWeight: 600, color: "#1c2a1c", marginBottom: 2 }}>
                        {p.name}
                      </div>
                      {p.why && (
                        <div style={{ fontSize: 15, color: "#2e4a3a", lineHeight: 1.4 }}>{p.why}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {Array.isArray(selectedCondition.avoid) && selectedCondition.avoid.length > 0 && (
              <div style={{ ...styles.card, padding: 16, marginBottom: 12 }}>
                <div style={styles.sectionTitle}>⚠️ Что ограничить</div>
                {selectedCondition.avoid.map((a, i) => (
                  <div key={i} style={{ fontSize: 16, color: "#2e4a3a", padding: "4px 0", lineHeight: 1.4 }}>
                    • {a}
                  </div>
                ))}
              </div>
            )}

            {selectedCondition.tip && (
              <div style={{ ...styles.card, padding: 16, marginBottom: 12, background: "#f4faf4" }}>
                <div style={styles.sectionTitle}>💡 Совет</div>
                <div style={{ fontSize: 16, color: "#33523f", lineHeight: 1.45 }}>
                  {selectedCondition.tip}
                </div>
              </div>
            )}

            <button style={{ ...styles.primaryButton, marginBottom: 12 }} onClick={cookFromCondition}>
              🍳 Приготовить из этих продуктов
            </button>

            <div style={{ ...styles.healthDisclaimer, marginTop: 4, marginBottom: 12 }}>
              Носит исключительно ознакомительный характер. Не является медицинской консультацией. При заболеваниях обращайтесь к врачу.
            </div>
          </>
        )}

        <Toast {...(toast || {})} onClose={() => setToast(null)} />
      </div>
    );
  }

  if (screen === "challengeIntro") {
    const theme = challengeThemes && challengeThemes[0];
    return (
      <div style={styles.page}>
        <div style={styles.header}>
          <button style={styles.headerBack} onClick={() => setScreen("main")}>‹</button>
          <div style={{ flex: 1 }}>
            <div style={styles.headerTitle}>Программа здоровья</div>
            <div style={styles.headerSubtitle}>30 дней лёгкости</div>
          </div>
        </div>

        {challengeLoading && !theme && (
          <div style={{ textAlign: "center", padding: 40, color: "#7a8a7a" }}>Загружаю...</div>
        )}

        {challengeError && (
          <div style={styles.error}>{challengeError}</div>
        )}

        {theme && (
          <>
            <div style={{ ...styles.card, textAlign: "center", padding: "24px 18px 18px" }}>
              <div style={{ fontSize: 64, marginBottom: 8 }}>🌿</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: "#1c2a1c", marginBottom: 4 }}>
                {theme.title}
              </div>
              <div style={{ fontSize: 16, color: "#7a8a7a", marginBottom: 12 }}>
                {theme.subtitle}
              </div>
              {theme.intro && (
                <div style={{ fontSize: 16, color: "#33523f", lineHeight: 1.5, marginBottom: 8 }}>
                  {theme.intro}
                </div>
              )}
            </div>

            <div style={{ ...styles.card, padding: 16, marginBottom: 12 }}>
              <div style={styles.sectionTitle}>Как устроен челлендж</div>
              <div style={{ fontSize: 15, color: "#2e4a3a", lineHeight: 1.5 }}>
                Каждый день — один совет по еде, рецепт и мягкое действие по желанию.
                Без диет, запретов и подсчёта калорий.<br /><br />
                Через 7 дней — пауза, чтобы решить продолжать или нет.
                На 30-й день — значок «🌿 Осознанность» навсегда.
              </div>
            </div>

            <div style={{ ...styles.healthDisclaimer, marginBottom: 14 }}>
              Информационная программа. Не лечение и не диета. Перед стартом проконсультируйтесь с врачом — особенно при хронических заболеваниях, беременности или приёме лекарств.
            </div>

            <button style={styles.primaryButton} onClick={openConsent}>
              Начать челлендж
            </button>
          </>
        )}

        {showConsentModal && (
          <div style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 16, zIndex: 100,
          }}>
            <div style={{
              background: "#f2f8f2", borderRadius: 18, padding: 20,
              maxWidth: 400, width: "100%",
            }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: "#1c2a1c", marginBottom: 8 }}>
                🌿 Перед стартом
              </div>
              <div style={{ fontSize: 15, color: "#33523f", lineHeight: 1.5, marginBottom: 12 }}>
                Эта программа — не лечение и не диета. Рекомендации не заменяют консультацию врача.<br /><br />
                Если у вас есть хронические заболевания, беременность или вы принимаете лекарства — обсудите участие с врачом.<br /><br />
                Все упражнения мягкие и выполняются по желанию.
              </div>
              <label style={{
                display: "flex", alignItems: "flex-start", gap: 10,
                padding: "12px 0", cursor: "pointer", fontSize: 15, color: "#1c2a1c",
              }}>
                <input
                  type="checkbox"
                  checked={consentChecked}
                  onChange={e => setConsentChecked(e.target.checked)}
                  style={{ marginTop: 2, transform: "scale(1.3)", cursor: "pointer" }}
                />
                <span>Я ознакомлен и беру ответственность на себя</span>
              </label>
              <button
                style={{
                  ...styles.primaryButton,
                  opacity: consentChecked ? 1 : 0.5,
                  cursor: consentChecked ? "pointer" : "not-allowed",
                  marginTop: 8,
                }}
                disabled={!consentChecked}
                onClick={confirmStartChallenge}>
                Начинаю
              </button>
              <button
                style={{
                  display: "block",
                  margin: "10px auto 0",
                  padding: "8px 16px",
                  background: "transparent",
                  border: "none",
                  color: "#7a8a7a",
                  fontSize: 16,
                  cursor: "pointer",
                  textDecoration: "underline",
                }}
                onClick={() => setShowConsentModal(false)}>
                Не сейчас
              </button>
            </div>
          </div>
        )}

        <Toast {...(toast || {})} onClose={() => setToast(null)} />
      </div>
    );
  }

  if (screen === "challengeDay") {
    const cs = challengeState;
    // Если состояние явно говорит «нет активного» — редирект на интро
    if (cs && cs.active === false) {
      setTimeout(() => setScreen("challengeIntro"), 0);
      return (
        <div style={styles.page}>
          <div style={{ textAlign: "center", padding: 40, color: "#7a8a7a" }}>Загружаю...</div>
        </div>
      );
    }
    const day = cs && cs.day_content;
    const progress = cs && cs.total_days ? (cs.completed_days / cs.total_days) * 100 : 0;
    const canCheckin = cs && (cs.can_checkin_today !== false) && !cs.today_marked;

    return (
      <div style={styles.page}>
        <div style={styles.header}>
          <button style={styles.headerBack} onClick={() => setScreen("main")}>‹</button>
          <div style={{ flex: 1 }}>
            <div style={styles.headerTitle}>
              {cs && cs.current_day && cs.total_days
                ? `День ${cs.current_day} из ${cs.total_days}`
                : "Челлендж"}
            </div>
            <div style={styles.headerSubtitle}>
              {cs && cs.title ? cs.title : "30 дней лёгкости"}
            </div>
          </div>
        </div>

        {cs && cs.total_days && (
          <div style={{
            background: "#e7f5e1", borderRadius: 8, height: 6,
            marginBottom: 14, overflow: "hidden",
          }}>
            <div style={{
              background: "#7cb342", height: "100%",
              width: `${progress}%`, transition: "width 0.3s",
            }} />
          </div>
        )}

        {challengeLoading && !day && (
          <div style={{ textAlign: "center", padding: 40, color: "#7a8a7a" }}>Загружаю...</div>
        )}

        {challengeError && (
          <div style={styles.error}>{challengeError}</div>
        )}

        {day && (
          <>
            <div style={{ ...styles.card, textAlign: "center", padding: "20px 16px 16px" }}>
              <div style={{ fontSize: 56, marginBottom: 8 }}>{day.emoji}</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: "#1c2a1c", marginBottom: 8 }}>
                {day.title}
              </div>
              {day.intro && (
                <div style={{ fontSize: 16, color: "#33523f", lineHeight: 1.5 }}>
                  {day.intro}
                </div>
              )}
            </div>

            {day.food_tip && (
              <div style={{ ...styles.card, padding: 16, marginBottom: 12 }}>
                <div style={styles.sectionTitle}>🥗 Сегодня</div>
                <div style={{ fontSize: 17, fontWeight: 600, color: "#1c2a1c", marginBottom: 6 }}>
                  {day.food_tip.headline}
                </div>
                {day.food_tip.text && (
                  <div style={{ fontSize: 16, color: "#33523f", lineHeight: 1.5 }}>
                    {day.food_tip.text}
                  </div>
                )}
              </div>
            )}

            {day.recipe_hint && (
              <div style={{ ...styles.card, padding: 16, marginBottom: 12, background: "#f4faf4" }}>
                <div style={styles.sectionTitle}>🍳 Рецепт дня</div>
                <div style={{ fontSize: 17, fontWeight: 600, color: "#1c2a1c", marginBottom: 4 }}>
                  {day.recipe_hint.name}
                </div>
                {day.recipe_hint.why && (
                  <div style={{ fontSize: 15, color: "#2e4a3a", lineHeight: 1.4 }}>
                    {day.recipe_hint.why}
                  </div>
                )}
              </div>
            )}

            {day.optional_action && (
              <div style={{ ...styles.card, padding: 16, marginBottom: 12 }}>
                <div style={styles.sectionTitle}>🌱 По желанию</div>
                <div style={{ fontSize: 17, fontWeight: 600, color: "#1c2a1c", marginBottom: 4 }}>
                  {day.optional_action.title}
                </div>
                {day.optional_action.text && (
                  <div style={{ fontSize: 15, color: "#2e4a3a", lineHeight: 1.4 }}>
                    {day.optional_action.text}
                  </div>
                )}
              </div>
            )}

            {day.motivation && (
              <div style={{
                ...styles.card, padding: "12px 16px", marginBottom: 12,
                fontStyle: "italic", textAlign: "center",
                fontSize: 16, color: "#33523f", background: "linear-gradient(145deg,#fdf3e0,#fae8cb)",
                boxShadow: "inset 0 1px 1px rgba(255,255,255,0.6), 0 4px 12px rgba(28,36,27,0.06)",
              }}>
                💚 {day.motivation}
              </div>
            )}

            {canCheckin && (
              <button
                style={{ ...styles.primaryButton, marginBottom: 12 }}
                onClick={checkinChallengeDay}
                disabled={challengeLoading}>
                {challengeLoading ? "..." : "✓ Отметить день"}
              </button>
            )}
            {!canCheckin && cs && (
              <div style={{
                ...styles.card, textAlign: "center", padding: 14,
                background: "#e7f5e1", color: "#1c2a1c", marginBottom: 12,
              }}>
                ✓ Сегодня день уже отмечен. Возвращайтесь завтра 💚
              </div>
            )}

            {cs && cs.disclaimer && (
              <div style={{ ...styles.healthDisclaimer, marginBottom: 12 }}>
                {cs.disclaimer}
              </div>
            )}
          </>
        )}

        <Toast {...(toast || {})} onClose={() => setToast(null)} />
      </div>
    );
  }

  if (screen === "challengeReflection") {
    return (
      <div style={styles.page}>
        <div style={styles.header}>
          <button style={styles.headerBack} onClick={() => setScreen("challengeDay")}>‹</button>
          <div style={{ flex: 1 }}>
            <div style={styles.headerTitle}>Неделя позади</div>
            <div style={styles.headerSubtitle}>День 7 — пауза для размышления</div>
          </div>
        </div>

        <div style={{ ...styles.card, textAlign: "center", padding: "24px 18px 18px" }}>
          <div style={{ fontSize: 64, marginBottom: 8 }}>✨</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#1c2a1c", marginBottom: 12 }}>
            Прошла первая неделя
          </div>
          <div style={{ fontSize: 16, color: "#33523f", lineHeight: 1.5, textAlign: "left" }}>
            Спросите себя три вещи:<br /><br />
            1) Что я чувствую — лёгкость, обычное или тяжесть?<br /><br />
            2) Какой день из семи был лучшим?<br /><br />
            3) Хочу ли я продолжать?<br /><br />
            Честно. Без правильных ответов.
          </div>
        </div>

        <button
          style={{ ...styles.primaryButton, marginBottom: 8 }}
          onClick={() => submitWeek1Decision("continue")}
          disabled={challengeLoading}>
          💚 Продолжаю
        </button>
        <button
          style={{
            ...styles.dangerButton, background: "transparent",
            color: "#7a8a7a", border: "1px solid #d5e5d5",
          }}
          onClick={() => submitWeek1Decision("quit")}
          disabled={challengeLoading}>
          Не моё, выйти
        </button>

        <Toast {...(toast || {})} onClose={() => setToast(null)} />
      </div>
    );
  }

  if (screen === "challengeFinish") {
    return (
      <div style={styles.page}>
        <div style={styles.header}>
          <button style={styles.headerBack} onClick={() => setScreen("main")}>‹</button>
          <div style={{ flex: 1 }}>
            <div style={styles.headerTitle}>Финиш!</div>
            <div style={styles.headerSubtitle}>30 дней позади</div>
          </div>
        </div>

        <div style={{ ...styles.card, textAlign: "center", padding: "32px 18px 24px" }}>
          <div style={{ fontSize: 80, marginBottom: 12 }}>🌿</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: "#1c2a1c", marginBottom: 8 }}>
            Поздравляем!
          </div>
          <div style={{ fontSize: 16, color: "#7a8a7a", marginBottom: 16 }}>
            Вы получили значок «Осознанность»
          </div>
          <div style={{ fontSize: 16, color: "#33523f", lineHeight: 1.5 }}>
            30 дней внимания к себе — это не мелочь, это редкое умение.<br /><br />
            Значок останется с вами навсегда. Можно вернуться к челленджу позже или попробовать другие.
          </div>
        </div>

        <button style={styles.primaryButton} onClick={() => setScreen("main")}>
          На главную
        </button>

        <Toast {...(toast || {})} onClose={() => setToast(null)} />
      </div>
    );
  }

  if (screen === "my_products") {
    const cooldownLeft = Math.max(0, Math.ceil((mpCooldownUntil - Date.now()) / 1000));
    return (
      <div style={styles.page}>
        <div style={styles.header}>
          <button style={styles.headerBack} onClick={() => setScreen("main")}>‹</button>
          <div>
            <div style={styles.headerTitle}>🥗 Мои продукты</div>
            <div style={styles.headerSubtitle}>личная база КБЖУ</div>
          </div>
        </div>

        {/* Кнопка открыть форму добавления */}
        {mpMode === null && (
          <div style={{ ...styles.card, padding: 16, marginBottom: 14 }}>
            <button
              style={{ ...styles.primaryButton, width: "100%" }}
              onClick={() => { setMpMode("form"); setMpError(""); }}>
              ➕ Добавить продукт
            </button>
          </div>
        )}

        {/* Форма добавления (единый путь: имя + заметка + КБЖУ) */}
        {mpMode === "form" && (
          <div style={{ ...styles.card, padding: 16, marginBottom: 14 }}>
            <div style={{ fontSize: 16, fontWeight: 600, color: "#1c2a1c", marginBottom: 4 }}>
              Новый продукт (на 100 г)
            </div>
            <div style={{ fontSize: 14, color: "#7a8a7a", marginBottom: 10, lineHeight: 1.4 }}>
              Укажите КБЖУ сами. Если продукта ещё нет в базе — он пройдёт быструю проверку.
            </div>
            <input style={styles.input} placeholder="Название (напр. Сосиски Талжи)"
              value={mpName} onChange={e => setMpName(e.target.value.slice(0, 60))} />
            <input style={{ ...styles.input, marginTop: 8 }} placeholder="Заметка (необязательно, напр. «не острые»)"
              value={mpNote} onChange={e => setMpNote(e.target.value.slice(0, 200))} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 8 }}>
              <input style={styles.input} placeholder="Ккал (авто)" inputMode="decimal"
                value={mpCal} onChange={e => setMpCal(e.target.value.slice(0, 6))} />
              <input style={styles.input} placeholder="Белки, г" inputMode="decimal"
                value={mpProt} onChange={e => updateMacro("prot", e.target.value)} />
              <input style={styles.input} placeholder="Жиры, г" inputMode="decimal"
                value={mpFat} onChange={e => updateMacro("fat", e.target.value)} />
              <input style={styles.input} placeholder="Углеводы, г" inputMode="decimal"
                value={mpCarb} onChange={e => updateMacro("carb", e.target.value)} />
            </div>
            <div style={{ fontSize: 13, color: "#7a8a7a", marginTop: 6 }}>
              💡 Калории считаются автоматически из Б/Ж/У. Можно поправить вручную, если на упаковке иначе.
            </div>
            {mpError && <div style={{ color: "#c0392b", fontSize: 15, marginTop: 8 }}>{mpError}</div>}
            {cooldownLeft > 0 && (
              <div style={{ color: "#7a8a7a", fontSize: 15, marginTop: 8 }}>
                ⏳ Проверка в очереди. Следующий продукт через {cooldownLeft} сек.
              </div>
            )}
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button style={{ ...styles.primaryButton, flex: 1, opacity: (mpBusy || cooldownLeft > 0) ? 0.6 : 1 }}
                disabled={mpBusy || cooldownLeft > 0} onClick={() => saveProduct({})}>
                {mpBusy ? "Сохраняю…" : "Сохранить"}
              </button>
              <button style={{ ...styles.secondaryButton, flex: 1 }} onClick={resetMpForm}>
                Отмена
              </button>
            </div>
          </div>
        )}

        {/* Форма редактирования своего продукта */}
        {mpMode === "edit" && (
          <div style={{ ...styles.card, padding: 16, marginBottom: 14 }}>
            <div style={{ fontSize: 16, fontWeight: 600, color: "#1c2a1c", marginBottom: 10 }}>
              <UiIcon name="pencil" size={19} style={{ marginRight: 6 }} />Редактировать «{mpName}» (на 100 г)
            </div>
            <input style={{ ...styles.input, marginBottom: 8 }} placeholder="Заметка"
              value={mpNote} onChange={e => setMpNote(e.target.value.slice(0, 200))} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <input style={styles.input} placeholder="Ккал (авто)" inputMode="decimal"
                value={mpCal} onChange={e => setMpCal(e.target.value.slice(0, 6))} />
              <input style={styles.input} placeholder="Белки, г" inputMode="decimal"
                value={mpProt} onChange={e => updateMacro("prot", e.target.value)} />
              <input style={styles.input} placeholder="Жиры, г" inputMode="decimal"
                value={mpFat} onChange={e => updateMacro("fat", e.target.value)} />
              <input style={styles.input} placeholder="Углеводы, г" inputMode="decimal"
                value={mpCarb} onChange={e => updateMacro("carb", e.target.value)} />
            </div>
            {mpError && <div style={{ color: "#c0392b", fontSize: 15, marginTop: 8 }}>{mpError}</div>}
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button style={{ ...styles.primaryButton, flex: 1, opacity: mpBusy ? 0.6 : 1 }}
                disabled={mpBusy} onClick={saveEditProduct}>
                {mpBusy ? "Сохраняю…" : "Сохранить"}
              </button>
              <button style={{ ...styles.secondaryButton, flex: 1 }} onClick={resetMpForm}>
                Отмена
              </button>
            </div>
          </div>
        )}

        {/* Диалог: продукт найден в базе → сохранить себе? */}
        {mpFound && (
          <div style={{ ...styles.card, padding: 16, marginBottom: 14, border: "1.5px solid #4caf50" }}>
            <div style={{ fontSize: 16, fontWeight: 600, color: "#1c2a1c", marginBottom: 6 }}>
              Такой продукт уже есть в базе
            </div>
            <div style={{ fontSize: 15, color: "#1c241b", marginBottom: 4 }}>
              {mpFound.emoji} {mpFound.name} — {mpFound.calories_per_100g} ккал (на 100 г)
            </div>
            <div style={{ fontSize: 14, color: "#7a8a7a", marginBottom: 12, lineHeight: 1.4 }}>
              Сохранить его в ваши продукты? Или создать свой вариант с вашими КБЖУ.
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <button style={styles.primaryButton}
                disabled={mpBusy}
                onClick={() => { setMpFound(null); saveProduct({ confirmExisting: true }); }}>
                ✓ Сохранить из базы
              </button>
              <button style={styles.secondaryButton}
                disabled={mpBusy}
                onClick={() => { setMpFound(null); saveProduct({ forceNew: true }); }}>
                Создать свой вариант (мои КБЖУ)
              </button>
              <button style={{ ...styles.secondaryButton, opacity: 0.7 }} onClick={resetMpForm}>
                Отмена
              </button>
            </div>
          </div>
        )}

        {/* Список моих продуктов */}
        {myProductsLoading ? (
          <div style={{ textAlign: "center", color: "#7a8a7a", padding: 20 }}>Загрузка…</div>
        ) : myProducts.length === 0 ? (
          <div style={{ ...styles.card, padding: 20, textAlign: "center", color: "#7a8a7a" }}>
            Пока пусто. Добавьте первый продукт выше 👆
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {myProducts.map((p) => (
              <div key={p.id} style={{ ...styles.card, padding: 14 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                  <span style={{ fontSize: 26 }}>{p.emoji || "🥗"}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 17, fontWeight: 600, color: "#1c2a1c" }}>{p.name}</div>
                    <div style={{ fontSize: 14, color: "#7a8a7a" }}>
                      {p.calories_per_100g} ккал · Б {p.proteins_g} · Ж {p.fats_g} · У {p.carbs_g} (на 100 г)
                    </div>
                  </div>
                </div>
                {/* личная заметка */}
                {p.note && (
                  <div style={{ fontSize: 14, color: "#4a5c4a", fontStyle: "italic", marginBottom: 8,
                                background: "#f6faf6", borderRadius: 10, padding: "7px 11px" }}>
                    <UiIcon name="note" size={16} style={{ marginRight: 5 }} />{p.note}
                  </div>
                )}
                {/* Статус продукта по is_public (не по source). Прошёл проверку → нейтрально;
                    треш (виден только автору) → честная пометка почему репост недоступен. */}
                {p.is_public === false ? (
                  <div style={{ fontSize: 13, color: "#c0392b", marginBottom: 8 }}>
                    ⚠️ Виден только вам — не прошёл проверку (рецепты с ним нельзя репостить)
                  </div>
                ) : (p.source === "user" || p.source === "giga" || p.source === "giga_recipe") ? (
                  <div style={{ fontSize: 13, color: "#4caf50", marginBottom: 8 }}>
                    ✓ В базе
                  </div>
                ) : null}
                {/* чипы фишек */}
                {(p.bread_units != null || p.glycemic_index != null) && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                    {p.bread_units != null && (
                      <span style={styles.healthTag}>{p.bread_units} ХЕ</span>
                    )}
                    {p.glycemic_index != null && (
                      <span style={styles.healthTag}>ГИ {p.glycemic_index}</span>
                    )}
                  </div>
                )}
                <div style={{ display: "flex", gap: 8 }}>
                  <button style={{ ...styles.secondaryButton, flex: 1, fontSize: 15 }}
                    onClick={() => cookFromProduct(p.name)} className="povar-primary">
                    🍳 Приготовить
                  </button>
                  {p.editable && (
                    <button
                      style={{
                        background: "#fff", border: "none", borderRadius: 14,
                        padding: "10px 14px", cursor: "pointer", fontSize: 15, color: "#9a6a1e",
                        fontFamily: "inherit", boxShadow: "0 1px 2px rgba(28,36,27,0.05), 0 3px 10px rgba(28,36,27,0.07)",
                      }}
                      onClick={() => startEditProduct(p)}
                      title="Редактировать КБЖУ">
                      <UiIcon name="pencil" size={22} />
                    </button>
                  )}
                  <button
                    style={{
                      background: "#fff", border: "none", borderRadius: 14,
                      padding: "10px 14px", cursor: "pointer", fontSize: 15, color: "#c0392b",
                      fontFamily: "inherit", boxShadow: "0 1px 2px rgba(28,36,27,0.05), 0 3px 10px rgba(28,36,27,0.07)",
                    }}
                    onClick={() => removeMyProduct(p.id)}
                    title="Убрать из моих продуктов">
                    <UiIcon name="trash" size={22} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Дисклеймер */}
        <div style={{ ...styles.healthDisclaimer, marginTop: 14, marginBottom: 12 }}>
          Данные из открытых источников и от ИИ, носят справочный характер.
          При диабете и заболеваниях консультируйтесь с врачом.
        </div>

        <Toast {...(toast || {})} onClose={() => setToast(null)} />
        <FloatingBack onClick={() => setScreen("main")} />
      </div>
    );
  }

  if (screen === "editor" && editRecipe) {
    const e = editRecipe;
    // Пул для автокомплита ингредиентов: общая база + ЛИЧНЫЕ продукты юзера (помечены _mine).
    // Чтоб юзер мог выбрать свой любимый продукт из-под руки, а не только из общей базы.
    const _baseNamesLc = new Set(productsBase.map(p => (p.name || "").toLowerCase()));
    const productPool = [
      ...productsBase,
      ...myProducts
        .filter(p => p.name && !_baseNamesLc.has((p.name || "").toLowerCase()))
        .map(p => ({ ...p, _mine: true })),
    ];
    // есть ли в рецепте продукт, которого нет в базе (его КБЖУ не учтётся, пока не добавят/опубликуют)
    const _poolNamesLc = new Set(productPool.map(p => (p.name || "").toLowerCase()));
    const hasUnknownIngredient = (e.ingredients || []).some(ing => {
      const nm = (ing.name || "").trim().toLowerCase();
      return nm && !_poolNamesLc.has(nm);
    });
    return (
      <div style={styles.page}>
        <div style={styles.header}>
          <button style={styles.headerBack} onClick={() => { setEditRecipe(null); setScreen("book"); }}>‹</button>
          <div style={{ flex: 1 }}>
            <div style={styles.headerTitle}>{e.id == null ? "Создать рецепт" : "Редактор рецепта"}</div>
            <div style={styles.headerSubtitle}>{e.id == null ? "Запишите свой рецепт" : "Меняйте что угодно — КБЖУ пересчитается"}</div>
          </div>
        </div>

        {/* Название */}
        <div style={{ ...styles.card, padding: 16, marginBottom: 12 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: "#1c2a1c", marginBottom: 6 }}>Название</div>
          <input style={styles.input} placeholder="Название рецепта"
            value={e.name} onChange={ev => setEditRecipe(prev => ({ ...prev, name: ev.target.value.slice(0, 150) }))} />
        </div>

        {/* Фото блюда (только при редактировании существующего; у нового рецепта нет id) */}
        {e.id != null && (
        <div style={{ ...styles.card, padding: 16, marginBottom: 12 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: "#1c2a1c", marginBottom: 10 }}>📷 Фото блюда</div>
          {e.photo_url ? (
            <div>
              <img src={e.photo_url} alt="Фото блюда"
                style={{ width: "100%", borderRadius: 12, marginBottom: 10, maxHeight: 280, objectFit: "cover" }} />
              <div style={{ display: "flex", gap: 8 }}>
                <label style={{ ...styles.secondaryButton, flex: 1, textAlign: "center", cursor: photoUploading ? "wait" : "pointer", opacity: photoUploading ? 0.6 : 1 }}>
                  {photoUploading ? "Загружаю…" : "Заменить"}
                  <input type="file" accept="image/*" style={{ display: "none" }} disabled={photoUploading}
                    onChange={ev => { const f = ev.target.files && ev.target.files[0]; if (f) uploadRecipePhoto(e.id, f); ev.target.value = ""; }} />
                </label>
                <button className="povar-press" disabled={photoUploading}
                  onClick={() => removeRecipePhoto(e.id)}
                  style={{ flex: 1, background: "#fff", border: "1px solid #e8c1c1", borderRadius: 10,
                           padding: "10px", cursor: "pointer", color: "#c0392b", fontSize: 16 }}>
                  Удалить фото
                </button>
              </div>
            </div>
          ) : (
            <label style={{ ...styles.secondaryButton, width: "100%", textAlign: "center", display: "block",
                            cursor: photoUploading ? "wait" : "pointer", opacity: photoUploading ? 0.6 : 1 }}>
              {photoUploading ? "Загружаю…" : "📷 Добавить фото блюда"}
              <input type="file" accept="image/*" style={{ display: "none" }} disabled={photoUploading}
                onChange={ev => { const f = ev.target.files && ev.target.files[0]; if (f) uploadRecipePhoto(e.id, f); ev.target.value = ""; }} />
            </label>
          )}
          <div style={{ fontSize: 13, color: "#8a9a8a", marginTop: 8, lineHeight: 1.4 }}>
            Фото хранится во ВКонтакте. Покажите, что у вас получилось!
          </div>
        </div>
        )}

        {/* Ингредиенты */}
        <div style={{ ...styles.card, padding: 16, marginBottom: 12 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: "#1c2a1c", marginBottom: 10 }}>🧺 Ингредиенты</div>
          {hasUnknownIngredient && (
            <div style={{ fontSize: 14, color: "#7a8a7a", background: "#f2f8f2", border: "1px solid #f0d0a8",
                          borderRadius: 8, padding: "8px 10px", marginBottom: 10, lineHeight: 1.45 }}>
              ℹ️ Калории считаются по продуктам из базы. Новый продукт (которого нет в базе) пока не учтён в КБЖУ — он добавится с калориями, когда нажмёшь «➕ добавить продукт» или опубликуешь рецепт в общую базу (при публикации продукты пройдут модерацию и получат КБЖУ).
            </div>
          )}
          {e.ingredients.map((ing, idx) => {
            const q = (ing.name || "").trim().toLowerCase();
            const suggestions = (ingredientFocusIdx === idx && q.length >= 1)
              ? productPool.filter(p => {
                  const nm = (p.name || "").toLowerCase();
                  const al = (p.aliases || "").toLowerCase();
                  return (nm.includes(q) || al.includes(q)) && nm !== q;
                }).slice(0, 6)
              : [];
            const exactInBase = productPool.some(p => (p.name || "").toLowerCase() === q);
            return (
            <div key={idx} style={{ marginBottom: 8 }}>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <div style={{ flex: 2, position: "relative" }}>
                  <input style={{ ...styles.input, width: "100%", marginBottom: 0, boxSizing: "border-box" }} placeholder="Продукт"
                    value={ing.name}
                    onChange={ev => editSetIngredient(idx, "name", ev.target.value.slice(0, 60))}
                    onFocus={() => { setIngredientFocusIdx(idx); loadProductsBase(); if (!myProducts.length && !myProductsLoading) loadMyProducts(); }}
                    onBlur={() => setTimeout(() => setIngredientFocusIdx(f => f === idx ? -1 : f), 200)} />
                  {suggestions.length > 0 && (
                    <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 20,
                                  background: "#fff", border: "1px solid #cfe0cf", borderRadius: 8,
                                  marginTop: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.12)", overflow: "hidden" }}>
                      {suggestions.map((p, si) => (
                        <button key={si} className="povar-press"
                          onMouseDown={ev => { ev.preventDefault(); editSetIngredient(idx, "name", p.name); setIngredientFocusIdx(-1); }}
                          style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "8px 10px",
                                   background: "#fff", border: "none", borderBottom: "1px solid #f3ebe0",
                                   cursor: "pointer", textAlign: "left", fontSize: 16, color: "#1c2a1c" }}>
                          <ProdIcon img={p.image_url} emoji={p.emoji} size={20} />
                          <span style={{ flex: 1 }}>{p.name}</span>
                          {p._mine && (
                            <span style={{ fontSize: 12, color: "#2e7d32", background: "#eaf6ea",
                                           borderRadius: 6, padding: "2px 6px", marginRight: 4 }}>мой</span>
                          )}
                          <span style={{ fontSize: 13, color: "#8a9a8a" }}>{p.calories_per_100g} ккал</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <input style={{ ...styles.input, flex: 1, marginBottom: 0 }} placeholder="200 г · 2 шт"
                  value={ing.amount} onChange={ev => editSetIngredient(idx, "amount", ev.target.value.slice(0, 30))} />
                <button className="povar-press" onClick={() => editRemoveIngredient(idx)}
                  style={{ background: "#fff", border: "1px solid #e8c1c1", borderRadius: 10, padding: "8px 10px",
                           cursor: "pointer", color: "#c0392b", fontSize: 16 }}>✕</button>
              </div>
              {/* подсказка добавить продукт если введён текст, но его нет в базе */}
              {ingredientFocusIdx === idx && q.length >= 2 && !exactInBase && suggestions.length === 0 && (
                <button className="povar-press"
                  onMouseDown={ev => { ev.preventDefault(); openAddProduct(ing.name.trim()); }}
                  style={{ marginTop: 4, fontSize: 14, color: "#2e7d32", background: "#eaf6ea",
                           border: "1px solid #a5d6a7", borderRadius: 8, padding: "6px 10px", cursor: "pointer" }}>
                  ➕ «{ing.name.trim()}» нет в базе — добавить продукт
                </button>
              )}
            </div>
            );
          })}
          <button className="povar-press" onClick={editAddIngredient}
            style={{ ...styles.secondaryButton, width: "100%", marginTop: 4 }}>+ Добавить ингредиент</button>
        </div>

        {/* Шаги */}
        <div style={{ ...styles.card, padding: 16, marginBottom: 12 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: "#1c2a1c", marginBottom: 10, display: "flex", alignItems: "center", gap: 7 }}><UiIcon name="pan_cooking" size={22} />Как готовить</div>
          {e.steps.map((step, idx) => (
            <div key={idx}>
              <div style={{ display: "flex", gap: 6, marginBottom: 6, alignItems: "flex-start" }}>
                <div style={{ background: "#0f6e56", color: "#fff", borderRadius: "50%", minWidth: 24, height: 24,
                              display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, marginTop: 8 }}>{idx + 1}</div>
                <textarea style={{ ...styles.textarea, flex: 1, minHeight: 44, marginBottom: 0, overflow: "hidden" }} placeholder="Описание шага"
                  ref={autoGrow}
                  value={step} onChange={ev => { editSetStep(idx, ev.target.value.slice(0, 1000)); autoGrow(ev.target); }} />
                <div style={{ display: "flex", flexDirection: "column", gap: 7, marginTop: 4 }}>
                  <button className="povar-press"
                    onClick={() => sttToggle(`step-${idx}`)}
                    disabled={sttState !== null && sttState.key !== `step-${idx}`}
                    title="Надиктовать шаг"
                    style={{
                      border: "none", borderRadius: 12, width: 42, height: 42, padding: 0, fontSize: 18, cursor: "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      background: sttState && sttState.key === `step-${idx}` && sttState.phase === "rec"
                        ? "linear-gradient(145deg,#d94b3f,#bd2a2a)"
                        : sttState && sttState.key === `step-${idx}`
                        ? "linear-gradient(145deg,#fdf3e0,#fae8cb)"
                        : "linear-gradient(145deg,#e2f3ec,#d0ebe0)",
                      color: sttState && sttState.key === `step-${idx}` && sttState.phase === "rec" ? "#fff" : "#0f6e56",
                      boxShadow: sttState && sttState.key === `step-${idx}` && sttState.phase === "rec"
                        ? `0 0 0 ${3 + Math.round(sttLevel * 12)}px rgba(217,75,63,${0.18 + sttLevel * 0.25})` : "inset 0 1px 1px rgba(255,255,255,0.7)",
                      transform: sttState && sttState.key === `step-${idx}` && sttState.phase === "rec" ? `scale(${1 + sttLevel * 0.22})` : "none",
                      transition: "box-shadow 0.09s linear, transform 0.09s linear",
                    }}>
                    {sttState && sttState.key === `step-${idx}`
                      ? (sttState.phase === "rec" ? "⏹" : "…")
                      : <UiIcon name="microphone" size={26} />}
                  </button>
                  <button className="povar-press" onClick={() => editMoveStep(idx, -1)} disabled={idx === 0} title="Выше"
                    style={{ background: "#fff", border: "none", borderRadius: 12, width: 42, height: 42, padding: 0,
                             display: "flex", alignItems: "center", justifyContent: "center",
                             boxShadow: "0 1px 2px rgba(28,36,27,0.05), 0 3px 8px rgba(28,36,27,0.07)",
                             cursor: idx === 0 ? "default" : "pointer", color: "#7a8a7a", fontSize: 19, fontWeight: 700,
                             opacity: idx === 0 ? 0.3 : 1, lineHeight: 1 }}>↑</button>
                  <button className="povar-press" onClick={() => editMoveStep(idx, 1)} disabled={idx === e.steps.length - 1} title="Ниже"
                    style={{ background: "#fff", border: "none", borderRadius: 12, width: 42, height: 42, padding: 0,
                             display: "flex", alignItems: "center", justifyContent: "center",
                             boxShadow: "0 1px 2px rgba(28,36,27,0.05), 0 3px 8px rgba(28,36,27,0.07)",
                             cursor: idx === e.steps.length - 1 ? "default" : "pointer", color: "#7a8a7a", fontSize: 19, fontWeight: 700,
                             opacity: idx === e.steps.length - 1 ? 0.3 : 1, lineHeight: 1 }}>↓</button>
                  <button className="povar-press" onClick={() => editRemoveStep(idx)} title="Удалить шаг"
                    style={{ background: "#fff", border: "none", borderRadius: 12, width: 42, height: 42, padding: 0,
                             display: "flex", alignItems: "center", justifyContent: "center",
                             boxShadow: "0 1px 2px rgba(28,36,27,0.05), 0 3px 8px rgba(28,36,27,0.07)",
                             cursor: "pointer", color: "#c0392b", fontSize: 17, lineHeight: 1 }}>✕</button>
                </div>
              </div>
              <div style={{ marginLeft: 30, marginBottom: 8 }}>
                {(e.step_photos || [])[idx] ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <img src={e.step_photos[idx]} alt={`шаг ${idx + 1}`}
                      style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 8, border: "1px solid #cfe0cf" }} />
                    <button className="povar-press" onClick={() => removeStepPhoto(idx)}
                      style={{ fontSize: 14, color: "#c0392b", background: "#fff", border: "1px solid #e8c1c1",
                               borderRadius: 8, padding: "5px 10px", cursor: "pointer" }}>Убрать фото</button>
                  </div>
                ) : (
                  <label style={{ display: "inline-block", fontSize: 14, color: "#8a6d4f", background: "#faf3e9",
                                  border: "1px dashed #d8c4ac", borderRadius: 8, padding: "5px 10px",
                                  cursor: stepPhotoUploading === idx ? "wait" : "pointer", opacity: stepPhotoUploading === idx ? 0.6 : 1 }}>
                    {stepPhotoUploading === idx ? "Загружаю…" : "📷 Фото шага"}
                    <input type="file" accept="image/*" style={{ display: "none" }} disabled={stepPhotoUploading >= 0}
                      onChange={ev => { const f = ev.target.files && ev.target.files[0]; if (f) uploadStepPhoto(idx, f); ev.target.value = ""; }} />
                  </label>
                )}
              </div>
              {idx < e.steps.length - 1 && (
                <button className="povar-press" onClick={() => editInsertStepAt(idx)}
                  style={{ width: "100%", marginBottom: 8, fontSize: 13, color: "#8a9a8a", background: "transparent",
                           border: "1px dashed #d8c4ac", borderRadius: 8, padding: "4px 0", cursor: "pointer" }}>
                  + вставить шаг сюда
                </button>
              )}
            </div>
          ))}
          <button className="povar-press" onClick={editAddStep}
            style={{ ...styles.secondaryButton, width: "100%", marginTop: 4 }}>+ Добавить шаг</button>
        </div>

        {/* Заметка */}
        <div style={{ ...styles.card, padding: 16, marginBottom: 12 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: "#1c2a1c", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}><UiIcon name="note" size={19} />Ваше примечание</div>
          <textarea style={{ ...styles.textarea, minHeight: 50, overflow: "hidden" }} placeholder="Например: добавляю больше специй"
            ref={autoGrow}
            value={e.note} onChange={ev => { setEditRecipe(prev => ({ ...prev, note: ev.target.value.slice(0, 300) })); autoGrow(ev.target); }} />
        </div>

        {/* Кнопки */}
        <div style={{ display: "flex", gap: 8, marginBottom: e.id != null && e.recipe_type === "own" ? 8 : 20 }}>
          <button style={{ ...styles.primaryButton, flex: 1, opacity: editBusy ? 0.6 : 1 }} className="povar-primary"
            onClick={onEditorSave} disabled={editBusy}>
            {editBusy ? "Сохраняю…" : "💾 Сохранить"}
          </button>
          <button style={{ ...styles.secondaryButton, flex: 1 }} className="povar-press"
            onClick={() => { setEditRecipe(null); setScreen("book"); }} disabled={editBusy}>
            Отмена
          </button>
        </div>
        {e.id != null && e.recipe_type === "own" && (
          <button style={{ ...styles.primaryButton, width: "100%", marginBottom: 20, background: "#2e7d32", opacity: editBusy ? 0.6 : 1 }}
            className="povar-press" onClick={() => saveEditedRecipe(false, true)} disabled={editBusy}>
            <UiIcon name="megaphone" size={20} style={{ marginRight: 7 }} />Сохранить и поделиться в общую базу
          </button>
        )}

        {/* Модалка выбора: заменить / новый (только для ai-рецептов) */}
        {editAskMode && (
          <div style={styles.modalOverlay} className="povar-fade" onClick={() => setEditAskMode(false)}>
            <div style={styles.modalCard} className="povar-pop" onClick={ev => ev.stopPropagation()}>
              <div style={styles.modalTitle}>Как сохранить?</div>
              <div style={styles.modalText}>Это рецепт от ИИ. Заменить его вашей версией или сохранить как новый отдельный рецепт?</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 6 }}>
                <button style={styles.modalBtnInfo} className="povar-press" disabled={editBusy}
                  onClick={() => saveEditedRecipe(false)}>Заменить текущий</button>
                <button style={{ ...styles.modalBtnInfo, background: "#4caf50" }} className="povar-press" disabled={editBusy}
                  onClick={() => saveEditedRecipe(true)}>Сохранить как новый</button>
                <button style={styles.modalBtnCancel} className="povar-press" onClick={() => setEditAskMode(false)}>Отмена</button>
              </div>
            </div>
          </div>
        )}

        <Toast {...(toast || {})} onClose={() => setToast(null)} />
        <InfoModal {...(info || {})} onClose={info?.onClose || (() => setInfo(null))} />
        {addProductModal && (
          <div style={styles.modalOverlay} className="povar-fade" onClick={() => !addProductBusy && setAddProductModal(null)}>
            <div style={styles.modalCard} className="povar-pop" onClick={ev => ev.stopPropagation()}>
              <div style={styles.modalTitle}>{addProductData.emoji || "🥗"} Добавить продукт</div>
              <div style={styles.modalText}>«{addProductModal.name}» добавится в ваши продукты и общую базу. Проверьте КБЖУ на 100 г.</div>
              {addProductBusy && !addProductData.calories_per_100g ? (
                <div style={{ textAlign: "center", padding: 16, color: "#8a9a8a" }}>Спрашиваю у ИИ КБЖУ…</div>
              ) : (
                <div style={{ marginTop: 10 }}>
                  <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, color: "#8a9a8a", marginBottom: 2 }}>Калории</div>
                      <input style={styles.input} type="number" inputMode="decimal" placeholder="ккал"
                        value={addProductData.calories_per_100g}
                        onChange={ev => setAddProductData({ ...addProductData, calories_per_100g: ev.target.value })} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, color: "#8a9a8a", marginBottom: 2 }}>Белки</div>
                      <input style={styles.input} type="number" inputMode="decimal" placeholder="г"
                        value={addProductData.proteins_g}
                        onChange={ev => setAddProductData({ ...addProductData, proteins_g: ev.target.value })} />
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, color: "#8a9a8a", marginBottom: 2 }}>Жиры</div>
                      <input style={styles.input} type="number" inputMode="decimal" placeholder="г"
                        value={addProductData.fats_g}
                        onChange={ev => setAddProductData({ ...addProductData, fats_g: ev.target.value })} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, color: "#8a9a8a", marginBottom: 2 }}>Углеводы</div>
                      <input style={styles.input} type="number" inputMode="decimal" placeholder="г"
                        value={addProductData.carbs_g}
                        onChange={ev => setAddProductData({ ...addProductData, carbs_g: ev.target.value })} />
                    </div>
                  </div>
                </div>
              )}
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
                <button style={styles.modalBtnInfo} className="povar-press" disabled={addProductBusy}
                  onClick={() => submitAddProduct()}>
                  {addProductBusy ? "Сохраняю…" : (addProductModal.fromReplace ? "Добавить и заменить" : "Добавить продукт")}
                </button>
                <button style={styles.modalBtnCancel} className="povar-press" disabled={addProductBusy}
                  onClick={() => setAddProductModal(null)}>Отмена</button>
              </div>
            </div>
          </div>
        )}
        <FloatingBack onClick={() => { setEditRecipe(null); setScreen("book"); }} />
      </div>
    );
  }

  if (screen === "recipes_base") {
    const typeBadgeOf = (t) => t === "edited" ? { label: "✏️ Изменён", color: "#9a6a1e", bg: "linear-gradient(145deg,#fdf3e0,#fae8cb)" }
      : t === "own" ? { label: "👨‍🍳 Свой", color: "#2e7d32", bg: "linear-gradient(145deg,#e6f4e6,#d7ecd7)" }
      : { label: "🤖 ИИ", color: "#5b4fc0", bg: "linear-gradient(145deg,#f0eefe,#e4e0fb)" };
    // маппинг категорий: ключ → {emoji, label}. Порядок = порядок чипов.
    const CATEGORY_LABELS = {
      chicken: { emoji: "🍗", label: "Курица" },
      meat: { emoji: "🥩", label: "Мясо" },
      fish: { emoji: "🐟", label: "Рыба" },
      potato: { emoji: "🥔", label: "Картошка" },
      pasta: { emoji: "🍝", label: "Макароны" },
      egg: { emoji: "🥚", label: "Яйца" },
      cottage: { emoji: "🧀", label: "Творог" },
      mushroom: { emoji: "🍄", label: "Грибы" },
      rice: { emoji: "🍚", label: "Рис" },
      vegetables: { emoji: "🥬", label: "Овощи" },
      soup: { emoji: "🍲", label: "Супы" },
      salad: { emoji: "🥗", label: "Салаты" },
      snack: { emoji: "🥪", label: "Закуски" },
      porridge: { emoji: "🥣", label: "Каши" },
      breakfast: { emoji: "🥞", label: "Завтраки" },
      baking: { emoji: "🍰", label: "Выпечка" },
      main: { emoji: "🍖", label: "Основные" },
      vegetarian: { emoji: "🥦", label: "Вегетарианские" },
      lenten: { emoji: "🌱", label: "Постные" },
      lean: { emoji: "🪶", label: "Нежирные" },
      quick: { emoji: "⚡", label: "Быстрые" },
    };
    // собираем какие категории реально есть в загруженных рецептах (с подсчётом)
    const catCounts = {};
    publicRecipes.forEach(item => {
      (item.categories || []).forEach(c => { catCounts[c] = (catCounts[c] || 0) + 1; });
    });
    // чипы только для присутствующих категорий, в порядке CATEGORY_LABELS
    const availableCats = Object.keys(CATEGORY_LABELS).filter(c => catCounts[c]);
    // фильтрация по выбранной категории
    const filtered = selectedCategory
      ? publicRecipes.filter(item => (item.categories || []).includes(selectedCategory))
      : publicRecipes;
    return (
      <div style={styles.page}>
        <div style={styles.header}>
          <button style={styles.headerBack} onClick={() => setScreen("book")}>‹</button>
          <div style={{ flex: 1 }}>
            <div style={styles.headerTitle}>База рецептов</div>
            <div style={styles.headerSubtitle}>{publicRecipes.length > 0 ? `${publicRecipes.length} ${ruRecipesWord(publicRecipes.length)} от сообщества` : "Рецепты от всех пользователей"}</div>
          </div>
        </div>
        {/* Чипы-фильтры — бегущая строка (CSS-анимация, плавно). Тап = фильтр, наведение/касание = пауза. */}
        {publicRecipesLoaded && availableCats.length > 0 && (() => {
          const renderChip = (c, keyPrefix) => {
            const chipBase = { flexShrink: 0, padding: "8px 15px", borderRadius: 20, fontSize: 15, fontWeight: 600,
                               cursor: "pointer", whiteSpace: "nowrap", border: "none", fontFamily: "inherit" };
            const chipOff = { ...chipBase, background: "#fff", color: "#4a5c4a",
                              boxShadow: "0 1px 2px rgba(28,36,27,0.05), 0 3px 10px rgba(28,36,27,0.07)" };
            const chipOn = { ...chipBase, background: "linear-gradient(145deg,#159872,#0c5a46)", color: "#fff",
                             boxShadow: "inset 0 1px 1px rgba(255,255,255,0.25), 0 4px 12px rgba(15,110,86,0.28)" };
            if (c === "__all__") {
              const act = selectedCategory === "";
              return (
                <button key={keyPrefix + "all"} onClick={() => setSelectedCategory("")} className="povar-chip"
                  style={act ? chipOn : chipOff}>
                  📋 Все
                </button>
              );
            }
            const cl = CATEGORY_LABELS[c];
            const act = selectedCategory === c;
            return (
              <button key={keyPrefix + c} onClick={() => setSelectedCategory(act ? "" : c)} className="povar-chip"
                style={act ? chipOn : chipOff}>
                {cl.emoji} {cl.label} <span style={{ opacity: 0.7, fontWeight: 400 }}>{catCounts[c]}</span>
              </button>
            );
          };
          const chipKeys = ["__all__", ...availableCats];
          const loop = chipKeys.length >= 5;  // дублируем ленту только когда она заведомо шире экрана
          return (
            <div ref={chipsVpRef} className="povar-chips-viewport"
              style={{ marginBottom: 10, overflowX: loop ? "hidden" : "auto" }}>
              <div ref={chipsTrackRef} data-loop={loop ? "1" : "0"}
                style={{ display: "flex", gap: 8, willChange: "transform", width: "max-content" }}>
                {loop ? chipKeys.map(c => renderChip(c, "")).concat(chipKeys.map(c => renderChip(c, "b"))) : chipKeys.map(c => renderChip(c, ""))}
              </div>
            </div>
          );
        })()}
        {!publicRecipesLoaded && (
          <div style={{ ...styles.card, textAlign: "center", padding: 24, color: "#4a5c4a" }}>Загружаю...</div>
        )}
        {publicRecipesLoaded && publicRecipes.length === 0 && (
          <div style={{ ...styles.card, textAlign: "center", padding: "40px 22px" }}>
            <div style={{ fontSize: 64, marginBottom: 14, opacity: 0.65 }}>🍲</div>
            <div style={styles.slideTitle}>Пока пусто</div>
            <div style={styles.slideText}>Никто ещё не поделился рецептом. Будьте первым — откройте свой рецепт в книге и нажмите «Поделиться»!</div>
          </div>
        )}
        {publicRecipesLoaded && publicRecipes.length > 0 && filtered.length === 0 && (
          <div style={{ ...styles.card, textAlign: "center", padding: "30px 22px" }}>
            <div style={{ fontSize: 48, marginBottom: 10, opacity: 0.6 }}>🔍</div>
            <div style={styles.slideText}>В этой категории пока нет рецептов.</div>
          </div>
        )}
        {publicRecipesLoaded && filtered.map((item, i) => {
          const badge = typeBadgeOf(item.recipe_type);
          return (
            <div key={item.id} style={styles.savedCardWrap} className="povar-fade">
              <button style={styles.savedCard} className="povar-tap" onClick={() => openPublicRecipe(item.id)}>
                {item.photo_url
                  ? <img src={item.photo_url.startsWith("/") ? `${API}${item.photo_url}` : item.photo_url} alt="" style={{ width: 48, height: 48, borderRadius: 10, objectFit: "cover", flexShrink: 0 }} />
                  : <div style={styles.savedCardEmoji}>{recipeEmoji(item.name)}</div>}
                <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                  <div style={styles.savedCardTitle}>{item.name}</div>
                  <div style={{ display: "inline-block", fontSize: 12, fontWeight: 600, color: badge.color, background: badge.bg,
                                borderRadius: 9, padding: "3px 9px", marginTop: 4, boxShadow: "inset 0 1px 1px rgba(255,255,255,0.7)" }}>
                    {badge.label}
                  </div>
                  <div style={styles.savedCardMeta}>
                    {item.author_name ? `от ${item.author_name}` : "от сообщества"}
                    {item.calories_per_100g > 0 ? ` · ${item.calories_per_100g} ккал/100г` : (item.calories_per_serving > 0 ? ` · ${item.calories_per_serving} ккал/порц` : "")}
                    {item.cooked_count > 0 && ` · 👨‍🍳 ${item.cooked_count}`}
                    {item.likes_count > 0 && ` · ❤️ ${item.likes_count}`}
                  </div>
                </div>
                <div style={{ fontSize: 18, color: "#0f6e56" }}>›</div>
              </button>
            </div>
          );
        })}
        <Toast {...(toast || {})} onClose={() => setToast(null)} />
        <InfoModal {...(info || {})} onClose={info?.onClose || (() => setInfo(null))} />
        <FloatingBack onClick={() => setScreen("book")} />
      </div>
    );
  }

  if (screen === "public_recipe" && publicView) {
    const pv = publicView;
    const rec = pv.recipe || {};
    const steps = rec.steps || [];
    const stepPhotos = rec.step_photos || [];
    const ingredients = rec.ingredients || [];
    const badge = pv.recipe_type === "edited" ? { label: "✏️ Изменён вами", color: "#9a6a1e", bg: "linear-gradient(145deg,#fdf3e0,#fae8cb)" }
      : pv.recipe_type === "own" ? { label: "👨‍🍳 Свой рецепт", color: "#2e7d32", bg: "linear-gradient(145deg,#e6f4e6,#d7ecd7)" }
      : { label: "🤖 Создан ИИ", color: "#5b4fc0", bg: "linear-gradient(145deg,#f0eefe,#e4e0fb)" };
    return (
      <div style={styles.page}>
        <div style={styles.header}>
          <button style={styles.headerBack} onClick={() => setScreen("recipes_base")}>‹</button>
          <div style={{ flex: 1 }}><div style={styles.headerTitle}>{pv.name}</div></div>
        </div>

        {/* Главное фото: ВСЕГДА сгенерированная картинка блюда (image_url). Юзерские фото — только в ленте внизу. */}
        {pv.image_url ? (
          <div style={{ position: "relative", marginBottom: 12 }}>
            <img src={`${API}${pv.image_url}`} alt="Фото блюда" className="povar-fade"
              style={{ width: "100%", borderRadius: 26, aspectRatio: "1 / 1", objectFit: "cover", display: "block", boxShadow: "0 8px 28px rgba(28,36,27,0.16)" }} />
            <div style={{ position: "absolute", bottom: 8, right: 8, fontSize: 12, color: "#fff",
                          background: "rgba(0,0,0,0.45)", borderRadius: 6, padding: "2px 7px", backdropFilter: "blur(2px)" }}>
              иллюстрация ИИ
            </div>
          </div>
        ) : (Array.isArray(pv.photos) && pv.photos.length > 0 ? (
          <img src={pv.photos[0].photo_url} alt="Фото блюда" className="povar-fade"
            style={{ width: "100%", borderRadius: 26, marginBottom: 12, maxHeight: 300, objectFit: "cover", boxShadow: "0 8px 28px rgba(28,36,27,0.16)" }} />
        ) : null)}

        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
          <div style={{ fontSize: 13, color: badge.color, background: badge.bg, borderRadius: 6, padding: "3px 8px" }}>{badge.label}</div>
          {rec.replaced_from && rec.replaced_from.alternative && (
            <div style={{ fontSize: 13, color: "#8a5a00", background: "#faeeda", borderRadius: 6, padding: "3px 8px" }}>
              🔄 с заменой: {rec.replaced_from.alternative}
            </div>
          )}
          {rec.time_minutes > 0 && <div style={{ fontSize: 14, color: "#4a5c4a" }}>⏱ {rec.time_minutes} мин</div>}
          {rec.difficulty && <div style={{ fontSize: 14, color: "#4a5c4a" }}>{rec.difficulty}</div>}
          {pv.author_name && <div style={{ fontSize: 14, color: "#8a9a8a" }}>от {pv.author_name}</div>}
          {pv.cooked_count > 0 && <div style={{ fontSize: 14, color: "#2e7d32" }}>👨‍🍳 {pv.cooked_count} {ruCookedWord(pv.cooked_count)}</div>}
          {pv.saves_count > 0 && <div style={{ fontSize: 14, color: "#7a6a9a" }}>🔖 {pv.saves_count} {ruSavedPeopleWord(pv.saves_count)}</div>}
        </div>

        {/* Лайк рецепта */}
        <button onClick={() => toggleLike(pv.id)} className="povar-press"
          style={{ width: "100%", marginBottom: 10, padding: "12px", borderRadius: 12,
                   border: pv.liked ? "2px solid #e85d75" : "2px solid #e8d5c8",
                   background: pv.liked ? "#fdeef1" : "#fff", cursor: "pointer",
                   display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                   fontSize: 17, fontWeight: 600, color: pv.liked ? "#c0392b" : "#4a5c4a" }}>
          {pv.liked ? "❤️" : "🤍"} {pv.likes_count > 0 ? `${pv.likes_count}` : "Нравится"}
        </button>

        {/* Кнопка сохранить себе */}
        <button onClick={() => savePublicToBook(pv.id)} className="povar-primary"
          style={{ ...styles.primaryButton, width: "100%", marginBottom: 12 }}>
          🔖 Сохранить себе в книгу
        </button>

        {/* КБЖУ */}
        {(rec.calories_per_100g > 0 || rec.calories_per_serving > 0) && (
          <div style={{ ...styles.card, padding: 14, marginBottom: 12, display: "flex", justifyContent: "space-around", textAlign: "center" }}>
            <div><div style={{ fontSize: 18, fontWeight: 700, color: "#0f6e56" }}>{rec.calories_per_100g || rec.calories_per_serving}</div><div style={{ fontSize: 13, color: "#8a9a8a" }}>{rec.calories_per_100g > 0 ? "ккал/100г" : "ккал/порц"}</div></div>
            <div><div style={{ fontSize: 18, fontWeight: 700, color: "#4a5c4a" }}>{rec.proteins_g || 0}</div><div style={{ fontSize: 13, color: "#8a9a8a" }}>белки</div></div>
            <div><div style={{ fontSize: 18, fontWeight: 700, color: "#4a5c4a" }}>{rec.fats_g || 0}</div><div style={{ fontSize: 13, color: "#8a9a8a" }}>жиры</div></div>
            <div><div style={{ fontSize: 18, fontWeight: 700, color: "#4a5c4a" }}>{rec.carbs_g || 0}</div><div style={{ fontSize: 13, color: "#8a9a8a" }}>углев</div></div>
          </div>
        )}

        {/* Ингредиенты */}
        {ingredients.length > 0 && (
          <div style={{ ...styles.card, padding: 16, marginBottom: 12 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: "#1c2a1c", marginBottom: 10 }}>🧺 Ингредиенты</div>
            {ingredients.map((ing, idx) => {
              const nm = typeof ing === "string" ? ing : (ing.name || "");
              const am = typeof ing === "string" ? "" : (ing.amount || "");
              return (
                <div key={idx} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: idx < ingredients.length - 1 ? "1px solid #f0e4d8" : "none" }}>
                  <span onClick={() => openProductFromRecipe(nm)}
                    className="povar-press"
                    style={{ color: "#1c2a1c", fontSize: 16, cursor: "pointer",
                             textDecoration: "underline", textDecorationColor: "#d8b89a", textUnderlineOffset: 3 }}
                    title={`Узнать о продукте «${nm}»`}>
                    {nm}
                  </span>
                  <span style={{ color: "#8a9a8a", fontSize: 16 }}>{am}</span>
                </div>
              );
            })}
          </div>
        )}

        {/* Шаги */}
        {steps.length > 0 && (
          <div style={{ ...styles.card, padding: 16, marginBottom: 12 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: "#1c2a1c", marginBottom: 10, display: "flex", alignItems: "center", gap: 7 }}><UiIcon name="pan_cooking" size={22} />Как готовить</div>
            {steps.map((step, idx) => (
              <div key={idx} style={{ marginBottom: 12 }}>
                <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <div style={{ background: "#0f6e56", color: "#fff", borderRadius: "50%", minWidth: 24, height: 24, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0 }}>{idx + 1}</div>
                  <div style={{ flex: 1, color: "#1c2a1c", fontSize: 16, lineHeight: 1.5 }}>{step}</div>
                </div>
                {stepPhotos[idx] && (
                  <img src={stepPhotos[idx]} alt={`шаг ${idx + 1}`}
                    style={{ display: "block", maxWidth: "100%", borderRadius: 10, margin: "8px 0 0 34px", border: "1px solid #d5e5d5" }} />
                )}
              </div>
            ))}
          </div>
        )}

        {/* Совет «подавайте с...» (tip) — как в личном рецепте */}
        {rec.tip && <div style={styles.tip}>💡 {rec.tip}</div>}

        {/* Заметка автора (user_note) */}
        {rec.user_note && (
          <div style={{ ...styles.card, padding: 14, marginBottom: 12, background: "#f4faf4" }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#4a5c4a", marginBottom: 4, display: "flex", alignItems: "center", gap: 6 }}><UiIcon name="note" size={19} />Примечание автора</div>
            <div style={{ fontSize: 16, color: "#445a44", lineHeight: 1.5 }}>{rec.user_note}</div>
          </div>
        )}

        {/* «Чем полезно» от Гигы */}
        {rec.benefit_note && (
          <div style={{ ...styles.card, padding: 14, marginBottom: 12, background: "#eef7ee", border: "1px solid #cfe6cf" }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#2e6e3a", marginBottom: 4, display: "flex", alignItems: "center", gap: 6 }}><UiIcon name="leaf" size={19} />Чем полезно</div>
            <div style={{ fontSize: 16, color: "#3a5a40", lineHeight: 1.5 }}>{rec.benefit_note}</div>
          </div>
        )}

        {/* Здоровье — теги и дисклеймер (если есть) */}
        {Array.isArray(rec.health_tags) && rec.health_tags.length > 0 && (
          <div style={{ ...styles.card, padding: 14, marginBottom: 12 }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {rec.health_tags.map((tag, idx) => (
                <span key={idx} style={{ ...styles.healthTag }}>{tag}</span>
              ))}
            </div>
            {rec.health_note && (
              <div style={{ fontSize: 14, color: "#445a44", marginTop: 8, lineHeight: 1.4 }}>{rec.health_note}</div>
            )}
            <div style={{ ...styles.healthDisclaimer, marginTop: 8 }}>
              ⚠️ Информация носит справочный характер и не заменяет консультацию врача.
            </div>
          </div>
        )}

        {/* Фото от пользователей этого блюда (галерея вниз) */}
        <div style={{ ...styles.card, padding: 16, marginBottom: 12 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: "#1c2a1c", marginBottom: 10 }}>📷 Фото от пользователей</div>
          {(!Array.isArray(pv.photos) || pv.photos.length === 0) && (
            <div style={{ fontSize: 15, color: "#8a9a8a", marginBottom: 12 }}>Пока никто не делился фото. Приготовили это блюдо? Поделитесь своим!</div>
          )}
          {Array.isArray(pv.photos) && pv.photos.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 12 }}>
              {pv.photos.map((ph, idx) => (
                <div key={ph.id || idx} className="povar-fade">
                  <div style={{ position: "relative" }}>
                    <img src={ph.photo_url} alt="" style={{ width: "100%", borderRadius: 12, maxHeight: 280, objectFit: "cover",
                      opacity: ph.moderation === "rejected" ? 0.45 : 1 }} />
                    {ph.moderation === "pending" && (
                      <div style={{ position: "absolute", top: 8, left: 8, fontSize: 12, fontWeight: 700, color: "#9a6a1e",
                        background: "linear-gradient(145deg,#fdf3e0,#fae8cb)", borderRadius: 9, padding: "4px 10px",
                        boxShadow: "inset 0 1px 1px rgba(255,255,255,0.7), 0 2px 6px rgba(0,0,0,0.15)" }}>
                        ⏳ На проверке — пока видно только вам
                      </div>
                    )}
                    {ph.moderation === "rejected" && (
                      <div style={{ position: "absolute", top: 8, left: 8, fontSize: 12, fontWeight: 700, color: "#b02a2a",
                        background: "linear-gradient(145deg,#fdecea,#f8d9d5)", borderRadius: 9, padding: "4px 10px",
                        boxShadow: "inset 0 1px 1px rgba(255,255,255,0.7), 0 2px 6px rgba(0,0,0,0.15)" }}>
                        ✗ Не прошло проверку{ph.mod_reason ? `: ${ph.mod_reason}` : ""}
                      </div>
                    )}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 6 }}>
                    <div style={{ fontSize: 14, color: "#4a5c4a", fontWeight: 600 }}>📷 {ph.author_name}</div>
                    {ph.author_vk_id === vkId && (
                      <div style={{ display: "flex", gap: 4 }}>
                        <button onClick={() => openEditPhoto(pv.id, ph)} className="povar-press"
                          style={{ fontSize: 14, color: "#5b6e5b", background: "none", border: "none", cursor: "pointer", padding: "2px 6px" }}>
                          <UiIcon name="pencil" size={17} style={{ marginRight: 4 }} />изменить
                        </button>
                        <button onClick={() => deleteGalleryPhoto(pv.id, ph.id)} className="povar-press"
                          style={{ fontSize: 14, color: "#c0392b", background: "none", border: "none", cursor: "pointer", padding: "2px 6px" }}>
                          <UiIcon name="trash" size={17} style={{ marginRight: 4 }} />удалить
                        </button>
                      </div>
                    )}
                  </div>
                  {ph.caption && <div style={{ fontSize: 15, color: "#445a44", marginTop: 2 }}>{ph.caption}</div>}
                </div>
              ))}
            </div>
          )}
          {/* Кнопка добавить своё фото */}
          <label style={{ ...styles.secondaryButton, width: "100%", textAlign: "center", display: "block",
                          cursor: galleryUploading ? "wait" : "pointer", opacity: galleryUploading ? 0.6 : 1 }}>
            {galleryUploading ? "Загружаю…" : "📷 Я готовил это блюдо — добавить фото"}
            <input type="file" accept="image/*" style={{ display: "none" }} disabled={galleryUploading}
              onChange={ev => { const f = ev.target.files && ev.target.files[0]; if (f) addGalleryPhoto(pv.id, f); ev.target.value = ""; }} />
          </label>
        </div>

        <Toast {...(toast || {})} onClose={() => setToast(null)} />
        <InfoModal {...(info || {})} onClose={info?.onClose || (() => setInfo(null))} />
        <ConfirmModal {...(confirm || {})} onCancel={() => setConfirm(null)} />
        {galleryCaptionModal && (
          <div style={styles.modalOverlay} className="povar-fade" onClick={() => setGalleryCaptionModal(null)}>
            <div style={styles.modalCard} className="povar-pop" onClick={ev => ev.stopPropagation()}>
              <div style={styles.modalTitle}>Добавить комментарий?</div>
              <div style={styles.modalText}>Фото загружено! Можете добавить комментарий (необязательно).</div>
              <textarea style={{ ...styles.textarea, minHeight: 50, marginTop: 8 }} placeholder="Например: добавила больше специй"
                value={galleryCaption} onChange={ev => setGalleryCaption(ev.target.value.slice(0, 150))} />
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 6 }}>
                <button style={styles.modalBtnInfo} className="povar-press" onClick={() => submitGalleryCaption()}>Готово</button>
              </div>
            </div>
          </div>
        )}
        {editPhotoModal && (
          <div style={styles.modalOverlay} className="povar-fade" onClick={() => !editPhotoBusy && setEditPhotoModal(null)}>
            <div style={styles.modalCard} className="povar-pop" onClick={ev => ev.stopPropagation()}>
              <div style={styles.modalTitle}>Изменить фото</div>
              <img src={editPhotoModal.currentUrl} alt="" style={{ width: "100%", borderRadius: 10, maxHeight: 180, objectFit: "cover", marginBottom: 8 }} />
              <textarea style={{ ...styles.textarea, minHeight: 50 }} placeholder="Комментарий к фото"
                value={editPhotoCaption} onChange={ev => setEditPhotoCaption(ev.target.value.slice(0, 150))} />
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
                <label style={{ ...styles.secondaryButton, textAlign: "center", display: "block", cursor: editPhotoBusy ? "wait" : "pointer", opacity: editPhotoBusy ? 0.6 : 1 }}>
                  {editPhotoBusy ? "Загружаю…" : "📷 Заменить фото"}
                  <input type="file" accept="image/*" style={{ display: "none" }} disabled={editPhotoBusy}
                    onChange={ev => { const f = ev.target.files && ev.target.files[0]; if (f) editPhotoNewImage(f); ev.target.value = ""; }} />
                </label>
                <button style={styles.modalBtnInfo} className="povar-press" disabled={editPhotoBusy} onClick={() => submitEditPhoto()}>Сохранить комментарий</button>
                <button style={{ ...styles.modalBtnCancel }} className="povar-press" disabled={editPhotoBusy} onClick={() => setEditPhotoModal(null)}>Закрыть</button>
              </div>
            </div>
          </div>
        )}
        <FloatingBack onClick={() => setScreen("recipes_base")} />
      </div>
    );
  }

  if (screen === "book") {
    const count = savedRecipes.length;
    const counterColor = getCounterColor(count);
    return (
      <div style={styles.page}>
        <div style={styles.header}>
          <button style={styles.headerBack} onClick={() => setScreen("main")}>‹</button>
          <div style={{ flex: 1 }}>
            <div style={styles.headerTitle}>{userName ? `${userName}, ваши рецепты` : "Мои рецепты"}</div>
            <div style={{ ...styles.headerSubtitle, color: counterColor, fontWeight: count >= FREE_SAVED_LIMIT - 2 ? 600 : 400 }}>
              {count} / {FREE_SAVED_LIMIT} {ruRecipesWord(count)} сохранено
            </div>
          </div>
        </div>
        {/* Кнопка создать свой рецепт */}
        <button onClick={openCreateOwn} className="povar-tap"
          style={{ width: "100%", marginBottom: 14, padding: "13px 15px", display: "flex", alignItems: "center", gap: 12,
                   background: "#fff", border: "1.5px dashed rgba(15,110,86,0.35)", borderRadius: 26, cursor: "pointer",
                   fontFamily: "inherit", boxShadow: "0 1px 2px rgba(28,36,27,0.05), 0 4px 12px rgba(28,36,27,0.06)" }}>
          <div className="povar-icon" style={{ width: 54, height: 54, background: "linear-gradient(145deg,#fbd28a,#f3b95c)", fontSize: 28 }}>✍️</div>
          <div style={{ flex: 1, textAlign: "left" }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#1c241b", letterSpacing: "-0.2px" }}>Создать свой рецепт</div>
            <div style={{ fontSize: 13, color: "#a1a39f", marginTop: 2, fontWeight: 500 }}>запишите рецепт с нуля</div>
          </div>
          <span style={{ color: "#c9cdc4", fontSize: 18 }}>›</span>
        </button>
        {savedLoading && <div style={{ ...styles.card, textAlign: "center", padding: 24, color: "#4a5c4a" }}>Загружаю...</div>}
        {!savedLoading && count === 0 && (
          <div style={{ ...styles.card, textAlign: "center", padding: "40px 22px" }}>
            <div style={{ fontSize: 64, marginBottom: 14, opacity: 0.65 }}>📖</div>
            <div style={styles.slideTitle}>Пока пусто</div>
            <div style={styles.slideText}>Сохраняйте рецепты, которые понравились</div>
            <button style={{ ...styles.primaryButton, marginTop: 20 }} onClick={() => setScreen("input")}>
              Придумать первый рецепт
            </button>
          </div>
        )}
        {!savedLoading && count > 0 && savedRecipes.map((item, i) => {
          const typeBadge = item.recipe_type === "edited"
            ? { label: "✏️ Изменён вами", color: "#9a6a1e", bg: "linear-gradient(145deg,#fdf3e0,#fae8cb)" }
            : item.recipe_type === "own"
            ? { label: "👨‍🍳 Свой рецепт", color: "#2e7d32", bg: "linear-gradient(145deg,#e6f4e6,#d7ecd7)" }
            : { label: "🤖 Создан ИИ", color: "#5b4fc0", bg: "linear-gradient(145deg,#f0eefe,#e4e0fb)" };
          return (
          <div key={item.id} style={styles.savedCardWrap} className="povar-fade">
            <button style={styles.savedCard} className="povar-tap"
              onClick={() => { if (item.recipe) openRecipe(item.recipe, "book", item.id); }}>
              {item.photo_url
                ? <img src={item.photo_url} alt="" style={{ width: 48, height: 48, borderRadius: 10, objectFit: "cover", flexShrink: 0 }} />
                : item.image_url
                ? <img src={`${API}${item.image_url}`} alt="" style={{ width: 48, height: 48, borderRadius: 10, objectFit: "cover", flexShrink: 0 }} />
                : <div style={styles.savedCardEmoji}>{recipeEmoji(item.name)}</div>}
              <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                <div style={styles.savedCardTitle}>{item.name}</div>
                <div style={{ display: "inline-block", fontSize: 12, fontWeight: 600, color: typeBadge.color,
                              background: typeBadge.bg, borderRadius: 9, padding: "3px 9px", marginTop: 4,
                              boxShadow: "inset 0 1px 1px rgba(255,255,255,0.7)" }}>
                  {typeBadge.label}
                </div>
                {item.recipe && (
                  <div style={styles.savedCardMeta}>
                    {item.recipe.time_minutes || 30} мин
                    {` · ${item.recipe.difficulty || "Средне"}`}
                    {item.recipe.calories_per_100g > 0 ? ` · ${item.recipe.calories_per_100g} ккал/100г` : (item.recipe.calories_per_serving > 0 ? ` · ${item.recipe.calories_per_serving} ккал/порц` : "")}
                    {item.likes_count > 0 && ` · ❤️ ${item.likes_count}`}
                  </div>
                )}
              </div>
            </button>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <button style={{ ...styles.deleteBtn, color: "#9a6a1e" }} className="povar-press"
                onClick={(e) => { e.stopPropagation(); openEditor(item); }} title="Редактировать"><UiIcon name="pencil" size={28} /></button>
              {item.recipe_type === "own" && (
                <button style={{ ...styles.deleteBtn, color: "#2e7d32" }} className="povar-press"
                  onClick={(e) => { e.stopPropagation(); openPublishOwn(item); }} title="Поделиться в общей базе"><UiIcon name="megaphone" size={28} /></button>
              )}
              <button style={styles.deleteBtn} className="povar-press"
                onClick={(e) => { e.stopPropagation(); askDeleteRecipe(item); }} title="Удалить"><UiIcon name="trash" size={28} /></button>
            </div>
          </div>
          );
        })}
        <ConfirmModal {...(confirm || {})} onCancel={() => setConfirm(null)} />
        <Toast {...(toast || {})} onClose={() => setToast(null)} />
        <InfoModal {...(info || {})} onClose={info?.onClose || (() => setInfo(null))} />
        {renderPublishModal()}
        <FloatingBack onClick={() => setScreen("main")} />
      </div>
    );
  }

  return null;
}

const styles = {
  page: { maxWidth: 480, margin: "0 auto", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", minHeight: "100vh", background: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160' viewBox='0 0 160 160'%3E%3Cg fill='none' stroke='%234a7d3a' stroke-opacity='0.055' stroke-width='1.6' stroke-linecap='round'%3E%3Cpath d='M20 150 Q30 120 28 95 Q26 75 38 60'/%3E%3Cpath d='M28 95 Q40 92 46 82'/%3E%3Cpath d='M29 112 Q19 108 15 99'/%3E%3Cpath d='M120 30 Q128 55 124 78 Q120 95 130 110'/%3E%3Cpath d='M124 78 Q134 74 139 64'/%3E%3Cpath d='M123 55 Q113 52 108 43'/%3E%3Cpath d='M75 10 Q80 24 76 38'/%3E%3Cpath d='M76 30 Q84 28 88 21'/%3E%3C/g%3E%3Cg fill='%234a7d3a' fill-opacity='0.05'%3E%3Cellipse cx='47' cy='80' rx='6' ry='3' transform='rotate(-30 47 80)'/%3E%3Cellipse cx='14' cy='97' rx='6' ry='3' transform='rotate(30 14 97)'/%3E%3Cellipse cx='140' cy='62' rx='6' ry='3' transform='rotate(-30 140 62)'/%3E%3Cellipse cx='107' cy='41' rx='6' ry='3' transform='rotate(30 107 41)'/%3E%3Cellipse cx='89' cy='19' rx='5' ry='2.5' transform='rotate(-35 89 19)'/%3E%3Cellipse cx='39' cy='58' rx='5' ry='2.5' transform='rotate(45 39 58)'/%3E%3C/g%3E%3C/svg%3E\"), linear-gradient(165deg, #f4f8ef 0%, #ecf3e6 55%, #e8f0e2 100%)", backgroundAttachment: "fixed", padding: "12px 12px 24px", boxSizing: "border-box", WebkitTapHighlightColor: "transparent" },
  header: { background: "transparent", borderRadius: 0, padding: "2px 4px", color: "#1c2a1c", marginBottom: 14, display: "flex", alignItems: "center", gap: 11 },
  headerBack: { background: "#ffffff", border: "none", color: "#1c2a1c", cursor: "pointer", padding: "0 0 2px 0", fontSize: 22, lineHeight: 1, width: 36, height: 36, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" },
  headerTitle: { fontSize: 21, fontWeight: 700, lineHeight: 1.2, color: "#1c241b" },
  headerSubtitle: { fontSize: 14, color: "#7a8a7a", marginTop: 2, lineHeight: 1.35 },
  slideCounter: { fontSize: 13, opacity: 0.75 },
  card: { background: "#ffffff", borderRadius: 26, padding: 18, marginBottom: 14, boxShadow: "0 1px 2px rgba(28,36,27,0.05), 0 5px 14px rgba(28,36,27,0.06), 0 14px 36px rgba(28,36,27,0.07)" },
  primaryButton: { width: "100%", background: "linear-gradient(140deg, #e97c4e 0%, #da6338 55%, #d05a2e 100%)", color: "#fff", border: "none", borderRadius: 22, padding: "17px 0", fontSize: 19, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", marginBottom: 10, letterSpacing: "-0.2px", boxShadow: "0 2px 6px rgba(218,99,56,0.25), 0 12px 30px rgba(218,99,56,0.38), inset 0 1px 1px rgba(255,255,255,0.4)" },
  secondaryButton: { flex: 1, background: "#ffffff", color: "#0f6e56", border: "1.5px solid #b8dcc8", borderRadius: 18, padding: "14px 0", fontSize: 17, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },
  secondaryFullButton: { width: "100%", background: "#ffffff", color: "#0f6e56", border: "2px solid #0f6e56", borderRadius: 18, padding: "14px 0", fontSize: 17, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", marginBottom: 10 },
  dangerButton: { background: "#fff", color: "#c62828", border: "2px solid #c62828", borderRadius: 18, padding: "14px 0", fontSize: 17, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },
  homeBtn: { width: "100%", background: "transparent", border: "none", color: "#0f6e56", fontSize: 16, fontWeight: 500, padding: "12px 0", cursor: "pointer", marginTop: 4, fontFamily: "inherit" },
  proHint: { background: "rgba(255,255,255,0.45)", border: "1.5px dashed rgba(15,110,86,0.3)", borderRadius: 18, padding: "12px 14px", fontSize: 15, color: "#4a5c4a", lineHeight: 1.5, textAlign: "center", marginTop: 8 },
  slideTitle: { fontSize: 20, fontWeight: 600, color: "#1c2a1c", marginBottom: 10, lineHeight: 1.25 },
  slideText: { fontSize: 16, color: "#4a5c4a", lineHeight: 1.5 },
  dots: { display: "flex", gap: 6, justifyContent: "center", marginBottom: 14, marginTop: 4 },
  dot: { height: 7, transition: "all 0.25s" },
  heroText: { fontSize: 16, color: "#1c2a1c", lineHeight: 1.45, fontWeight: 500 },
  counter: { textAlign: "center", color: "#4a5c4a", fontSize: 15, margin: "6px 0 16px" },
  bookEntry: { width: "100%", background: "#ffffff", border: "none", borderRadius: 26, padding: "12px 14px", display: "flex", alignItems: "center", gap: 12, cursor: "pointer", fontFamily: "inherit" },
  bookEntryIcon: { width: 52, height: 52, borderRadius: 12, background: "#d9ecd9", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 26 },
  bookEntryLabel: { fontSize: 14, color: "#7a8a7a", marginBottom: 2 },
  bookEntryTitle: { fontSize: 17, color: "#1c2a1c", fontWeight: 600 },
  chevron: { color: "#7a8a7a", fontSize: 22, fontWeight: 300 },
  hint: { fontSize: 15, color: "#4a5c4a", marginBottom: 8, paddingLeft: 4 },
  contextLabel: { fontSize: 15, color: "#1c2a1c", marginBottom: 8, paddingLeft: 4, fontWeight: 600 },
  contextOptional: { color: "#7a8a7a", fontWeight: 400 },
  contextChips: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 18 },
  contextChip: { padding: "8px 14px 8px 10px", fontSize: 18, background: "#fff", border: "none", borderRadius: 20, cursor: "pointer", color: "#1c241b", fontWeight: 600, fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "flex-start", transition: "all 0.15s", width: "100%" },
  contextChipActive: { background: "linear-gradient(145deg,#159872,#0c5a46)", color: "#fff", border: "1.5px solid transparent", boxShadow: "inset 0 1px 1px rgba(255,255,255,0.25), 0 3px 8px rgba(15,110,86,0.3)" },
  inputCard: { background: "#ffffff", borderRadius: 26, padding: 16, marginBottom: 14, position: "relative", boxShadow: "0 1px 2px rgba(28,36,27,0.05), 0 5px 14px rgba(28,36,27,0.06), 0 14px 36px rgba(28,36,27,0.07)" },
  input: { width: "100%", fontSize: 17, padding: "11px 13px", border: "1.5px solid #d5e5d5", borderRadius: 12, background: "#fff", outline: "none", color: "#1c2a1c", fontFamily: "inherit", boxSizing: "border-box" },
  textarea: { width: "100%", minHeight: 84, fontSize: 16, padding: 0, border: "none", background: "transparent", resize: "none", outline: "none", color: "#1c2a1c", fontFamily: "inherit", boxSizing: "border-box" },
  clearBtn: { position: "absolute", top: 12, right: 12, background: "rgba(15,110,86,0.15)", border: "none", borderRadius: "50%", width: 26, height: 26, fontSize: 14, cursor: "pointer", color: "#0f6e56", fontWeight: "bold", display: "flex", alignItems: "center", justifyContent: "center", padding: 0, lineHeight: 1 },
  charCount: { textAlign: "right", fontSize: 14, color: "#7a8a7a", marginTop: 4 },
  error: { color: "#c62828", marginBottom: 10, fontSize: 16, fontWeight: 500 },
  sectionLabel: { fontSize: 17, color: "#1c2a1c", fontWeight: 600, marginBottom: 10, marginTop: 4 },
  quickButtons: { display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 },
  quickBtn: { padding: "9px 14px", fontSize: 16, background: "#fff", border: "none", borderRadius: 22, cursor: "pointer", color: "#1c241b", fontWeight: 500, fontFamily: "inherit", boxShadow: "0 1px 2px rgba(28,36,27,0.06), 0 3px 10px rgba(28,36,27,0.07)" },
  futureButtons: { display: "flex", gap: 8, marginBottom: 16 },
  futureBtn: { flex: 1, padding: "13px 0", fontSize: 17, fontWeight: 700, background: "#ffffff", border: "none", borderRadius: 20, cursor: "pointer", color: "#c05a2e", display: "flex", alignItems: "center", justifyContent: "center", gap: 9, fontFamily: "inherit", boxShadow: "0 1px 2px rgba(28,36,27,0.05), 0 5px 14px rgba(28,36,27,0.07)" },
  soonBadge: { background: "rgba(15,110,86,0.15)", color: "#0f6e56", borderRadius: 6, padding: "2px 6px", fontSize: 12, fontWeight: 600 },
  privacyNote: { textAlign: "center", fontSize: 14, color: "#4a5c4a", marginTop: 6 },
  recipeCard: { background: "#ffffff", borderRadius: 26, padding: 16, marginBottom: 12, display: "flex", gap: 14, alignItems: "flex-start" },
  recipeCardEmoji: { fontSize: 44, lineHeight: 1, flexShrink: 0 },
  recipeCardTitle: { fontSize: 16, fontWeight: 600, color: "#1c2a1c", marginBottom: 6, lineHeight: 1.25 },
  recipeCardMeta: { display: "flex", gap: 8, marginBottom: 6, flexWrap: "wrap", alignItems: "center" },
  recipeCardTags: { display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 },
  recipeCardTag: { background: "rgba(76,175,80,0.15)", color: "#2e7d32", borderRadius: 8, padding: "2px 8px", fontSize: 13, fontWeight: 500 },
  timeBadge: { background: "#faeeda", color: "#ba7515", borderRadius: 8, padding: "3px 9px", fontSize: 14, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 3 },
  diffBadge: { borderRadius: 8, padding: "3px 9px", fontSize: 14, fontWeight: 600, display: "inline-flex", alignItems: "center" },
  calBadge: { background: "#faece7", color: "#c62828", borderRadius: 8, padding: "3px 9px", fontSize: 14, fontWeight: 600, display: "inline-flex", alignItems: "center" },
  cardBtn: { marginTop: 10, background: "linear-gradient(145deg,#159872,#0c5a46)", color: "#fff", border: "none", borderRadius: 16, padding: "11px 18px", fontSize: 16, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", boxShadow: "inset 0 1px 1px rgba(255,255,255,0.25), 0 4px 12px rgba(15,110,86,0.28)" },
  dislikeBlock: { background: "#ffffff", borderRadius: 26, padding: 16, marginTop: 4 },
  dislikeTitle: { fontSize: 17, color: "#1c2a1c", fontWeight: 600, marginBottom: 2 },
  dislikeSubtitle: { fontSize: 14, color: "#4a5c4a", marginBottom: 12 },
  dislikeBtns: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 },
  dislikeBtn: { padding: "10px 12px", fontSize: 14, background: "#fff", border: "none", borderRadius: 12, cursor: "pointer", color: "#1c2a1c", textAlign: "left", fontFamily: "inherit" },
  dislikeBtnLabel: { fontWeight: 600, marginBottom: 2, fontSize: 15 },
  dislikeBtnSub: { fontSize: 13, color: "#7a8a7a", lineHeight: 1.3 },
  recipeBadgesRow: { display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap", justifyContent: "center" },
  sectionTitle: { fontSize: 19, fontWeight: 700, color: "#1c241b", marginBottom: 14 },
  ingredient: { fontSize: 17, marginBottom: 10, color: "#1c2a1c", display: "flex", gap: 6, alignItems: "baseline", flexWrap: "wrap" },
  ingredientDot: { color: "#1d9e75", fontWeight: "bold", flexShrink: 0 },
  ingredientName: { minWidth: 0, wordBreak: "break-word", flexShrink: 1 },
  ingredientDots: { flex: 1, borderBottom: "1.5px dotted rgba(15,110,86,0.25)", margin: "0 6px", alignSelf: "flex-end", marginBottom: 4 },
  ingredientAmount: { fontWeight: 600, color: "#1c2a1c", flexShrink: 0 },
  ingredientKbju: { fontWeight: 400, fontSize: 16, color: "#7a8a7a", marginLeft: 4 },
  replacement: { display: "flex", alignItems: "flex-start", gap: 8, fontSize: 16, color: "#1c2a1c", marginBottom: 10, lineHeight: 1.4 },
  replacementRow: { display: "flex", alignItems: "center", gap: 10, marginBottom: 10, lineHeight: 1.4, flexWrap: "wrap" },
  replacementText: { fontSize: 16, color: "#1c2a1c", flex: 1, minWidth: 0 },
  replaceBtn: { background: "#0f6e56", color: "#fff", border: "none", borderRadius: 8, padding: "6px 12px", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", flexShrink: 0 },
  replacementOriginal: { color: "#1c2a1c", fontWeight: 600, flexShrink: 0 },
  replacementArrow: { color: "#1d9e75", fontWeight: "bold", flexShrink: 0 },
  replacementAlternative: { color: "#4a5c4a", flex: 1 },
  step: { fontSize: 16, marginBottom: 8, display: "flex", gap: 12, alignItems: "flex-start", color: "#1c2a1c", lineHeight: 1.5, background: "#f6faf6", borderRadius: 12, padding: "11px 12px" },
  stepNum: { background: "#1d9e75", color: "#fff", borderRadius: "50%", minWidth: 26, height: 26, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 15, fontWeight: 600 },
  proStepBtn: { display: "flex", alignItems: "center", gap: 8, width: "100%", background: "rgba(255,255,255,0.4)", border: "1.5px dashed rgba(15,110,86,0.28)", borderRadius: 10, padding: "8px 12px", marginLeft: 38, marginBottom: 14, marginTop: 2, cursor: "pointer", fontFamily: "inherit", maxWidth: "calc(100% - 38px)", boxSizing: "border-box" },
  proStepIcon: { fontSize: 16, flexShrink: 0 },
  proStepText: { fontSize: 14, color: "#4a5c4a", fontWeight: 500, flex: 1, textAlign: "left" },
  proStepBadge: { background: "rgba(216,67,21,0.12)", color: "#1d9e75", borderRadius: 6, padding: "2px 6px", fontSize: 12, fontWeight: 700, flexShrink: 0 },
  tip: { background: "#0f6e56", borderRadius: 18, padding: "13px 15px", fontSize: 15, color: "#d3ede2", marginBottom: 14, lineHeight: 1.5 },
  actions: { display: "flex", gap: 10 },
  missing: { background: "#faeeda", borderRadius: 12, padding: "10px 14px", fontSize: 16, color: "#ba7515", marginBottom: 12, border: "1px solid rgba(15,110,86,0.25)" },
  savedCardWrap: { display: "flex", gap: 8, marginBottom: 8, alignItems: "stretch" },
  savedCard: { background: "#ffffff", borderRadius: 18, padding: 12, flex: 1, display: "flex", alignItems: "center", gap: 12, border: "none", cursor: "pointer", fontFamily: "inherit", minWidth: 0, boxShadow: "0 1px 2px rgba(28,36,27,0.05), 0 4px 12px rgba(28,36,27,0.07)" },
  savedCardEmoji: { fontSize: 32, lineHeight: 1, flexShrink: 0 },
  savedCardTitle: { fontSize: 16, fontWeight: 600, color: "#1c2a1c", marginBottom: 2 },
  savedCardMeta: { fontSize: 14, color: "#1d9e75" },
  deleteBtn: { background: "#ffffff", border: "none", borderRadius: 18, width: 54, minHeight: 48, fontSize: 20, cursor: "pointer", flexShrink: 0, color: "#c62828", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 1px 2px rgba(28,36,27,0.05), 0 4px 12px rgba(28,36,27,0.07)" },
  loadingMsg: { fontSize: 16, color: "#1c2a1c", marginBottom: 18, minHeight: 24, fontWeight: 500 },
  progressBar: { width: "100%", height: 8, background: "rgba(15,110,86,0.15)", borderRadius: 4, overflow: "hidden", marginBottom: 6 },
  progressFill: { height: "100%", background: "#1d9e75", borderRadius: 4, transition: "width 0.3s ease" },
  progressText: { fontSize: 14, color: "#1d9e75", fontWeight: 600 },
  toast: { position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: "linear-gradient(145deg,#159872,#0c5a46)", color: "#fff", padding: "13px 18px", borderRadius: 20, fontSize: 16, fontWeight: 500, zIndex: 1000, display: "flex", alignItems: "center", gap: 12, minWidth: 280, maxWidth: 360, boxShadow: "0 8px 28px rgba(0,0,0,0.28), inset 0 1px 1px rgba(255,255,255,0.2)" },
  toastBtn: { background: "rgba(255,255,255,0.2)", color: "#fff", border: "none", borderRadius: 8, padding: "6px 12px", fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", flexShrink: 0 },
  modalOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000, padding: 20 },
  modalCard: { background: "#ffffff", borderRadius: 26, padding: 22, maxWidth: 340, width: "100%", boxShadow: "0 8px 24px rgba(0,0,0,0.3)" },
  modalTitle: { fontSize: 17, fontWeight: 600, color: "#1c2a1c", marginBottom: 10 },
  modalText: { fontSize: 16, color: "#4a5c4a", lineHeight: 1.5, marginBottom: 20, whiteSpace: "pre-line" },
  modalButtons: { display: "flex", gap: 10 },
  modalBtnCancel: { flex: 1, background: "#fff", color: "#0f6e56", border: "1.5px solid #d5e5d5", borderRadius: 14, padding: "12px 0", fontSize: 17, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },
  modalBtnConfirm: { flex: 1, background: "linear-gradient(145deg,#d94b3f,#bd2a2a)", color: "#fff", border: "none", borderRadius: 14, padding: "12px 0", fontSize: 17, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", boxShadow: "inset 0 1px 1px rgba(255,255,255,0.25), 0 4px 12px rgba(198,40,40,0.28)" },
  modalBtnInfo: { width: "100%", background: "linear-gradient(145deg,#159872,#0c5a46)", color: "#fff", border: "none", borderRadius: 14, padding: "12px 0", fontSize: 17, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", boxShadow: "inset 0 1px 1px rgba(255,255,255,0.25), 0 4px 12px rgba(15,110,86,0.28)" },
  // Совет дня
  adviceCard: { background: "#ffffff", borderRadius: 26, padding: "16px 17px", marginBottom: 12, boxShadow: "0 1px 2px rgba(28,36,27,0.05), 0 5px 14px rgba(28,36,27,0.06), 0 14px 36px rgba(28,36,27,0.07)" },
  adviceHeader: { display: "flex", alignItems: "center", gap: 8, marginBottom: 10, fontSize: 17, color: "#1c2a1c", fontWeight: 600 },
  adviceMoon: { fontSize: 18, lineHeight: 1 },
  adviceTitle: { flex: 1 },
  adviceZodiac: { fontSize: 13, lineHeight: 1, background: "linear-gradient(145deg,#f0eefe,#e4e0fb)", color: "#5b4fc0", borderRadius: 11, padding: "5px 12px", fontWeight: 600, boxShadow: "inset 0 1px 1px rgba(255,255,255,0.7)" },
  advicePhrase: { fontSize: 15, color: "#33523f", lineHeight: 1.5, marginBottom: 10 },
  adviceLifehack: { fontSize: 14, color: "#9a6a1e", lineHeight: 1.5, background: "linear-gradient(145deg,#fdf3e0,#fae8cb)", borderRadius: 16, padding: "11px 13px", marginTop: 2, boxShadow: "inset 0 1px 2px rgba(255,255,255,0.6)" },
  adviceLifehackLabel: { fontWeight: 600, color: "#854f0b" },
  adviceBdayLink: { background: "transparent", border: "none", color: "#1d9e75", fontSize: 14, fontWeight: 600, cursor: "pointer", padding: "8px 0 0", fontFamily: "inherit", textAlign: "left", width: "100%" },
  bdayInputs: { display: "flex", gap: 10, marginBottom: 14 },
  bdayInput: { flex: 1, padding: "12px 14px", fontSize: 17, border: "1px solid #d5e5d5", borderRadius: 10, background: "#fff", color: "#1c2a1c", fontFamily: "inherit", boxSizing: "border-box", textAlign: "center" },
  // КБЖУ и здоровье
  nutritionCard: { background: "#ffffff", borderRadius: 26, padding: "16px 18px", marginBottom: 14, boxShadow: "0 1px 2px rgba(28,36,27,0.05), 0 5px 14px rgba(28,36,27,0.06), 0 14px 36px rgba(28,36,27,0.07)" },
  nutritionRow: { display: "flex", alignItems: "center", justifyContent: "space-around", marginBottom: 12 },
  nutritionItem: { textAlign: "center", flex: 1 },
  nutritionValue: { fontSize: 20, fontWeight: 700, color: "#1c2a1c", lineHeight: 1 },
  nutritionLabel: { fontSize: 12, color: "#7a8a7a", marginTop: 3, fontWeight: 500 },
  nutritionPerServing: { fontSize: 9, color: "#7a8a7a", marginTop: 1, fontWeight: 400, fontStyle: "italic" },
  overlayBg: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 3000, padding: 20 },
  overlayCard: { background: "#ffffff", borderRadius: 22, padding: "28px 24px", textAlign: "center", boxShadow: "0 8px 32px rgba(0,0,0,0.35)", maxWidth: 320, width: "100%" },
  overlayTitle: { fontSize: 18, fontWeight: 700, color: "#1c2a1c", marginBottom: 6 },
  overlaySub: { fontSize: 15, color: "#4a5c4a", lineHeight: 1.4 },
  overlayDots: { marginTop: 14, fontSize: 24, color: "#1d9e75", letterSpacing: 8, height: 24, lineHeight: 1 },
  overlayDot: { animation: "overlayDotBounce 1.4s infinite both" },
  nutritionDivider: { width: 1, height: 32, background: "rgba(15,110,86,0.15)" },
  healthTags: { display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 },
  healthTag: { background: "rgba(76,175,80,0.12)", color: "#2e7d32", borderRadius: 10, padding: "4px 10px", fontSize: 14, fontWeight: 500 },
  healthNote: { display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 8 },
  healthNoteIcon: { fontSize: 16, flexShrink: 0, lineHeight: 1.4 },
  healthNoteText: { fontSize: 15, color: "#1c2a1c", lineHeight: 1.5, flex: 1 },
  healthDisclaimer: { fontSize: 12, color: "#7a8a7a", lineHeight: 1.4, fontStyle: "italic", textAlign: "center", whiteSpace: "pre-line" },
  extChip: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "linear-gradient(145deg,#eef6ee,#e0eede)", border: "none", borderRadius: 14, padding: "9px 13px", minWidth: 72, fontSize: 17, color: "#1c241b", boxShadow: "inset 0 1px 1px rgba(255,255,255,0.7), 0 1px 3px rgba(28,36,27,0.06)" },
  extHint: { fontSize: 12, color: "#7a8a7a", marginTop: 2, fontWeight: 400 },
  // Полезно при болячках
  conditionsBlock: { background: "rgba(76,175,80,0.08)", borderRadius: 12, padding: "10px 12px", marginBottom: 10, border: "1.5px solid rgba(76,175,80,0.2)" },
  conditionsTitle: { fontSize: 15, fontWeight: 600, color: "#2e7d32", marginBottom: 8 },
  conditionsList: { display: "flex", flexWrap: "wrap", gap: 6 },
  conditionTag: { background: "rgba(76,175,80,0.15)", color: "#1b5e20", borderRadius: 20, padding: "5px 12px", fontSize: 15, fontWeight: 500 },
  // Осторожно
  cautionBlock: { display: "flex", gap: 8, alignItems: "flex-start", background: "rgba(255,152,0,0.08)", borderRadius: 10, padding: "8px 12px", marginBottom: 10, border: "1px solid rgba(255,152,0,0.25)" },
  cautionIcon: { fontSize: 17, flexShrink: 0, lineHeight: 1.4 },
  cautionText: { fontSize: 14, color: "#ba7515", lineHeight: 1.45, flex: 1 },
};
